// ArtistWall.js - Updated Edit Modal with Side-by-Side Layout for Image and Details

import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from "../context/AuthContext";
import { db, auth, storage, firebaseConfig } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  deleteDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { backendConfig, API_ENDPOINTS } from "../config/backendConfig";
import PostModal from "../components/PostModal";
import AccountSettingsModal from "../components/AccountSettingsModal";
import { useNavigate, useLocation } from "react-router-dom";
import { canShareArtworkPublicly, isUnder13, getDisplayName } from "../utils/consentUtils";
import { getAvatarEmoji } from "../components/AvatarSelector";
import { getCache, setCache, createCacheKey, clearCache } from "../utils/cache";
import { invalidateArtistProfileCache } from "../utils/artistProfileCache";
import { invalidateSalonCache } from "../utils/artistSalonCache";
import { getArtworkCards } from "../utils/artHistoryFlowService";
import { syncArtworkVisibility } from "../utils/artworkVisibilitySync";
import { authFetch } from "../utils/authFetch";
import { resolveImageUrl as resolveImageUrlUtil, resolveImageUrlAsync, warmResolvedImageUrls } from "../utils/urlResolver";
import { toJSDate } from "../utils/date";
import { optimizeImageForUpload, createThumbnailForUpload } from "../utils/imageUploadUtils";
import { dedupeWaysOfSeeingActivitiesByArtworkStyle } from "../utils/activityService";

import NotificationBell from "../components/NotificationBell";
import ReactionNotifications from "../components/ReactionNotifications";
import ArtworkReactions from "../components/ArtworkReactions";
import ArtistWallMuseumInsights from "../components/ArtistWallMuseumInsights";
import ArtistWallMostLoved from "../components/ArtistWallMostLoved";
import MuseumExploreCta from "../components/MuseumExploreCta";
import { fetchReactionCountsForArtworks } from "../utils/museumCoLearningStats";
import WaysOfSeeingModal from "../components/WaysOfSeeingModal";
import { FaEdit, FaTimes, FaExpand } from 'react-icons/fa';
import { getReactionArtworkId } from '../utils/reactionArtworkId';
import { coLearningWallBackdropProps } from '../ui/coLearningModalTheme';
import { artPrimaryBtnLg, artSecondaryBtnLg } from '../ui/artmosphereButtonTheme';
import {
  onboardingInputClass,
  onboardingErrorClass,
  uploadWizardPanelClass,
  uploadWizardStepCardClass,
  uploadWizardStepBadgeClass,
  uploadWizardStepTitleClass,
  uploadWizardLabelClass,
  uploadWizardBodyClass,
  uploadWizardChoiceCardClass,
} from '../components/OnboardingModalShell';
import { showPremiumConfirm } from '../utils/premiumConfirmDialog';
import { BookOpen, Image as ImageIcon, Home, Search, User, ArrowLeft, Sparkles, Calendar, BookMarked, Plus, LayoutDashboard, Frame, Heart, Menu, LogOut, X, Landmark } from 'lucide-react';
import KidBackground from "../ui/backgrounds/KidBackground";
import RoomBackground from "../components/artHistoryRoom/RoomBackground";
import RoomSceneWrapper from "../components/artHistoryRoom/RoomSceneWrapper";
import PerspectiveHero from "../components/artHistoryRoom/PerspectiveHero";
import SmartImage from "../components/SmartImage";
import ArtworkFillDisplay from "../components/ArtworkFillDisplay";
import { createPageBooks, BOOK_CO_LEARNING_UPLOAD_TAGS } from "../data/artmosphereBooks";

const bookTags = [
  "Around the world with 100 artists",
  "What's your story",
  "Complete the story",
  "Impressionism",
  "Pointillism",
  "Misc",
];

/** Labels for kudos / GIF reaction ids (aligned with ArtworkReactions). */
const KUDOS_REACTION_LABELS = {
  sparkles: { emoji: '✨', label: 'Sparkle Wow' },
  bravo: { emoji: '👏', label: 'Big Applause' },
  heartburst: { emoji: '💖', label: 'Heart Burst' },
  highfive: { emoji: '🙌', label: 'Looks Amazing' },
  like: { emoji: '❤️', label: 'Like' },
};

function formatCuratedKudosTime(timestamp) {
  const date = toJSDate(timestamp);
  if (!date || Number.isNaN(date.getTime())) return 'Recently';
  const now = Date.now();
  const diffInMs = now - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 7) return `${diffInDays}d ago`;
  const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return formatted === 'Invalid Date' ? 'Recently' : formatted;
}

function getCuratedReactionPopoverStyle(anchorRect) {
  if (typeof window === 'undefined') {
    return { position: 'fixed', left: 12, top: 12, width: 280, zIndex: 200060 };
  }
  const panelW = 280;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const padding = 12;
  const left = Math.max(padding, Math.min(anchorRect.right - panelW, vw - panelW - padding));
  const maxPopoverH = 300;
  let top = anchorRect.bottom + 8;
  if (top + maxPopoverH > vh - padding) {
    top = Math.max(padding, anchorRect.top - maxPopoverH - 8);
  }
  return { position: 'fixed', left, top, width: panelW, zIndex: 200060 };
}

const ENV = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host.includes('staging')) {
      return 'staging';
    }
  }
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV) {
    return process.env.REACT_APP_ENV;
  }
  return 'prod';
})();

// Countries list for "Around the world with 100 artists"
const countries = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", "Belgium", "Brazil", "Bulgaria",
  "Cambodia", "Canada", "Chile", "China", "Colombia", "Croatia", "Cuba", "Czech Republic", "Denmark", "Egypt",
  "Ethiopia", "Finland", "France", "Germany", "Ghana", "Greece", "Guatemala", "Hungary", "Iceland", "India",
  "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Japan", "Jordan", "Kenya", "Korea",
  "Lebanon", "Malaysia", "Mexico", "Morocco", "Myanmar", "Nepal", "Netherlands", "New Zealand", "Nigeria", "Norway",
  "Pakistan", "Peru", "Philippines", "Poland", "Portugal", "Romania", "Russia", "Saudi Arabia", "Senegal", "Serbia",
  "Singapore", "South Africa", "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tanzania",
  "Thailand", "Tunisia", "Turkey", "Ukraine", "United Kingdom", "United States", "Uruguay", "Venezuela", "Vietnam", "Yemen",
  "Zambia", "Zimbabwe", "Belarus", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Cameroon", "Chad", "Costa Rica", "Dominican Republic",
  "Ecuador", "El Salvador", "Estonia", "Georgia", "Honduras", "Jamaica", "Kazakhstan", "Kuwait", "Latvia", "Lithuania",
  "Luxembourg", "Madagascar", "Mali", "Mauritius", "Mongolia", "Namibia", "Nicaragua", "Panama", "Paraguay", "Qatar"
];

// Activity numbers for "Simple acts of art" (1-120 activities)
const activityNumbers = Array.from({ length: 120 }, (_, i) => i + 1);
const artistWallSnapshotKey = (uid) => `artist-wall:artworks-snapshot:${uid || 'anon'}`;
const stableArtworkUrlById = new Map(); // artworkId -> stable http(s) url

const readArtistWallSnapshot = (uid) => {
  const keys = [artistWallSnapshotKey(uid), artistWallSnapshotKey('anon')];
  for (const key of keys) {
    try {
      const local = localStorage.getItem(key);
      if (local) {
        const parsed = JSON.parse(local);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
    try {
      const session = sessionStorage.getItem(key);
      if (session) {
        const parsed = JSON.parse(session);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
  }
  return null;
};

const writeArtistWallSnapshot = (uid, artworks = []) => {
  if (!Array.isArray(artworks) || artworks.length === 0) return;
  try {
    artworks.forEach((art) => {
      const id = art?.id;
      const stable = art?.thumbnailUrl || art?.imageUrl || art?.artworkUrl || '';
      if (id && typeof stable === 'string' && /^https?:\/\//i.test(stable)) {
        stableArtworkUrlById.set(id, stable);
      }
    });
  } catch (_) {}
  const payload = JSON.stringify(artworks.slice(0, 120));
  const keys = [artistWallSnapshotKey(uid), artistWallSnapshotKey('anon')];
  keys.forEach((key) => {
    try { localStorage.setItem(key, payload); } catch (_) {}
    try { sessionStorage.setItem(key, payload); } catch (_) {}
  });
};

/** Display-only: activity context for cards (icons + hover copy; no footer label line). */
function getArtworkStudioMeta(art) {
  const empty = {
    detail: '',
    technique: '',
    activityKind: 'general',
    activityTooltip: 'Your own artwork on your Artist Wall.',
  };
  if (!art) return empty;
  const technique = art.technique || art.artworkTechnique || '';

  // Art History first: many flows set activityId; book heuristics also treat activityId as book.
  if (
    art.activityType === 'art-history-appreciation' ||
    art.source === 'art-history' ||
    art.type === 'art-history-appreciation' ||
    art.isArtHistory === true ||
    art.metadata?.feature === 'learn-art-history-flow'
  ) {
    return {
      detail: 'Created in an Art History journey',
      technique,
      activityKind: 'art-history',
      activityTooltip: 'Art History activity — guided appreciation flow.',
    };
  }

  const waysish =
    art.styleId ||
    art.styleName ||
    art.source === 'ways-of-seeing' ||
    art.originalArtworkUrl ||
    art.metadata?.feature === 'ways-of-seeing-journey' ||
    (art.type &&
      art.type !== 'art-history-appreciation' &&
      (String(art.type).includes('tutorial') || String(art.type).includes('reimagine')));
  if (waysish) {
    return {
      detail: 'Studio transformation',
      technique,
      activityKind: 'ways-of-seeing',
      activityTooltip: 'Ways of Seeing — style transfer from an original artwork.',
    };
  }

  // Daily: use explicit studio sources only (bare activityId overlaps book + art flows).
  if (art.source === 'daily-art' || art.dailyActivityId || art.source === 'daily-activity') {
    return {
      detail: 'Daily Art Studio',
      technique,
      activityKind: 'daily',
      activityTooltip: 'Daily Art Studio — Art of the Day or studio activity.',
    };
  }

  // General gallery uploads — before book heuristics so stray activityId does not imply co-learning.
  if (art.source === 'general' || art.category === 'general') {
    return {
      detail: 'Your ARTmosphere gallery',
      technique,
      activityKind: 'general',
      activityTooltip: 'Your own creation — not linked to a guided activity.',
    };
  }

  const bookish =
    art.isBookArtwork ||
    art.bookArtworkId ||
    art.activityId ||
    art.source === 'book-co-learning' ||
    art.bookActivityMeta ||
    art.type === 'book-activity' ||
    (art.tag &&
      /book|Around the world|What's your story|Complete the story|Impressionism|Pointillism/i.test(String(art.tag)));
  if (bookish) {
    const parts = ['Book co-learning'];
    const tag = String(art.tag || '').trim();
    if (tag) parts.push(tag);
    const code =
      art.activityCode ||
      art.bookActivityMeta?.activityCode ||
      art.activityId ||
      '';
    if (code) parts.push(String(code));
    return {
      detail: 'From an ARTmosphere book activity',
      technique,
      activityKind: 'book',
      activityTooltip: parts.join(' · '),
    };
  }

  return {
    detail: 'Your ARTmosphere gallery',
    technique,
    activityKind: 'general',
    activityTooltip: 'Your own creation — not linked to a guided activity.',
  };
}

function ArtistWallActivityTypeBadge({ kind, tooltip }) {
  const Icon =
    kind === 'book'
      ? BookOpen
      : kind === 'art-history'
        ? Landmark
        : kind === 'ways-of-seeing'
          ? Sparkles
          : kind === 'daily'
            ? Calendar
            : kind === 'general'
              ? ImageIcon
              : Frame;
  return (
    <button
      type="button"
      className="absolute left-2 top-2 z-[11] flex h-8 w-8 items-center justify-center rounded-full border border-[#e8d4bc]/90 bg-white/95 text-[#8A7357] shadow-md ring-1 ring-[#efe2d0]/80 transition hover:border-[#d4b896] hover:bg-[#fffdf9] hover:text-[#5A4632]"
      title={tooltip}
      aria-label={tooltip}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  );
}

/** True when artwork belongs on General Exhibit (not co-learning / book activities). */
function isGeneralExhibitArtwork(art) {
  if (!art) return true;
  if (art.source === 'general' || art.category === 'general') return true;
  if (art.tag && BOOK_CO_LEARNING_UPLOAD_TAGS.includes(art.tag)) return false;
  if (
    art.source === 'book-co-learning' ||
    art.source === 'ways-of-seeing' ||
    art.source === 'art-history' ||
    art.source === 'daily-activity'
  ) {
    return false;
  }
  if (art.isBookArtwork || art.bookArtworkId) return false;
  if (art.activityType === 'art-history-appreciation' || art.type === 'art-history-appreciation') return false;
  if (art.styleId && art.originalArtworkId) return false;
  if (art.styleName && art.originalArtworkUrl) return false;
  return true;
}

function getEditArtworkTypeLabel(type) {
  const labels = {
    general: 'General — your own creation',
    'ways-of-seeing': 'Ways of Seeing activity',
    'art-history': 'Art History activity',
    'book-co-learning': 'ARTmosphere book activity',
    'daily-activity': 'Daily Art Studio activity',
    'daily-acts': 'Daily Acts activity',
  };
  const key = type === '' ? 'general' : type;
  return labels[key] || 'General — your own creation';
}

function isArtmosphereBookArtwork(art) {
  if (!art) return false;
  const tag = art.tag || '';
  return (
    art.isBookArtwork ||
    art.bookArtworkId ||
    art.source === 'book-co-learning' ||
    art.bookActivityMeta ||
    art.type === 'book-activity' ||
    BOOK_CO_LEARNING_UPLOAD_TAGS.includes(tag)
  );
}

function resolveSuccessModalArtworkType(artworkType, oldArtwork) {
  const t = (artworkType || '').toLowerCase();
  if (
    t === 'art-history' ||
    oldArtwork?.source === 'art-history' ||
    oldArtwork?.activityType === 'art-history-appreciation'
  ) {
    return 'art-history';
  }
  if (
    t === 'book-co-learning' ||
    oldArtwork?.isBookArtwork ||
    oldArtwork?.bookArtworkId ||
    oldArtwork?.source === 'book-co-learning'
  ) {
    return 'book-co-learning';
  }
  if (
    t === 'ways-of-seeing' ||
    oldArtwork?.source === 'ways-of-seeing' ||
    (oldArtwork?.styleId && oldArtwork?.originalArtworkId && !isGeneralExhibitArtwork(oldArtwork))
  ) {
    return 'ways-of-seeing';
  }
  if (t === 'daily-activity' || t === 'daily-acts') return t;
  return 'general';
}

function getSharedArtworkDestination(modalType) {
  const routes = {
    'art-history': '/gallery/studio?tab=art-history',
    'ways-of-seeing': '/gallery/studio?tab=ways',
    'book-co-learning': '/gallery/studio?tab=books',
    'daily-activity': '/gallery/studio?tab=activities',
    'daily-acts': '/gallery/studio?tab=activities',
    general: '/gallery/general',
  };
  return routes[(modalType || '').toLowerCase()] || '/gallery/general';
}

function getSharedArtworkCtaLabel(modalType) {
  const labels = {
    'art-history': 'View in Art History Space',
    'ways-of-seeing': 'View in Ways of Seeing Space',
    'book-co-learning': 'View in Co-Learning Space',
    'daily-activity': 'View in Daily Art Studio',
    'daily-acts': 'View in Daily Art Studio',
    general: 'View in General Exhibit',
  };
  return labels[(modalType || '').toLowerCase()] || 'View in General Exhibit';
}

const ARTIST_EXPANDED_SHELL_SELECTOR = '.artist-expanded-shell';
const ARTIST_EXPANDED_ANIM_MS = 380;

function openArtistWallExpandedView(artId) {
  const el = document.getElementById(`expanded-view-${artId}`);
  if (!el) return;
  el.classList.remove('hidden');
  el.style.display = 'flex';
  el.classList.remove('artist-expanded-overlay--open');
  const shell = el.querySelector(ARTIST_EXPANDED_SHELL_SELECTOR);
  if (shell) shell.classList.remove('artist-expanded-shell--open');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.classList.add('artist-expanded-overlay--open');
      if (shell) shell.classList.add('artist-expanded-shell--open');
    });
  });
}

function closeArtistWallExpandedView(artId, immediate = false) {
  const el = document.getElementById(`expanded-view-${artId}`);
  if (!el) return;
  el.classList.remove('artist-expanded-overlay--open');
  const shell = el.querySelector(ARTIST_EXPANDED_SHELL_SELECTOR);
  if (shell) shell.classList.remove('artist-expanded-shell--open');
  if (immediate) {
    el.classList.add('hidden');
    el.style.display = 'none';
    return;
  }
  window.setTimeout(() => {
    el.classList.add('hidden');
    el.style.display = 'none';
  }, ARTIST_EXPANDED_ANIM_MS);
}

/** Fullscreen artwork viewer (toggle). Used by expand control and tap-on-image. */
function openArtworkFullscreenView(art) {
  if (!art?.id || typeof document === 'undefined') return;
  try {
    const existingModal = document.getElementById(`fullscreen-artwork-${art.id}`);
    if (existingModal) {
      document.body.removeChild(existingModal);
      return;
    }

    const originalImgSrc =
      resolveImageUrlUtil(art.thumbnailUrl || art.imageUrl || art.artworkUrl || '') ||
      art.thumbnailUrl ||
      art.imageUrl ||
      art.artworkUrl ||
      '';

    const fullscreenModal = document.createElement('div');
    fullscreenModal.id = `fullscreen-artwork-${art.id}`;
    fullscreenModal.style.cssText =
      'position:fixed;inset:0;z-index:200100;display:flex;align-items:center;justify-content:center;padding:1rem;background:rgba(253,238,216,0.78);-webkit-backdrop-filter:blur(40px) saturate(1.22);backdrop-filter:blur(40px) saturate(1.22);';
    fullscreenModal.onclick = (event) => {
      if (event.target === fullscreenModal) {
        document.body.removeChild(fullscreenModal);
      }
    };

    const imgContainer = document.createElement('div');
    imgContainer.style.cssText =
      'position:relative;width:min(94vw,1100px);height:min(88vh,820px);overflow:hidden;border-radius:20px;border:1px solid rgba(232,212,188,0.92);box-shadow:0 32px 90px rgba(120,72,20,0.22);background:linear-gradient(145deg,#fff8ef 0%,#fdebd0 48%,#f5d0a8 100%);';
    imgContainer.onclick = (e) => e.stopPropagation();

    const bgImg = document.createElement('img');
    bgImg.src = originalImgSrc;
    bgImg.alt = '';
    bgImg.setAttribute('aria-hidden', 'true');
    bgImg.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:scale(1.12);filter:blur(24px) saturate(1.25);opacity:0.55;pointer-events:none;';

    const wash = document.createElement('div');
    wash.style.cssText =
      'position:absolute;inset:0;background:linear-gradient(145deg,rgba(255,248,239,0.88) 0%,rgba(253,235,208,0.72) 48%,rgba(245,208,168,0.78) 100%);pointer-events:none;';

    const img = document.createElement('img');
    img.src = originalImgSrc;
    img.alt = art.title || art.artworkTitle || 'Artwork';
    img.style.cssText =
      'position:absolute;inset:0;z-index:1;margin:auto;max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;object-position:center;';
    img.onerror = () => {
      try {
        const current = img.src || '';
        const src = originalImgSrc || '';
        if (!current.includes('/proxy-image?') && (src.startsWith('http://') || src.startsWith('https://'))) {
          const api = require('../config/backendConfig').API_ENDPOINTS;
          const proxied = `${api.backendBase}/proxy-image?url=${encodeURIComponent(originalImgSrc)}`;
          img.src = proxied;
          bgImg.src = proxied;
        } else {
          img.alt = 'Image failed to load';
        }
      } catch (_) {
        img.alt = 'Image failed to load';
      }
    };

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.style.cssText =
      'position:absolute;top:1rem;right:1rem;z-index:10;display:flex;align-items:center;justify-content:center;width:2.5rem;height:2.5rem;border-radius:9999px;border:1px solid #e8d4bc;background:rgba(255,255,255,0.95);color:#5e5a54;cursor:pointer;box-shadow:0 4px 14px rgba(120,72,20,0.12);';
    closeBtn.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    `;
    closeBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (fullscreenModal.parentNode) {
        document.body.removeChild(fullscreenModal);
      }
    };

    imgContainer.appendChild(bgImg);
    imgContainer.appendChild(wash);
    imgContainer.appendChild(img);
    imgContainer.appendChild(closeBtn);
    fullscreenModal.appendChild(imgContainer);
    document.body.appendChild(fullscreenModal);
  } catch (error) {
    console.error('Error creating fullscreen modal:', error);
    alert('Failed to expand artwork. Please try again.');
  }
}

export default function ArtistWall({ forceOpenUpload = false, onCloseUploadModal } = {}) {
  // Comprehensive error logging wrapper for TDZ detection
  const logTDZError = (variableName, lineHint) => {
    const error = new Error(`TDZ Error: Cannot access uninitialized variable '${variableName}'${lineHint ? ` (near line ${lineHint})` : ''}`);
    console.error('🚨 TDZ ERROR DETECTED:', {
      variable: variableName,
      lineHint: lineHint,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
    // Send to error tracking service if available
    if (window.errorTracking) {
      window.errorTracking.captureException(error, { tags: { type: 'TDZ', variable: variableName } });
    }
    return error;
  };

  // Safe variable access wrapper
  const safeAccess = (variable, name, defaultValue = null) => {
    try {
      if (variable === undefined) {
        throw logTDZError(name);
      }
      return variable;
    } catch (error) {
      console.error(`⚠️ Safe access fallback for ${name}:`, error);
      return defaultValue;
    }
  };

  const [title, setTitle] = useState("");
  const [technique, setTechnique] = useState("");
  const [inspiration, setInspiration] = useState("");
  const [description, setDescription] = useState("");
  const [tag, setTag] = useState(bookTags[0]);
  const [file, setFile] = useState(null);
  const [visibility, setVisibility] = useState("private"); // Default private — opt in to General Exhibit
  const [showUpload, setShowUpload] = useState(false);
  /** New-upload wizard: sourceChoice → activityCategoryChoice → bookSelect | studioActivityChoice → generalUpload | bookActivityUpload. Edit ignores steps. */
  const [uploadFlowStep, setUploadFlowStep] = useState("sourceChoice");
  const [isFromBook, setIsFromBook] = useState(null);
  const [selectedBookId, setSelectedBookId] = useState("");
  const [bookSelectError, setBookSelectError] = useState("");
  /** When set (new upload only), artwork type is fixed and tabs are hidden: general-only = own artwork; studio locks map to existing upload pipelines. */
  const [uploadWizardLock, setUploadWizardLock] = useState(null);

  const [isBookActivity, setIsBookActivity] = useState(false);
  const [activityCode, setActivityCode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedActivityNumber, setSelectedActivityNumber] = useState('');
  const [artworkType, setArtworkType] = useState(''); // 'ways-of-seeing', 'art-history', 'book-co-learning', or '' (general)
  const [artHistoryActivities, setArtHistoryActivities] = useState([]);
  const [selectedArtHistoryActivity, setSelectedArtHistoryActivity] = useState(null);
  const [waysOfSeeingActivities, setWaysOfSeeingActivities] = useState([]);
  const [selectedWaysOfSeeingActivity, setSelectedWaysOfSeeingActivity] = useState(null);
  const [shareToCoLearning, setShareToCoLearning] = useState(false);
  const [waysOfSeeingOriginalArtwork, setWaysOfSeeingOriginalArtwork] = useState(null);
  const [waysOfSeeingStyleId, setWaysOfSeeingStyleId] = useState(null);
  const [waysOfSeeingStyleName, setWaysOfSeeingStyleName] = useState('');
  const [selectedOriginalArtwork, setSelectedOriginalArtwork] = useState(null);
  const [availableStylesForArtwork, setAvailableStylesForArtwork] = useState([]);
  const [artworks, setArtworks] = useState(() => {
    try {
      const uid = auth?.currentUser?.uid;
      return readArtistWallSnapshot(uid) || [];
    } catch (_) {
      return [];
    }
  });
  const [editId, setEditId] = useState(null);
  const [editLockedArtworkType, setEditLockedArtworkType] = useState(null);
  const [editSource, setEditSource] = useState(null); // 'artworks' or 'sharedArtworks'
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [notificationRefresh, setNotificationRefresh] = useState(0);
  const [reactionCountsByArtworkId, setReactionCountsByArtworkId] = useState({});
  const [curatedWallReactionPopover, setCuratedWallReactionPopover] = useState(null);
  const curatedReactionPopoverRef = React.useRef(null);
  const [showExploreDropdown, setShowExploreDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [navMenuAnchor, setNavMenuAnchor] = useState(null);
  const artistWallNavButtonRef = React.useRef(null);
  const exploreButtonRef = React.useRef(null);
  const accountButtonRef = React.useRef(null);
  const [profile, setProfile] = useState(() => {
    try {
      const uid = auth?.currentUser?.uid;
      if (!uid || typeof createCacheKey !== 'function' || typeof getCache !== 'function') return {};
      const profileCacheKey = createCacheKey('artistProfile', { uid }, uid);
      return getCache(profileCacheKey) || {};
    } catch (_) {
      return {};
    }
  });
  const [displayName, setDisplayName] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [isLoading, setIsLoading] = useState(() => {
    try {
      const uid = auth?.currentUser?.uid;
      const snap = readArtistWallSnapshot(uid);
      return !(Array.isArray(snap) && snap.length > 0);
    } catch (_) {
      return true;
    }
  });
  const [showWaysOfSeeing, setShowWaysOfSeeing] = useState(false);
  const [waysOfSeeingModalOriginalArtwork, setWaysOfSeeingModalOriginalArtwork] = useState(null);
  const [selectedOriginalArtworkTitle, setSelectedOriginalArtworkTitle] = useState('');
  const [expandedInfoId, setExpandedInfoId] = useState(null);
  const [showArtHistoryModal, setShowArtHistoryModal] = useState(false);
  const [activityToggle, setActivityToggle] = useState('artist-wall'); // kept for any logic that may reference it

  const navigate = useNavigate();
  const location = useLocation();
  
  // CRITICAL: Mobile browsers have stricter TDZ enforcement and different execution timing
  // MUST call useAuth() unconditionally (React Hooks rule), then safely handle the result
  const authContextRaw = useAuth();
  
  // Mobile-specific: Safely extract values with comprehensive error handling
  // Mobile browsers may return undefined/null even with default context value
  const authContext = (authContextRaw === undefined || authContextRaw === null) ? {
    user: null,
    logout: () => {},
    loading: true
  } : authContextRaw;
  
  const user = authContext?.user ?? null;
  useEffect(() => {
    try {
      if (!user?.uid) return;
      const parsed = readArtistWallSnapshot(user.uid);
      if (Array.isArray(parsed) && parsed.length > 0) {
        try {
          parsed.forEach((art) => {
            const id = art?.id;
            const stable = art?.thumbnailUrl || art?.imageUrl || art?.artworkUrl || '';
            if (id && typeof stable === 'string' && /^https?:\/\//i.test(stable)) {
              stableArtworkUrlById.set(id, stable);
            }
          });
        } catch (_) {}
        setArtworks(
          parsed.map((art) => {
            const displayRaw = art?.thumbnailUrl || art?.imageUrl || art?.artworkUrl || '';
            const stable = stableArtworkUrlById.get(art?.id);
            return {
              ...art,
              thumbnailUrl: stable || resolveImageUrlUtil(displayRaw) || displayRaw || '',
            };
          })
        );
      }
    } catch (_) {}
  }, [user?.uid]);

  const logout = authContext?.logout ?? (() => {});
  const authLoading = authContext?.loading === true;
  
  // Mobile-specific logging and validation
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // CRITICAL: Track variable initialization state for TDZ debugging
  const [variableInitState, setVariableInitState] = useState({
    authContextRaw: false,
    authContext: false,
    user: false,
    authLoading: false,
    createCacheKey: false,
    getCache: false,
    setCache: false,
    fetchProfile: false,
    fetchArtworks: false,
    fetchFeatureImages: false
  });

  // Track variable initialization
  useEffect(() => {
    const initState = {
      authContextRaw: typeof authContextRaw !== 'undefined',
      authContext: typeof authContext !== 'undefined',
      user: typeof user !== 'undefined',
      authLoading: typeof authLoading !== 'undefined',
      createCacheKey: typeof createCacheKey !== 'undefined' && typeof createCacheKey === 'function',
      getCache: typeof getCache !== 'undefined' && typeof getCache === 'function',
      setCache: typeof setCache !== 'undefined' && typeof setCache === 'function',
      fetchProfile: typeof fetchProfile !== 'undefined' && typeof fetchProfile === 'function',
      fetchArtworks: typeof fetchArtworks !== 'undefined' && typeof fetchArtworks === 'function',
      fetchFeatureImages: typeof fetchFeatureImages !== 'undefined' && typeof fetchFeatureImages === 'function'
    };
    
    setVariableInitState(initState);
    
    console.log('🔍 [ArtistWall] Variable initialization state:', initState);
    
    // Check for uninitialized variables
    const uninitialized = Object.entries(initState)
      .filter(([_, initialized]) => !initialized)
      .map(([name, _]) => name);
    
    if (uninitialized.length > 0) {
      console.error('🚨 [ArtistWall] UNINITIALIZED VARIABLES DETECTED:', uninitialized);
    }
  }, [authContextRaw, authContext, user, authLoading]);

  // CRITICAL: Track login state transitions
  const [prevUser, setPrevUser] = useState(null);
  const [loginTransitionState, setLoginTransitionState] = useState('idle'); // 'idle', 'logging-in', 'logged-in', 'error'
  
  useEffect(() => {
    const wasLoggedOut = prevUser === null || !prevUser;
    const isNowLoggedIn = user !== null && user !== undefined && user.uid;
    
    if (wasLoggedOut && isNowLoggedIn) {
      // LOGIN DETECTED - This is when the error occurs!
      console.log('🚨 [ArtistWall] LOGIN TRANSITION DETECTED:', {
        prevUser: prevUser ? { uid: prevUser.uid } : null,
        currentUser: user ? { uid: user.uid, email: user.email } : null,
        authLoading: authLoading,
        timestamp: new Date().toISOString(),
        variableInitState: variableInitState
      });
      
      setLoginTransitionState('logging-in');
      
      // Check ALL variables during login transition
      const loginVarChecks = {
        // Auth variables
        authContextRaw: typeof authContextRaw !== 'undefined' ? 'defined' : 'UNDEFINED',
        authContext: typeof authContext !== 'undefined' ? 'defined' : 'UNDEFINED',
        user: typeof user !== 'undefined' ? 'defined' : 'UNDEFINED',
        authLoading: typeof authLoading !== 'undefined' ? 'defined' : 'UNDEFINED',
        userUid: user?.uid ? 'defined' : 'UNDEFINED',
        
        // Cache utilities
        createCacheKey: typeof createCacheKey !== 'undefined' && typeof createCacheKey === 'function' ? 'function' : typeof createCacheKey,
        getCache: typeof getCache !== 'undefined' && typeof getCache === 'function' ? 'function' : typeof getCache,
        setCache: typeof setCache !== 'undefined' && typeof setCache === 'function' ? 'function' : typeof setCache,
        
        // Fetch functions
        fetchProfile: typeof fetchProfile !== 'undefined' && typeof fetchProfile === 'function' ? 'function' : typeof fetchProfile,
        fetchArtworks: typeof fetchArtworks !== 'undefined' && typeof fetchArtworks === 'function' ? 'function' : typeof fetchArtworks,
        fetchFeatureImages: typeof fetchFeatureImages !== 'undefined' && typeof fetchFeatureImages === 'function' ? 'function' : typeof fetchFeatureImages,
        
        // Firebase imports
        db: typeof db !== 'undefined' ? 'defined' : 'UNDEFINED',
        auth: typeof auth !== 'undefined' ? 'defined' : 'UNDEFINED',
        storage: typeof storage !== 'undefined' ? 'defined' : 'UNDEFINED',
        
        // React hooks
        setArtworks: typeof setArtworks !== 'undefined' && typeof setArtworks === 'function' ? 'function' : typeof setArtworks,
        setProfile: typeof setProfile !== 'undefined' && typeof setProfile === 'function' ? 'function' : typeof setProfile,
        setIsLoading: typeof setIsLoading !== 'undefined' && typeof setIsLoading === 'function' ? 'function' : typeof setIsLoading
      };
      
      console.log('🔍 [ArtistWall] LOGIN TRANSITION - Variable checks:', loginVarChecks);
      
      // Find uninitialized variables
      const uninitializedVars = Object.entries(loginVarChecks)
        .filter(([_, value]) => value === 'undefined' || value === 'UNDEFINED')
        .map(([name, _]) => name);
      
      if (uninitializedVars.length > 0) {
        console.error('🚨 [ArtistWall] CRITICAL: UNINITIALIZED VARIABLES DURING LOGIN:', {
          variables: uninitializedVars,
          allChecks: loginVarChecks,
          stack: new Error().stack,
          timestamp: new Date().toISOString()
        });
        
        // Store in window for ErrorBoundary
        window.lastLoginTDZError = {
          variables: uninitializedVars,
          allChecks: loginVarChecks,
          timestamp: new Date().toISOString()
        };
        
        setLoginTransitionState('error');
      } else {
        console.log('✅ [ArtistWall] All variables initialized during login transition');
        setLoginTransitionState('logged-in');
      }
    }
    
    setPrevUser(user);
  }, [user, prevUser, authLoading, authContextRaw, authContext, variableInitState]);

  if (isMobile) {
    console.log('📱 [ArtistWall] Mobile device detected - Auth context:', {
      hasAuthContextRaw: !!authContextRaw,
      hasAuthContext: !!authContext,
      hasUser: !!user,
      hasUid: user?.uid ? true : false,
      authLoading: authLoading,
      loginTransitionState: loginTransitionState,
      userAgent: navigator.userAgent.substring(0, 50),
      timestamp: new Date().toISOString()
    });
    
    // Mobile-specific TDZ detection
    if (authContextRaw === undefined) {
      console.error('🚨 [ArtistWall] Mobile TDZ detected: authContextRaw is undefined');
    }
  }
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [msError, setMsError] = useState("");
  const [msData, setMsData] = useState(null);
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false); // Track upload state to prevent multiple clicks
  const [uploadStatusText, setUploadStatusText] = useState('Preparing your masterpiece...');
  const [wasArtworkShared, setWasArtworkShared] = useState(false); // Track if artwork was actually shared to co-learning space
  const [successModalArtworkType, setSuccessModalArtworkType] = useState(''); // Store artwork type for success modal navigation
  const [planConfirm, setPlanConfirm] = useState(null); // { id, label, price, suffix, currency }
  const [proratedDetails, setProratedDetails] = useState(null); // { proratedAmount, daysRemaining, currentPlan, newPlan, currentPrice, newPrice }
  const [planChangeStep, setPlanChangeStep] = useState(1); // Step for plan change confirmation (1, 2, or 3)
  const [msSuccess, setMsSuccess] = useState("");
  const [showUploadNudge, setShowUploadNudge] = useState(true);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [featureImages, setFeatureImages] = useState({});

  // Auto-redirect after sharing publicly (General Exhibit or co-learning tab).
  useEffect(() => {
    if (!showUploadSuccess || !wasArtworkShared) return;
    const destination = getSharedArtworkDestination(successModalArtworkType);
    if (!destination) return;

    const timer = setTimeout(() => {
      setShowUploadSuccess(false);
      setUploadSuccessMessage('');
      setWasArtworkShared(false);
      setSuccessModalArtworkType('');
      navigate(destination);
    }, 900);

    return () => clearTimeout(timer);
  }, [showUploadSuccess, wasArtworkShared, successModalArtworkType, navigate]);

  // For private uploads from learning paths, keep user on Artist Wall and auto-close success.
  useEffect(() => {
    if (!showUploadSuccess || wasArtworkShared) return;
    const type = (successModalArtworkType || '').toLowerCase();
    if (!['art-history', 'ways-of-seeing', 'book-co-learning', 'daily-activity', 'daily-acts'].includes(type)) return;
    const timer = setTimeout(() => {
      setShowUploadSuccess(false);
      setUploadSuccessMessage('');
      setSuccessModalArtworkType('');
    }, 1200);
    return () => clearTimeout(timer);
  }, [showUploadSuccess, wasArtworkShared, successModalArtworkType]);
  // Plan pricing - matches Membership.js
  const planMeta = {
    // Student plans (in ₹)
    base: { id: 'base', label: 'Base', price: 199, currency: '₹', suffix: '/mo' },
    pro: { id: 'pro', label: 'Pro', price: 399, currency: '₹', suffix: '/mo' },
    premium: { id: 'premium', label: 'Premium', price: 749, currency: '₹', suffix: '/mo' },
    // Billing frequency plans (in $) - monthly removed, only semiannual and annual
    semiannual: { id: 'semiannual', label: '6 Months', price: 49, currency: '$', suffix: '/6mo' },
    annual: { id: 'annual', label: 'Yearly', price: 89, currency: '$', suffix: '/yr' }
  };

  // Debug helper: explicit handler for General Exhibit navigation
  const handleGoToGeneralExhibit = () => {
    console.log('🔍 General Exhibit menu clicked - navigating to /gallery/general');
    // Temporary visual confirmation while debugging
    try {
      // eslint-disable-next-line no-alert
      alert('Navigating to General Exhibit...');
    } catch (_) {}
    navigate('/gallery/general');
    setShowExploreDropdown(false);
  };

  // Close dropdowns when global close event dispatched or on Escape
  // (click-outside is handled by an overlay inside the portal)
  useEffect(() => {
    const closeAll = (e) => {
      setShowExploreDropdown(false);
      setShowAccountDropdown(false);
      if (e?.detail !== 'artist-wall-nav') {
        setNavMenuOpen(false);
        setNavMenuAnchor(null);
      }
    };

    const onKey = (e) => {
      if (e.key === 'Escape') closeAll();
    };

    window.addEventListener('ui-close-popovers', closeAll);
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('ui-close-popovers', closeAll);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const updateNavMenuAnchor = React.useCallback(() => {
    const el = artistWallNavButtonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setNavMenuAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const toggleArtistWallNavMenu = () => {
    setNavMenuOpen((open) => {
      const next = !open;
      if (next) {
        window.dispatchEvent(new CustomEvent('ui-close-popovers', { detail: 'artist-wall-nav' }));
        updateNavMenuAnchor();
      } else {
        setNavMenuAnchor(null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!navMenuOpen) return;
    const close = (e) => {
      if (
        !e.target.closest('.artist-wall-nav-menu') &&
        !e.target.closest('[data-artist-wall-nav-dropdown]')
      ) {
        setNavMenuOpen(false);
        setNavMenuAnchor(null);
      }
    };
    const timerId = window.setTimeout(() => document.addEventListener('click', close, true), 0);
    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('click', close, true);
    };
  }, [navMenuOpen]);

  // Load membership status when Manage Subscription opens
  useEffect(() => {
    // Wait for auth to be ready before accessing user properties
    if (authLoading || !user || !user.uid) return;
    if (!showManageSubscription) return;
    
    const loadStatus = async () => {
      setMsLoading(true); setMsError("");
      try {
        const res = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}&_t=${Date.now()}`);
        const ct = res.headers.get('content-type') || '';
        const j = ct.includes('application/json') ? await res.json() : null;
        if (!res.ok) throw new Error((j && j.error) || 'Failed to fetch status');
        if (!j) throw new Error('Unexpected response');
        console.log('🔍 ArtistWall: Membership data received:', {
          tier: j?.membership?.tier,
          planId: j?.membership?.planId,
          selectedPlan: j?.membership?.selectedPlan,
          price: j?.membership?.price,
          lastChargeAmount: j?.membership?.lastChargeAmount,
          fullMembership: j?.membership
        });
        setMsData(j);
      } catch (e) {
        setMsError(e.message);
      } finally {
        setMsLoading(false);
      }
    };
    loadStatus();
    // MOBILE FIX: Check user/authLoading INSIDE effect, not in dependency array to avoid TDZ
  }, [showManageSubscription]); // Removed user, authLoading to prevent TDZ on mobile

  // Auto-switch features every 4 seconds
  useEffect(() => {
    const features = [
      { name: 'Storyboarding Studio', description: 'Create interactive stories with character and scene generation', icon: '📚', color: 'from-purple-400 to-pink-400' },
      { name: 'Art Appreciation', description: 'Discover the meaning and context behind famous artworks', icon: '🎨', color: 'from-blue-400 to-cyan-400' },
      { name: 'Art Style Transfer', description: 'Transform your artwork into different artistic styles from history', icon: '✨', color: 'from-amber-400 to-orange-400' },
      { name: 'Art History', description: 'Explore art movements, artists, and masterpieces from around the world', icon: '🏛️', color: 'from-emerald-400 to-teal-400' },
      { name: 'Art Comparison', description: 'Compare artworks side-by-side and learn about similarities and differences', icon: '🔍', color: 'from-indigo-400 to-purple-400' },
      { name: 'Daily Art Activity', description: 'Get personalized daily art challenges and creative prompts', icon: '📅', color: 'from-rose-400 to-pink-400' },
      { name: 'Ways of Seeing', description: 'Reimagine your artwork in different artistic styles and perspectives', icon: '👁️', color: 'from-yellow-400 to-amber-400' },
      { name: 'Re-imagine Artwork', description: 'Transform and enhance your creations with style transfers', icon: '🔄', color: 'from-violet-400 to-purple-400' }
    ];

    const interval = setInterval(() => {
      setCurrentFeatureIndex((prev) => (prev + 1) % features.length);
    }, 4000); // Switch every 4 seconds

    return () => clearInterval(interval);
  }, []);

  // CRITICAL: Define ALL functions BEFORE useEffect that calls them to prevent TDZ errors
  // Define resetForm FIRST since it's used by handleUpload, handleEdit, etc.
  const resetForm = () => {
    setTitle("");
    setTechnique("");
    setInspiration("");
    setDescription("");
    setTag(bookTags[0]);
    setFile(null);
    setVisibility("private");
    // Do not reset success redirect state here.
    // Upload success flows call resetForm() before auto-redirect effects run.
    setEditId(null);
    setEditLockedArtworkType(null);
    setEditSource(null);
    setExistingImageUrl(null);
    setIsBookActivity(false);
    setActivityCode("");
    setSelectedCountry("");
    setSelectedActivityNumber("");
    setArtworkType('');
    setArtHistoryActivities([]);
    setSelectedArtHistoryActivity(null);
    setWaysOfSeeingActivities([]);
    setSelectedWaysOfSeeingActivity(null);
    setShareToCoLearning(false);
    setWaysOfSeeingOriginalArtwork(null);
    setWaysOfSeeingStyleId(null);
    setWaysOfSeeingStyleName('');
    setWaysOfSeeingModalOriginalArtwork(null);
    setAvailableStylesForArtwork([]);
    setUploadFlowStep("sourceChoice");
    setIsFromBook(null);
    setSelectedBookId("");
    setBookSelectError("");
    setUploadWizardLock(null);
  };

  const clearStudioWizardFields = () => {
    setArtworkType("");
    setIsBookActivity(false);
    setActivityCode("");
    setSelectedCountry("");
    setSelectedActivityNumber("");
    setTag(bookTags[0]);
    setSelectedArtHistoryActivity(null);
    setWaysOfSeeingActivities([]);
    setSelectedWaysOfSeeingActivity(null);
    setWaysOfSeeingOriginalArtwork(null);
    setWaysOfSeeingStyleId(null);
    setWaysOfSeeingStyleName("");
    setWaysOfSeeingModalOriginalArtwork(null);
    setAvailableStylesForArtwork([]);
    setSelectedOriginalArtwork(null);
  };

  const handleUploadModalBack = () => {
    setBookSelectError("");
    if (uploadFlowStep === "generalUpload") {
      if (uploadWizardLock === "general-only") {
        setUploadWizardLock(null);
        setUploadFlowStep("sourceChoice");
        setIsFromBook(null);
        clearStudioWizardFields();
        return;
      }
      if (uploadWizardLock === "ways-of-seeing" || uploadWizardLock === "art-history" || uploadWizardLock === "daily-acts") {
        setUploadWizardLock(null);
        clearStudioWizardFields();
        setUploadFlowStep("studioActivityChoice");
        return;
      }
      setUploadFlowStep("sourceChoice");
      setIsFromBook(null);
      clearStudioWizardFields();
      return;
    }
    if (uploadFlowStep === "studioActivityChoice") {
      setUploadFlowStep("activityCategoryChoice");
      return;
    }
    if (uploadFlowStep === "activityCategoryChoice") {
      setUploadFlowStep("sourceChoice");
      setIsFromBook(null);
      return;
    }
    if (uploadFlowStep === "bookSelect") {
      setUploadFlowStep("activityCategoryChoice");
      setSelectedBookId("");
      return;
    }
    if (uploadFlowStep === "bookActivityUpload") {
      setUploadFlowStep("bookSelect");
      setArtworkType("");
      setIsBookActivity(false);
      setActivityCode("");
      setSelectedCountry("");
      setSelectedActivityNumber("");
    }
  };

  const confirmBookSelectionForUpload = () => {
    const book = createPageBooks.find((b) => b.id === selectedBookId);
    if (!book) {
      setBookSelectError("Please choose your ARTmosphere book.");
      return;
    }
    setBookSelectError("");
    setTag(book.uploadTag);
    setArtworkType("book-co-learning");
    setIsBookActivity(true);
    setActivityCode("");
    setSelectedCountry("");
    setSelectedActivityNumber("");
    setUploadFlowStep("bookActivityUpload");
  };

  // Allows other pages to mount ArtistWall upload modal as an overlay (must run after resetForm exists).
  useEffect(() => {
    if (forceOpenUpload) {
      resetForm();
      setShowUpload(true);
    }
  }, [forceOpenUpload]);

  // CRITICAL: Define fetchProfile BEFORE useEffect that calls it
  // Using function declaration (hoisted) instead of const arrow function for mobile compatibility
  async function fetchProfile(bypassCache = false) {
    // CRITICAL: Ensure cache utilities are available (mobile TDZ fix)
    if (typeof createCacheKey !== 'function' || typeof getCache !== 'function') {
      console.error('🚨 [ArtistWall] Cache utilities not available in fetchProfile');
      setIsLoading(false);
      return;
    }
    
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    const profileCacheKey = createCacheKey('artistProfile', { uid: user.uid }, user.uid);

    if (bypassCache) {
      invalidateArtistProfileCache(user.uid);
    } else {
      const cachedProfile = getCache(profileCacheKey);
      if (cachedProfile) {
        console.log('✅ [ARTIST WALL] Using cached profile');
        setProfile(cachedProfile);
        const name = await getDisplayName(user.uid);
        setDisplayName(name);
        setIsLoading(false);
        const cachedHasPhoto =
          !!cachedProfile.profilePicture ||
          (cachedProfile.isUnder13 && cachedProfile.avatarId);
        if (!cachedHasPhoto) {
          fetchProfile(true);
        }
        return;
      }
    }
    
    try {
      const [profileSnap, userDocSnap] = await Promise.all([
        getDoc(doc(db, "artistProfiles", user.uid)),
        getDoc(doc(db, "users", user.uid)),
      ]);
      const isUnder13User = userDocSnap.exists() && !!userDocSnap.data().isUnder13;

      if (profileSnap.exists()) {
        const profileData = { ...profileSnap.data(), isUnder13: isUnder13User };

        if (
          !isUnder13User &&
          !profileData.profilePicture &&
          userDocSnap.exists() &&
          userDocSnap.data().profilePicture
        ) {
          profileData.profilePicture = userDocSnap.data().profilePicture;
        }

        console.log('🔍 ArtistWall: Profile data loaded:', {
          hasProfilePicture: !!profileData.profilePicture,
          profilePictureUrl: profileData.profilePicture,
          avatarId: profileData.avatarId,
          isUnder13: isUnder13User,
          name: profileData.name,
          nickname: profileData.nickname,
        });

        setCache(profileCacheKey, profileData, 10 * 60 * 1000);
        setProfile(profileData);
        const name = await getDisplayName(user.uid);
        setDisplayName(name);
      } else {
        const name = await getDisplayName(user.uid);
        setDisplayName(name);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      if (user.displayName) {
        setDisplayName(user.displayName);
      }
    } finally {
      setIsLoading(false);
    }
  }

  // CRITICAL: Define fetchArtworks BEFORE useEffect that calls it
  // Using function declaration (hoisted) instead of const arrow function for mobile compatibility
  async function fetchArtworks() {
    // CRITICAL: Ensure cache utilities are available (mobile TDZ fix)
    if (typeof createCacheKey !== 'function' || typeof getCache !== 'function') {
      console.error('🚨 [ArtistWall] Cache utilities not available in fetchArtworks');
      return;
    }
    
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Instant UI: hydrate from cache first while network refreshes
      const artworksCacheKey = createCacheKey('artworks', { uid: user.uid }, user.uid);
      const cached = getCache(artworksCacheKey);
      if (cached && Array.isArray(cached) && cached.length > 0) {
        setArtworks(cached);
      }

      // Fetch from both artworks and sharedArtworks collections
      const [artworksQuery, sharedArtworksQuery] = await Promise.all([
        getDocs(query(collection(db, "artworks"), where("uid", "==", user.uid))),
        getDocs(query(collection(db, "sharedArtworks"), where("uid", "==", user.uid)))
      ]);
      
      // Map sharedArtworks to match artworks structure
      const sharedArtworksList = sharedArtworksQuery.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.artworkTitle || data.title,
          description: data.artworkDescription || data.description,
          technique: data.artworkTechnique || data.technique,
          inspiration: data.artworkInspiration || data.inspiration,
          imageUrl: data.artworkUrl || data.imageUrl,
          thumbnailUrl: data.thumbnailUrl || data.artworkThumbnailUrl || data.artworkThumbnail || data.artworkUrl || data.imageUrl,
          visibility: data.shareWithArtmosphere ? 'public' : 'private',
          source: 'sharedArtworks',
          ...data // Include all other fields
        };
      });
      
      // Combine artworks from both collections
      const artworksCombined = [
        ...artworksQuery.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          source: 'artworks' // Track source
        })),
        ...sharedArtworksQuery.docs.map((doc) => {
          const data = doc.data();
          // Map sharedArtworks structure to artworks structure for consistency
          return {
            id: doc.id,
            title: data.artworkTitle || data.title || '',
            description: data.artworkDescription || data.description || '',
            imageUrl: data.artworkUrl || data.imageUrl || '',
            thumbnailUrl: data.thumbnailUrl || data.artworkThumbnailUrl || data.artworkThumbnail || data.artworkUrl || data.imageUrl || '',
            technique: data.artworkTechnique || data.technique || '',
            inspiration: data.artworkInspiration || data.inspiration || '',
            tag: data.tag || '',
            visibility: data.shareWithArtmosphere ? 'public' : 'private',
            ...data,
            source: 'sharedArtworks' // Track source
          };
        })
      ];

      // Filter by environment: staging sees only staging-tagged docs; prod sees all
      const artworksData =
        ENV === 'staging'
          // On staging: show ONLY staging-tagged artworks
          ? artworksCombined.filter(a => a.environment === 'staging')
          // On prod: hide staging-tagged docs; show prod or legacy (no env)
          : artworksCombined.filter(a => !a.environment || a.environment === 'prod');

      // Sort latest → oldest by createdAt timestamp (fallback to 0 when missing)
      artworksData.sort((a, b) => {
        const aTime = a.createdAt && typeof a.createdAt.toMillis === 'function' ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt && typeof b.createdAt.toMillis === 'function' ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      });

      // Instant paint: preserve already-resolved URLs from current state/snapshot
      // so refresh/login does not regress to a brief skeleton flash.
      const previousById = new Map((artworks || []).map((a) => [a.id, a]));
      const artworksFast = artworksData.map((artwork) => {
        const rawUrl = artwork?.thumbnailUrl || artwork?.imageUrl || artwork?.artworkUrl || '';
        const syncResolved = resolveImageUrlUtil(rawUrl) || rawUrl || '';
        const prev = previousById.get(artwork.id);
        const prevThumb = prev?.thumbnailUrl || '';
        const keepPrevThumb = typeof prevThumb === 'string' && /^https?:\/\//i.test(prevThumb);
        const stable = stableArtworkUrlById.get(artwork.id);
        const nextThumb = stable || (keepPrevThumb ? prevThumb : syncResolved);
        if (artwork?.id && typeof nextThumb === 'string' && /^https?:\/\//i.test(nextThumb)) {
          stableArtworkUrlById.set(artwork.id, nextThumb);
        }
        return {
          ...artwork,
          thumbnailUrl: nextThumb
        };
      });

      // Start decoding/downloading the first visible set immediately (raw urls).
      // This reduces perceived delay even before async refinement finishes.
      try {
        const warmUrls = artworksData
          .map((a) => a?.thumbnailUrl || a?.imageUrl || a?.artworkUrl)
          .filter(Boolean)
          .slice(0, 24);
        if (warmUrls.length > 0) warmResolvedImageUrls(warmUrls, 24).catch(() => {});
      } catch (_) {}

      setArtworks(artworksFast);
      setCache(artworksCacheKey, artworksFast, 5 * 60 * 1000);
      console.log(`✅ Loaded ${artworksFast.length} artworks (${artworksQuery.docs.length} from artworks, ${sharedArtworksQuery.docs.length} from sharedArtworks)`);

      // Background refinement: resolve storage references per card (no all-or-nothing wait).
      artworksData.forEach((artwork) => {
        const rawUrl = artwork?.thumbnailUrl || artwork?.imageUrl || artwork?.artworkUrl || '';
        resolveImageUrlAsync(rawUrl)
          .then((resolvedImageUrl) => {
            const nextUrl = resolvedImageUrl || rawUrl || '';
            if (!nextUrl) return;
            if (artwork?.id && /^https?:\/\//i.test(nextUrl)) {
              stableArtworkUrlById.set(artwork.id, nextUrl);
            }
            setArtworks((prev) =>
              (prev || []).map((item) =>
                item.id === artwork.id && item.thumbnailUrl !== nextUrl
                  ? { ...item, thumbnailUrl: nextUrl }
                  : item
              )
            );
          })
          .catch(() => {});
      });
    } catch (error) {
      console.error("Error fetching artworks:", error);
    }
  }

  // When auth hydrates after first paint, load data (legacy empty-deps effect may have skipped).
  useEffect(() => {
    if (authLoading || !user?.uid) return;
    try {
      fetchArtworks();
      fetchProfile();
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, authLoading]);

  useEffect(() => {
    try {
      if (Array.isArray(artworks) && artworks.length > 0) {
        writeArtistWallSnapshot(user?.uid, artworks);
      }
    } catch (_) {}
  }, [user?.uid, artworks]);

  // Warm image cache for first visible cards to reduce perceived lag.
  // UseLayoutEffect + sync prewarm so cards can show immediately on refresh/login.
  useLayoutEffect(() => {
    const urls = (artworks || [])
      .map((a) => a?.thumbnailUrl || a?.imageUrl || a?.artworkUrl)
      .filter(Boolean)
      .slice(0, 24);

    // Sync prewarm for http(s) urls.
    try {
      urls
        .map((u) => resolveImageUrlUtil(u))
        .filter((u) => typeof u === 'string' && /^https?:\/\//i.test(u))
        .forEach((u) => {
          try {
            const img = new Image();
            img.decoding = 'async';
            img.src = u;
          } catch (_) {}
        });
    } catch (_) {}

    // Async fallback (resolves any remaining storage ref formats).
    warmResolvedImageUrls(urls, 24).catch(() => {});
  }, [artworks]);


  // CRITICAL: Define fetchFeatureImages BEFORE useEffect that calls it
  // Using function declaration (hoisted) instead of const arrow function for mobile compatibility
  async function fetchFeatureImages() {
    // CRITICAL: Ensure cache utilities are available (mobile TDZ fix)
    if (typeof createCacheKey !== 'function' || typeof getCache !== 'function') {
      console.error('🚨 [ArtistWall] Cache utilities not available in fetchFeatureImages');
      return;
    }
    
    // Check cache first
    const featuresCacheKey = createCacheKey('featureImages');
    const cachedFeatures = getCache(featuresCacheKey);
    if (cachedFeatures) {
      console.log('✅ [ARTIST WALL] Using cached feature images');
      setFeatureImages(cachedFeatures);
      return;
    }
    
    try {
      const featureImageMap = {};
      
      // First, try to fetch from Firestore collection 'features'
      try {
        const featuresCollection = collection(db, 'features');
        const featuresSnapshot = await getDocs(featuresCollection);
        featuresSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.imageUrl || data.image) {
            featureImageMap[doc.id] = data.imageUrl || data.image;
            console.log(`✅ Found image in Firestore for ${doc.id}:`, data.imageUrl || data.image);
          }
        });
      } catch (firestoreError) {
        console.log('Features collection not found in Firestore, trying Storage...', firestoreError);
      }

      // Then try Firebase Storage for any missing images.
      // In localhost/dev, Storage rules often block these reads and flood console with 403/404 noise.
      // We skip probing locally and let the UI fall back to gradient cards/icons.
      const features = [
        'art-style-transfer',
        'ways-of-seeing',
        're-imagine-artwork',
        'art-appreciation',
        'art-history',
        'art-comparison',
        'storyboarding-studio',
        'co-learning-space',
        'daily-art-activity'
      ];

      const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
      if (!isLocalhost) {
        for (const feature of features) {
          // Skip if already found in Firestore
          if (featureImageMap[feature]) {
            continue;
          }

          // Try different storage paths
          const paths = [
            `features/${feature}.jpg`,
            `features/${feature}.png`,
            `features/${feature}.jpeg`,
            `feature-images/${feature}.jpg`,
            `feature-images/${feature}.png`,
            `feature-images/${feature}.jpeg`,
            `features/${feature}`,
            `feature-images/${feature}`,
            `featureImages/${feature}.jpg`,
            `featureImages/${feature}.png`
          ];

          for (const path of paths) {
            try {
              const imageRef = ref(storage, path);
              const url = await getDownloadURL(imageRef);
              featureImageMap[feature] = url;
              break; // Found image, move to next feature
            } catch (error) {
              continue;
            }
          }
        }
      } else {
        console.log('ℹ️ [ARTIST WALL] Skipping Storage feature image probing on localhost');
      }
      
      // Cache the feature images (30 min cache - feature images don't change often)
      setCache(featuresCacheKey, featureImageMap, 30 * 60 * 1000);
      console.log('✅ [ARTIST WALL] Feature images cached');
      
      setFeatureImages(featureImageMap);
      console.log('📊 Feature images summary:', {
        total: features.length,
        loaded: Object.keys(featureImageMap).length,
        missing: features.filter(f => !featureImageMap[f]),
        loadedFeatures: Object.keys(featureImageMap)
      });
    } catch (error) {
      console.error('❌ Error fetching feature images:', error);
    }
  }

  // CRITICAL: useEffect to call fetch functions - must be AFTER ALL function definitions
  // Wait for auth to be ready before fetching data
  // MOBILE FIX: Use empty dependency array like BEFORE optimization to avoid TDZ
  // Check user/authLoading INSIDE the effect, not in dependency array
  useEffect(() => {
    // CRITICAL: Delay execution to ensure all variables are initialized
    // This prevents TDZ errors on mobile browsers
    const timeoutId = setTimeout(() => {
      try {
        // Access loginTransitionState INSIDE effect to avoid TDZ (like user/authLoading)
        const currentLoginTransitionState = loginTransitionState;
        
        // LOGIN-SPECIFIC: Track this execution context
        const executionContext = {
          isLoginTransition: currentLoginTransitionState === 'logging-in' || currentLoginTransitionState === 'logged-in',
          loginTransitionState: currentLoginTransitionState,
          timestamp: new Date().toISOString()
        };
        
        console.log('🔐 [ArtistWall] Post-login useEffect triggered:', executionContext);
        
        // Access user and authLoading INSIDE the effect to avoid TDZ in dependency array
        // Before optimization, this useEffect had empty dependency array []
        const currentUser = user;
        const currentAuthLoading = authLoading;
        
        // CRITICAL: Comprehensive variable validation BEFORE any access
        const allVarChecks = {
          // Auth variables
          user: typeof currentUser !== 'undefined' ? 'defined' : 'UNDEFINED',
          authLoading: typeof currentAuthLoading !== 'undefined' ? 'defined' : 'UNDEFINED',
          userUid: currentUser?.uid ? 'defined' : 'UNDEFINED',
          
          // Cache utilities - CRITICAL for login flow
          createCacheKey: typeof createCacheKey !== 'undefined' && typeof createCacheKey === 'function' ? 'function' : (typeof createCacheKey === 'undefined' ? 'UNDEFINED' : typeof createCacheKey),
          getCache: typeof getCache !== 'undefined' && typeof getCache === 'function' ? 'function' : (typeof getCache === 'undefined' ? 'UNDEFINED' : typeof getCache),
          setCache: typeof setCache !== 'undefined' && typeof setCache === 'function' ? 'function' : (typeof setCache === 'undefined' ? 'UNDEFINED' : typeof setCache),
          
          // Fetch functions
          fetchArtworks: typeof fetchArtworks !== 'undefined' && typeof fetchArtworks === 'function' ? 'function' : (typeof fetchArtworks === 'undefined' ? 'UNDEFINED' : typeof fetchArtworks),
          fetchProfile: typeof fetchProfile !== 'undefined' && typeof fetchProfile === 'function' ? 'function' : (typeof fetchProfile === 'undefined' ? 'UNDEFINED' : typeof fetchProfile),
          fetchFeatureImages: typeof fetchFeatureImages !== 'undefined' && typeof fetchFeatureImages === 'function' ? 'function' : (typeof fetchFeatureImages === 'undefined' ? 'UNDEFINED' : typeof fetchFeatureImages),
          
          // Firebase imports
          db: typeof db !== 'undefined' ? 'defined' : 'UNDEFINED',
          auth: typeof auth !== 'undefined' ? 'defined' : 'UNDEFINED',
          storage: typeof storage !== 'undefined' ? 'defined' : 'UNDEFINED',
          
          // React state setters
          setArtworks: typeof setArtworks !== 'undefined' && typeof setArtworks === 'function' ? 'function' : 'UNDEFINED',
          setProfile: typeof setProfile !== 'undefined' && typeof setProfile === 'function' ? 'function' : 'UNDEFINED',
          setIsLoading: typeof setIsLoading !== 'undefined' && typeof setIsLoading === 'function' ? 'function' : 'UNDEFINED'
        };
        
        console.log('🔍 [ArtistWall] Comprehensive variable checks:', allVarChecks);
        
        // Find uninitialized variables
        const uninitializedVars = Object.entries(allVarChecks)
          .filter(([_, value]) => value === 'undefined' || value === 'UNDEFINED')
          .map(([name, _]) => name);
        
        if (uninitializedVars.length > 0) {
          const error = new Error(`TDZ Error: Variables accessed before initialization: ${uninitializedVars.join(', ')}`);
          console.error('🚨 [ArtistWall] CRITICAL TDZ DETECTED in useEffect:', {
            uninitializedVariables: uninitializedVars,
            allChecks: allVarChecks,
            executionContext: executionContext,
            error: error.message,
            stack: new Error().stack,
            timestamp: new Date().toISOString()
          });
          
          // Store for ErrorBoundary
          window.lastTDZError = {
            variables: uninitializedVars,
            allChecks: allVarChecks,
            executionContext: executionContext,
            timestamp: new Date().toISOString()
          };
          
          setIsLoading(false);
          return;
        }
        
        // Enhanced logging for post-login authentication validation
        console.log('🔐 [ArtistWall] Auth state check:', {
          authLoading: currentAuthLoading,
          hasUser: !!currentUser,
          hasUid: currentUser?.uid ? true : false,
          timestamp: new Date().toISOString()
        });
        
        if (currentAuthLoading || !currentUser || !currentUser.uid) {
          console.log('⏳ [ArtistWall] Waiting for auth to be ready...', {
            authLoading: currentAuthLoading,
            hasUser: !!currentUser,
            hasUid: currentUser?.uid ? true : false
          });
          setIsLoading(true);
          return;
        }
        
        console.log('✅ [ArtistWall] Auth ready, calling fetch functions...');
        
        // CRITICAL: Double-check cache utilities before calling fetch functions
        if (typeof createCacheKey !== 'function' || typeof getCache !== 'function' || typeof setCache !== 'function') {
          console.error('🚨 [ArtistWall] Cache utilities not available before fetch calls:', {
            createCacheKey: typeof createCacheKey,
            getCache: typeof getCache,
            setCache: typeof setCache
          });
          setIsLoading(false);
          return;
        }

        // Preload top visible artwork images immediately on auth success (before navigation/fetch).
        // Uses session snapshot (raw thumbnail/image/artwork URLs) so decode/download starts ASAP.
        try {
          const parsed = readArtistWallSnapshot(currentUser?.uid);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const urls = parsed
              .slice(0, 24)
              .map((a) => a?.thumbnailUrl || a?.imageUrl || a?.artworkUrl)
              .filter(Boolean);
            if (urls.length > 0) {
              warmResolvedImageUrls(urls, 24).catch(() => {});
            }
          }
        } catch (_) {}
        
        // Call functions directly - they're hoisted function declarations
        try {
          console.log('✅ [ArtistWall] Calling fetchArtworks...');
          fetchArtworks();
        } catch (err) {
          const errorMessage = err?.message || err?.toString() || 'Unknown error';
          const isTDZ = errorMessage.includes('uninitialized') || errorMessage.includes('TDZ') || errorMessage.includes('before initialization');
          const variableMatch = errorMessage.match(/variable ['"]([^'"]+)['"]/i) ||
                               errorMessage.match(/['"]([a-zA-Z_$][a-zA-Z0-9_$]*)['"]/i) ||
                               errorMessage.match(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s+(?:is\s+not\s+defined|before\s+initialization)/i);
          const variableName = variableMatch ? variableMatch[1] : 'UNKNOWN_VARIABLE';
          
          console.error('❌ [ArtistWall] Error calling fetchArtworks:', {
            error: errorMessage,
            stack: err?.stack,
            isTDZ: isTDZ,
            variableName: variableName,
            timestamp: new Date().toISOString()
          });
          
          if (isTDZ) {
            window.lastTDZError = {
              variables: [variableName],
              function: 'fetchArtworks',
              error: errorMessage,
              timestamp: new Date().toISOString()
            };
          }
          
          setIsLoading(false);
          return;
        }
        
        try {
          console.log('✅ [ArtistWall] Calling fetchProfile...');
          fetchProfile(!!location?.state?.profileUpdated);
        } catch (err) {
          const errorMessage = err?.message || err?.toString() || 'Unknown error';
          const isTDZ = errorMessage.includes('uninitialized') || errorMessage.includes('TDZ') || errorMessage.includes('before initialization');
          const variableMatch = errorMessage.match(/variable ['"]([^'"]+)['"]/i) ||
                               errorMessage.match(/['"]([a-zA-Z_$][a-zA-Z0-9_$]*)['"]/i) ||
                               errorMessage.match(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s+(?:is\s+not\s+defined|before\s+initialization)/i);
          const variableName = variableMatch ? variableMatch[1] : 'UNKNOWN_VARIABLE';
          
          console.error('❌ [ArtistWall] Error calling fetchProfile:', {
            error: errorMessage,
            stack: err?.stack,
            isTDZ: isTDZ,
            variableName: variableName,
            timestamp: new Date().toISOString()
          });
          
          if (isTDZ) {
            window.lastTDZError = {
              variables: [variableName],
              function: 'fetchProfile',
              error: errorMessage,
              timestamp: new Date().toISOString()
            };
          }
          
          setIsLoading(false);
          return;
        }
        
        try {
          console.log('✅ [ArtistWall] Calling fetchFeatureImages...');
          fetchFeatureImages();
        } catch (err) {
          const errorMessage = err?.message || err?.toString() || 'Unknown error';
          const isTDZ = errorMessage.includes('uninitialized') || errorMessage.includes('TDZ') || errorMessage.includes('before initialization');
          const variableMatch = errorMessage.match(/variable ['"]([^'"]+)['"]/i) ||
                               errorMessage.match(/['"]([a-zA-Z_$][a-zA-Z0-9_$]*)['"]/i) ||
                               errorMessage.match(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s+(?:is\s+not\s+defined|before\s+initialization)/i);
          const variableName = variableMatch ? variableMatch[1] : 'UNKNOWN_VARIABLE';
          
          console.error('❌ [ArtistWall] Error calling fetchFeatureImages:', {
            error: errorMessage,
            stack: err?.stack,
            isTDZ: isTDZ,
            variableName: variableName,
            timestamp: new Date().toISOString()
          });
          
          if (isTDZ) {
            window.lastTDZError = {
              variables: [variableName],
              function: 'fetchFeatureImages',
              error: errorMessage,
              timestamp: new Date().toISOString()
            };
          }
          
          setIsLoading(false);
          return;
        }
        
        console.log('✅ [ArtistWall] All fetch functions called successfully');
      } catch (error) {
        // Access loginTransitionState INSIDE catch to avoid TDZ
        const currentLoginTransitionStateInCatch = loginTransitionState;
        
        // Enhanced error extraction for TDZ errors
        const errorMessage = error?.message || error?.toString() || 'Unknown error';
        const isTDZ = errorMessage.includes('uninitialized') || errorMessage.includes('TDZ') || errorMessage.includes('before initialization');
        const variableMatch = errorMessage.match(/variable ['"]([^'"]+)['"]/i) ||
                             errorMessage.match(/['"]([a-zA-Z_$][a-zA-Z0-9_$]*)['"]/i) ||
                             errorMessage.match(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s+(?:is\s+not\s+defined|before\s+initialization)/i);
        const variableName = variableMatch ? variableMatch[1] : 'UNKNOWN_VARIABLE';
        
        console.error('🚨 [ArtistWall] CRITICAL ERROR in post-login useEffect:', {
          error: errorMessage,
          stack: error?.stack,
          isTDZ: isTDZ,
          variableName: variableName,
          loginTransitionState: currentLoginTransitionStateInCatch,
          timestamp: new Date().toISOString()
        });
        
        if (isTDZ) {
          window.lastTDZError = {
            variables: [variableName],
            error: errorMessage,
            stack: error?.stack,
            timestamp: new Date().toISOString()
          };
        }
        
        setIsLoading(false);
      }
    }, 0); // Delay by 0ms to push to next event loop tick
    
    return () => clearTimeout(timeoutId);
    // CRITICAL: Empty dependency array like BEFORE optimization
    // This prevents TDZ errors on mobile when React evaluates dependency array
    // We check user/authLoading INSIDE the effect instead
  }, []); // Empty array - same as before optimization!

  // Fresh profile after saving from Artist Profile (photo + fields)
  useEffect(() => {
    if (!location?.state?.profileUpdated || !auth.currentUser) return;
    fetchProfile(true);
    navigate('.', { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when returning from profile save
  }, [location?.state?.profileUpdated]);

  // Open Ways of Seeing when navigated with state from tutorial upload
  useEffect(() => {
    try {
      if (location && location.state && location.state.openWaysOfSeeing && location.state.artwork) {
        setWaysOfSeeingModalOriginalArtwork(location.state.artwork);
        setSelectedOriginalArtworkTitle(location.state.title || '');
        setShowWaysOfSeeing(true);
        // clear once used
        navigate('.', { replace: true, state: {} });
      }
    } catch (_) {}
  }, [location]);

  // Open upload modal when navigated with state (e.g. from My Art World / Magic Studio)
  useEffect(() => {
    try {
      if (location?.state?.openUpload) {
        resetForm();
        setShowUpload(true);
        navigate(".", { replace: true, state: {} });
      }
    } catch (_) {}
  }, [location, navigate]);

  // Deep-link from public profile: book country upload for Artist Salon
  useEffect(() => {
    try {
      const s = location?.state;
      if (!s?.openBookMuseumUpload) return;
      const bookTag = s.bookTag || "Around the world with 100 artists";
      const country = s.selectedCountry || "";
      const preferredBookId = s.bookId || "book1";
      resetForm();
      const book =
        createPageBooks.find((b) => b.id === preferredBookId) ||
        createPageBooks.find(
          (b) => String(b.uploadTag || "").trim() === String(bookTag).trim()
        );
      if (book) {
        setSelectedBookId(book.id);
        setTag(book.uploadTag);
      } else {
        setTag(bookTag);
      }
      setArtworkType("book-co-learning");
      setIsBookActivity(true);
      setSelectedCountry(country);
      setActivityCode("");
      setSelectedActivityNumber("");
      setUploadFlowStep("bookActivityUpload");
      setShowUpload(true);
      navigate(".", { replace: true, state: {} });
    } catch (_) {}
  }, [location, navigate]);

  // Force notification refresh when artworks change
  useEffect(() => {
    setNotificationRefresh(prev => prev + 1);
  }, [artworks]);

  // Fetch Art History activities when artwork type is set to art-history.
  // Includes both legacy userActivities (type: art-history-appreciation)
  // and new Learn Art History flows from artHistoryFlows (cards).
  useEffect(() => {
    if (artworkType === 'art-history' && user) {
      const fetchArtHistoryActivities = async () => {
        // Check cache first
        const cacheKey = createCacheKey('artHistoryActivities', { uid: user.uid, type: 'art-history-appreciation' }, user.uid);
        const cached = getCache(cacheKey);
        if (cached) {
          console.log('✅ [ARTIST WALL] Using cached Art History activities:', cached.length);
          setArtHistoryActivities(cached);
          return;
        }
        
        try {
          // Learn Art History flows from artHistoryFlows (one card per artwork)
          const flowCards = await getArtworkCards(user.uid);
          const flowActivities = Array.isArray(flowCards)
            ? flowCards
                // Only include flows where Step 1 and Step 2 are complete
                .filter(card => {
                  const summary = card.lastFlowSummary || {};
                  return summary.step1Complete && summary.step2Complete;
                })
                .map(card => {
                const primary = card.primaryArtwork || {};
                return {
                  id: card.lastFlowId || card.id,
                  type: 'learn-art-history-flow',
                  title: primary.title || 'Art History Learning',
                  description: card.artworkTypeLabel || primary.artMovement || 'Art history learning flow',
                  originalImageUrl: primary.imageUrl || '',
                  metadata: {
                    feature: 'learn-art-history-flow',
                    artworkTitle: primary.title || 'Art History Learning',
                    artworkImage: primary.imageUrl || '',
                    artworkMovement: primary.artMovement || '',
                    artworkTypeKey: card.artworkTypeKey || '',
                    artworkTypeLabel: card.artworkTypeLabel || '',
                    level: card.level || 1
                  }
                };
              })
            : [];

          const activities = flowActivities;

          // Cache the combined results (5 min cache)
          setCache(cacheKey, activities, 5 * 60 * 1000);
          console.log('✅ [ARTIST WALL] Art History activities (flows only) cached:', flowActivities.length);
          
          setArtHistoryActivities(activities);
          console.log(`✅ Loaded ${activities.length} Art History activities (flows only)`);
        } catch (error) {
          console.error('Error fetching Art History activities:', error);
          setArtHistoryActivities([]);
        }
      };
      fetchArtHistoryActivities();
    }
    // MOBILE FIX: Check user/authLoading INSIDE effect, not in dependency array to avoid TDZ
  }, [artworkType]); // Removed user, authLoading to prevent TDZ on mobile

  // Fetch Ways of Seeing activities when artwork type is set to ways-of-seeing
  useEffect(() => {
    if (artworkType === 'ways-of-seeing' && user) {
      const fetchWaysOfSeeingActivities = async () => {
        // Check cache first
        const waysCacheKey = createCacheKey('waysOfSeeingActivities', { uid: user.uid, type: 'reimagine' }, user.uid);
        const cachedWays = getCache(waysCacheKey);
        if (cachedWays) {
          console.log('✅ [ARTIST WALL] Using cached Ways of Seeing activities:', cachedWays.length);
          setWaysOfSeeingActivities(cachedWays);
          return;
        }
        
        try {
          const activitiesQuery = query(
            collection(db, 'userActivities'),
            where('userId', '==', user.uid),
            where('type', '==', 'reimagine')
          );
          
          const snapshot = await getDocs(activitiesQuery);
          const activities = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(activity => {
              // Filter for ways-of-seeing related activities
              // Include all reimagine activities that have a transformed image or style info
              const feature = activity.metadata?.feature;
              const hasTransformedImage = !!(activity.transformedImageUrl || activity.metadata?.transformations?.length > 0);
              const hasStyleInfo = !!(activity.styleInfo || activity.metadata?.styleName);
              
              // Include if it matches ways-of-seeing features OR if it has transformed image/style info
              return feature === 'ways-of-seeing-journey' || 
                     feature === 'reimagine-artwork' || 
                     feature === 'style-transformation' ||
                     (hasTransformedImage && hasStyleInfo) ||
                     (!feature && hasTransformedImage);
            })
            .sort((a, b) => {
              // Sort by creation date, newest first
              const aTime = a.createdAt?.toMillis?.() || a.createdAt?._seconds * 1000 || 0;
              const bTime = b.createdAt?.toMillis?.() || b.createdAt?._seconds * 1000 || 0;
              return bTime - aTime;
            });
          
          // Log sample activity structure for debugging
          if (activities.length > 0) {
            console.log('📊 Sample Ways of Seeing activity structure:', {
              id: activities[0].id,
              hasTransformedImageUrl: !!activities[0].transformedImageUrl,
              transformedImageUrl: activities[0].transformedImageUrl?.substring(0, 100),
              hasTransformations: !!activities[0].metadata?.transformations,
              transformationsCount: activities[0].metadata?.transformations?.length || 0,
              styleName: activities[0].metadata?.styleName || activities[0].styleInfo?.name,
              metadataKeys: Object.keys(activities[0].metadata || {}),
              topLevelKeys: Object.keys(activities[0]).filter(k => !['metadata', 'styleInfo'].includes(k))
            });
          }
          
          setWaysOfSeeingActivities(dedupeWaysOfSeeingActivitiesByArtworkStyle(activities));
          console.log(`✅ Loaded ${activities.length} Ways of Seeing activities`);
        } catch (error) {
          console.error('Error fetching Ways of Seeing activities:', error);
          setWaysOfSeeingActivities([]);
        }
      };
      fetchWaysOfSeeingActivities();
    }
    // MOBILE FIX: Check user/authLoading INSIDE effect, not in dependency array to avoid TDZ
  }, [artworkType]); // Removed user, authLoading to prevent TDZ on mobile

  // When original artwork is selected, find all available styles for that artwork
  useEffect(() => {
    if (selectedOriginalArtwork && waysOfSeeingActivities.length > 0) {
      const originalImageUrl = selectedOriginalArtwork.imageUrl || selectedOriginalArtwork.originalImageUrl;
      if (originalImageUrl) {
        // Find all activities that match this original artwork
        const matchingActivities = waysOfSeeingActivities.filter(activity => {
          const activityOriginalUrl = activity.originalImageUrl || activity.metadata?.originalImageUrl;
          return activityOriginalUrl === originalImageUrl;
        });
        
        // Extract unique styles from matching activities
        const styles = matchingActivities.map(activity => ({
          activityId: activity.id,
          styleName: activity.metadata?.styleName || activity.styleInfo?.name || 'Unknown Style',
          styleId: activity.styleInfo?.id || activity.metadata?.styleId || 'default',
          activity: activity
        }));
        
        // Remove duplicates based on styleName
        const uniqueStyles = styles.reduce((acc, style) => {
          if (!acc.find(s => s.styleName === style.styleName)) {
            acc.push(style);
          }
          return acc;
        }, []);
        
        setAvailableStylesForArtwork(uniqueStyles);
        console.log(`✅ Found ${uniqueStyles.length} styles for selected artwork`);
      }
    } else {
      setAvailableStylesForArtwork([]);
    }
  }, [selectedOriginalArtwork, waysOfSeeingActivities]);

  // Auto-set default values for Ways of Seeing when style is selected
  useEffect(() => {
    if (artworkType === 'ways-of-seeing' && waysOfSeeingStyleName) {
      // Set default inspiration
      if (!inspiration || inspiration === '') {
        setInspiration('Artmosphere Ways of Seeing Tutorial');
      }
      // Set default technique (Art Style) to the selected style name
      if (!technique || technique === '') {
        setTechnique(waysOfSeeingStyleName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waysOfSeeingStyleName, artworkType]);

  // Auto-switch features every 4 seconds
  useEffect(() => {
    // Organized features by category - matches the JSX structure
    const features = [
      // Magic Studio Features
      { 
        name: 'Art Style Transfer', 
        description: 'Transform your artwork into different artistic styles from history', 
        category: 'Magic Studio',
        color: 'from-amber-100 to-orange-100',
        borderColor: 'border-amber-200'
      },
      { 
        name: 'Ways of Seeing', 
        description: 'Reimagine your artwork in different artistic styles and perspectives', 
        category: 'Magic Studio',
        color: 'from-yellow-100 to-amber-100',
        borderColor: 'border-yellow-200'
      },
      { 
        name: 'Re-imagine Artwork', 
        description: 'Transform and enhance your creations with creative style transfers', 
        category: 'Magic Studio',
        color: 'from-orange-100 to-amber-100',
        borderColor: 'border-orange-200'
      },
      // Art History Features
      { 
        name: 'Art Appreciation', 
        description: 'Discover the meaning and context behind famous artworks with detailed insights', 
        category: 'Art History',
        color: 'from-amber-50 to-yellow-50',
        borderColor: 'border-amber-100'
      },
      { 
        name: 'Art History', 
        description: 'Explore art movements, artists, and masterpieces from around the world', 
        category: 'Art History',
        color: 'from-yellow-50 to-amber-50',
        borderColor: 'border-yellow-100'
      },
      { 
        name: 'Art Comparison', 
        description: 'Compare artworks side-by-side and learn about similarities and differences', 
        category: 'Art History',
        color: 'from-orange-50 to-amber-50',
        borderColor: 'border-orange-100'
      },
      // Once Upon a Click Features
      { 
        name: 'Storyboarding Studio', 
        description: 'Create interactive stories with character and scene generation', 
        category: 'Once Upon a Click',
        color: 'from-amber-100 to-yellow-100',
        borderColor: 'border-amber-200'
      },
      // Artmosphere Co-Learning Space
      { 
        name: 'Artmosphere Co-Learning Space', 
        description: 'Share and explore art history activities with the community in a collaborative space', 
        category: 'Community',
        color: 'from-yellow-100 to-amber-100',
        borderColor: 'border-yellow-200'
      },
      // Daily Activity
      { 
        name: 'Daily Art Activity', 
        description: 'Get personalized daily art challenges and creative prompts', 
        category: 'Daily',
        color: 'from-amber-50 to-orange-50',
        borderColor: 'border-amber-100'
      }
    ];

    const interval = setInterval(() => {
      setCurrentFeatureIndex((prev) => (prev + 1) % features.length);
    }, 4000); // Switch every 4 seconds

    return () => clearInterval(interval);
  }, []);

  const artworkIdsKey = useMemo(
    () => (artworks || []).map((a) => a.id).filter(Boolean).join(','),
    [artworks]
  );

  useEffect(() => {
    if (!user?.uid || !artworkIdsKey) return;
    let cancelled = false;
    const list = artworks || [];
    fetchReactionCountsForArtworks(list).then((map) => {
      if (!cancelled) setReactionCountsByArtworkId(map);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, artworkIdsKey]);

  useEffect(() => {
    if (!curatedWallReactionPopover) return undefined;
    const onPointerDown = (event) => {
      const root = curatedReactionPopoverRef.current;
      if (root && root.contains(event.target)) return;
      if (event.target.closest?.('[data-curated-reaction-trigger]')) return;
      setCuratedWallReactionPopover(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setCuratedWallReactionPopover(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [curatedWallReactionPopover]);

  // Safety check to ensure all state is initialized
  if (typeof isLoading === 'undefined') {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-amber-800 text-xl">Initializing...</div>
      </div>
    );
  }

  // fetchProfile and fetchArtworks are now defined above (around line 227) to prevent TDZ error

  const handleKudosChange = (artworkId, newCount) => {
    console.log('ArtistWall: handleKudosChange called', { artworkId, newCount });
    setArtworks(prev => prev.map(art => 
      art.id === artworkId 
        ? { ...art, kudosCount: newCount }
        : art
    ));
    setReactionCountsByArtworkId((prev) => ({ ...prev, [artworkId]: newCount }));
    setNotificationRefresh(prev => prev + 1);
  };

  const handleCommentChange = (artworkId) => {
    console.log('ArtistWall: handleCommentChange called', { artworkId });
    // Force notification refresh when comments are added
    setNotificationRefresh(prev => prev + 1);
    console.log('ArtistWall: notificationRefresh incremented');
  };

  const openCuratedWallReactionList = async (e, artworkId) => {
    e.preventDefault();
    e.stopPropagation();
    if (curatedWallReactionPopover?.artworkId === artworkId) {
      setCuratedWallReactionPopover(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setCuratedWallReactionPopover({
      artworkId,
      anchorRect: {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
      },
      items: [],
      loading: true,
    });
    try {
      const response = await fetch(
        `${API_ENDPOINTS.getKudosList}?artworkId=${encodeURIComponent(artworkId)}`
      );
      const data = await response.json();
      const items = data.success && Array.isArray(data.reactions) ? data.reactions : [];
      setCuratedWallReactionPopover((prev) => {
        if (!prev || prev.artworkId !== artworkId) return prev;
        return { ...prev, items, loading: false };
      });
    } catch (err) {
      console.error('[ArtistWall] fetch kudos list', err);
      setCuratedWallReactionPopover((prev) => {
        if (!prev || prev.artworkId !== artworkId) return prev;
        return { ...prev, items: [], loading: false };
      });
    }
  };

  const handleUpload = async () => {
    // Prevent multiple clicks
    if (isUploading) {
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to upload artwork.");
      return;
    }

    // Track old artwork data for success message detection (accessible across all paths)
    let oldArtworkForMessage = null;

    // Standardized validation: Check for file first (unless editing existing artwork)
    if (!file && !editId) {
      alert("Please select an image to upload.");
      return;
    }

    // Set uploading state
    setUploadStatusText('Preparing your masterpiece...');
    setIsUploading(true);

    // Validate based on artwork type - standardized approach
    // When editing, allow using existing image (existingImageUrl) without requiring new file
    if (artworkType === 'book-co-learning') {
      if (!file && !editId && !existingImageUrl) {
        alert("Please select an image to upload.");
        setIsUploading(false);
        return;
      }
      // Only require activity code/country/activity number for new uploads, not when editing
      const isAroundTheWorld = tag === "Around the world with 100 artists";
      const isSimpleActs = tag === "Simple acts of art";
      if (!editId) {
        if (isAroundTheWorld && !selectedCountry) {
          alert("Please select a country for this artwork.");
          setIsUploading(false);
          return;
        } else if (isSimpleActs && !selectedActivityNumber) {
          alert("Please select an activity number for this artwork.");
          setIsUploading(false);
          return;
        } else if (!isAroundTheWorld && !isSimpleActs && !activityCode) {
          alert("Please enter the activity code for book artwork.");
          setIsUploading(false);
          return;
        }
      }
    } else if (artworkType === 'art-history') {
      if (!file && !editId && !existingImageUrl) {
        alert("Please select an image to upload.");
        setIsUploading(false);
        return;
      }
      // Only require activity selection for new uploads, not when editing
      if (!editId && !selectedArtHistoryActivity) {
        alert("Please select an Art History activity.");
        setIsUploading(false);
        return;
      }
    } else if (artworkType === 'ways-of-seeing') {
      if (!file && !editId && !existingImageUrl) {
        alert("Please select an image to upload.");
        setIsUploading(false);
        return;
      }
      // Only require original artwork and style for new uploads, not when editing
      if (!editId && (!selectedOriginalArtwork || !waysOfSeeingStyleId)) {
        alert("Please select an original artwork and the style you re-imagined it as.");
        setIsUploading(false);
        return;
      }
      // Title and description are optional for Ways of Seeing, will use defaults if not provided
      // Validate that we have the style information
      if (!waysOfSeeingStyleName && !editId) {
        console.warn('⚠️ Style name not set, will use fallback');
      }
    } else {
      // General artwork (My New Creation)
      if (!file && !editId && !existingImageUrl) {
        alert("Please select an image to upload.");
        setIsUploading(false);
        return;
      }
      if (!title || !description) {
        alert("Please fill in the title and description for your artwork.");
        setIsUploading(false);
        return;
      }
    }

    // Check consent settings if trying to make artwork public
    if (visibility === "public") {
      const canShare = await canShareArtworkPublicly(user.uid);
      const isUnder13User = await isUnder13(user.uid);
      
      if (!canShare) {
        if (isUnder13User) {
          alert('To share this artwork publicly, your parent needs to enable artwork sharing in Account Settings. An email will be sent to your parent for approval.');
          // TODO: Trigger parent consent email
          setIsUploading(false);
          return;
        } else {
          alert('To share this artwork publicly, you need to allow artwork sharing in Account Settings. Please go to Account Settings and enable "Allow my artwork to be shared publicly".');
          setIsUploading(false);
          return;
        }
      }
    }

    try {
      // Handle image upload - only if new file is provided or not editing
      // IMPORTANT: Declare imageUrl early so it's available for all artwork types
      let imageUrl = existingImageUrl;
      let thumbnailUrl = existingImageUrl;
      if (file) {
        try {
          setUploadStatusText('Optimizing image for faster upload...');
          const optimizedFile = await optimizeImageForUpload(file);
          const thumbnailFile = await createThumbnailForUpload(optimizedFile || file);
          setUploadStatusText('Uploading your artwork...');

          // Use deduplication utility to prevent duplicate storage
          // Always make files public in storage (visibility controls co-learning space sharing)
          const { uploadWithDeduplication } = await import('../utils/storageDeduplication');
          const storagePath = `artworks/${user.uid}/${uuidv4()}_${optimizedFile.name || 'artwork'}`;
          const uploadResult = await uploadWithDeduplication(optimizedFile, storagePath, true); // Always public in storage
          imageUrl = uploadResult.url;
          thumbnailUrl = imageUrl;
          if (thumbnailFile) {
            try {
              const thumbPath = `artworks/${user.uid}/thumbnails/${uuidv4()}_${thumbnailFile.name || 'thumb.jpg'}`;
              const thumbUpload = await uploadWithDeduplication(thumbnailFile, thumbPath, true);
              thumbnailUrl = thumbUpload?.url || imageUrl;
            } catch (thumbErr) {
              console.warn('[ArtistWall] Thumbnail upload failed, using full image:', thumbErr);
              thumbnailUrl = imageUrl;
            }
          }
          
          if (uploadResult.isNewUpload) {
            console.log('📤 New artwork uploaded to Storage');
          } else {
            console.log('♻️ Reused existing artwork from Storage (deduplication)');
          }
        } catch (storageError) {
          console.error('Firebase Storage upload error:', storageError);
          // If it's a permission error, provide a more helpful message
          if (storageError.code === 'storage/unauthorized' || storageError.code === 'permission-denied') {
            throw new Error('Permission denied: Unable to upload image. Please check your account permissions or contact support.');
          }
          throw storageError;
        }
      } else if (editId && !existingImageUrl) {
        // If editing but no existing image URL, we need to get it from the document
        try {
          const artworkCollection = editSource === 'sharedArtworks' ? 'sharedArtworks' : 'artworks';
          const docRef = doc(db, artworkCollection, editId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const docData = docSnap.data();
            imageUrl = docData.imageUrl || docData.artworkUrl || existingImageUrl;
            thumbnailUrl = docData.thumbnailUrl || imageUrl || existingImageUrl;
            console.log('✅ Using existing image URL from document:', imageUrl ? 'found' : 'not found');
          }
        } catch (fetchError) {
          console.warn('Could not fetch existing image URL:', fetchError);
        }
      }

      setUploadStatusText('Saving artwork details...');

      // Handle Art History Activity upload - use original art-history endpoint
      if (artworkType === 'art-history') {
        // If editing, use the update path instead of creating new documents
        if (editId) {
          // Use backend API for updates to avoid permission issues
          try {
            // IMPORTANT: Fetch old artwork data BEFORE updating (for sync)
            let oldArtwork = null;
            let oldVisibility = null;
            try {
              const artworkCollection = editSource === 'sharedArtworks' ? 'sharedArtworks' : 'artworks';
              const docRef = doc(db, artworkCollection, editId);
              const docSnap = await getDoc(docRef);
              
              if (docSnap.exists()) {
                const docData = docSnap.data();
                oldArtwork = { id: editId, ...docData };
                
                // For art history artworks, check the actual visibility in artHistorySharedArtworks collection
                if (artworkType === 'art-history' && docData.artworkId) {
                  try {
                    const artHistoryQuery = query(
                      collection(db, 'artHistorySharedArtworks'),
                      where('artworkId', '==', docData.artworkId),
                      where('uid', '==', user.uid)
                    );
                    const artHistorySnapshot = await getDocs(artHistoryQuery);
                    if (!artHistorySnapshot.empty) {
                      const artHistoryData = artHistorySnapshot.docs[0].data();
                      oldVisibility = artHistoryData.visibility || 'private';
                      console.log('📋 [SYNC] Found in artHistorySharedArtworks, using visibility from there:', oldVisibility);
                    } else {
                      // Not found in co-learning space, so it's private
                      oldVisibility = 'private';
                      console.log('📋 [SYNC] Not found in artHistorySharedArtworks, defaulting to private');
                    }
                  } catch (artHistoryError) {
                    console.warn('⚠️ Could not check artHistorySharedArtworks, using fallback:', artHistoryError);
                    // Fallback to sharedArtworks visibility
                    oldVisibility = editSource === 'sharedArtworks' 
                      ? (docData.shareWithArtmosphere ? 'public' : 'private')
                      : (docData.visibility || 'private');
                  }
                } else {
                  // For non-art-history artworks, use the standard logic
                  oldVisibility = editSource === 'sharedArtworks' 
                    ? (docData.shareWithArtmosphere ? 'public' : 'private')
                    : (docData.visibility || 'private');
                }
                
                console.log('📋 [SYNC] Fetched old artwork data:', {
                  id: editId,
                  oldVisibility,
                  hasImageUrl: !!(docData.imageUrl || docData.artworkUrl),
                  isArtHistory: !!(docData.activityType === 'art-history-appreciation' || docData.artworkId),
                  artworkId: docData.artworkId
                });
              }
            } catch (fetchError) {
              console.warn('⚠️ Could not fetch old artwork data for sync:', fetchError);
            }

            const updatePayload = {
              uid: user.uid,
              artworkId: editId,
              source: editSource || 'artworks',
              title,
              description,
              technique,
              inspiration,
              tag,
              visibility,
              imageUrl: imageUrl || existingImageUrl // Use existing image if no new file uploaded
            };
            
            console.log('🔄 [SYNC] Updating artwork with visibility:', visibility, 'old visibility:', oldVisibility);
            
            const response = await fetch(`${backendConfig.baseUrl}/api/update-artwork`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updatePayload),
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: 'Failed to update artwork' }));
              throw new Error(errorData.error || 'Failed to update artwork');
            }
            
            const result = await response.json();
            console.log('✅ Art History artwork updated successfully via API:', result);

            // Sync with co-learning space if visibility changed
            if (oldArtwork && visibility !== oldVisibility) {
              console.log('🔄 [SYNC] Visibility changed, syncing to co-learning space...', {
                oldVisibility,
                newVisibility: visibility,
                artworkId: editId
              });
              
              // Get artworkId from multiple possible locations
              const artworkId = oldArtwork.artworkId || 
                               oldArtwork.metadata?.artworkMeta?.id || 
                               oldArtwork.metadata?.artworkId ||
                               oldArtwork.artworkImageUrl?.split('/').pop() || // Fallback: extract from URL
                               null;
              
              console.log('🔍 [SYNC] Art history artwork data:', {
                hasArtworkId: !!artworkId,
                artworkId,
                oldArtworkKeys: Object.keys(oldArtwork),
                hasMetadata: !!oldArtwork.metadata,
                metadataKeys: oldArtwork.metadata ? Object.keys(oldArtwork.metadata) : []
              });
              
              const updatedArtwork = {
                ...oldArtwork,
                visibility: visibility,
                imageUrl: imageUrl || existingImageUrl || oldArtwork.imageUrl || oldArtwork.artworkUrl,
                title: title || oldArtwork.title || oldArtwork.artworkTitle,
                description: description || oldArtwork.description,
                activityType: 'art-history-appreciation',
                artworkId: artworkId, // Use the extracted artworkId
                source: 'art-history'
              };
              
              try {
                const { syncArtworkVisibility } = await import('../utils/artworkVisibilitySync');
                await syncArtworkVisibility(
                  updatedArtwork,
                  visibility,
                  oldVisibility,
                  editSource || 'artworks'
                );
                console.log('✅ [SYNC] Successfully synced artwork visibility to co-learning space');
              } catch (syncError) {
                console.error('❌ [SYNC] Error syncing art history artwork visibility:', syncError);
                // Don't throw - sync failure shouldn't block the update
              }
            } else {
              console.log('ℹ️ [SYNC] No sync needed:', {
                hasOldArtwork: !!oldArtwork,
                visibilityChanged: visibility !== oldVisibility,
                oldVisibility,
                newVisibility: visibility
              });
            }

            // Show success message
            const isShared = visibility === 'public' || visibility === true;
            setWasArtworkShared(isShared);
            setSuccessModalArtworkType(artworkType);
            setUploadSuccessMessage(
              isShared 
                ? 'Your Art History activity artwork has been updated and shared to the Co-Learning Space!'
                : 'Your Art History activity artwork has been updated!'
            );
            setShowUploadSuccess(true);
            
            resetForm();
            fetchArtworks();
            setShowUpload(false);
            setIsUploading(false);
            return;
          } catch (updateError) {
            console.error('Error updating art history artwork via API:', updateError);
            throw updateError;
          }
        }

        // NEW UPLOAD ONLY - Handle Art History Activity upload
        if (!selectedArtHistoryActivity) {
          throw new Error('Please select an Art History activity.');
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('uid', user.uid);
        formData.append('userName', user.displayName || user.email || 'Anonymous');
        formData.append('activityType', 'art-history-appreciation');
        formData.append('public', visibility === 'public' ? 'true' : 'false');
        
        const activityMeta = selectedArtHistoryActivity.metadata?.artworkMeta || {};
        formData.append('artworkId', activityMeta.id || selectedArtHistoryActivity.originalImageUrl || 'unknown');
        formData.append('artworkTitle', activityMeta.title || selectedArtHistoryActivity.metadata?.artworkTitle || 'Unknown Artwork');
        formData.append('artworkArtist', activityMeta.artist || activityMeta.artistName || 'Unknown Artist');
        formData.append('artworkMovement', activityMeta.movement || activityMeta.artMovement || '');
        formData.append('artworkImageUrl', selectedArtHistoryActivity.originalImageUrl || '');

        // Add timeout to prevent hanging
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 120000); // 120 second timeout (2 minutes)

        try {
          // Use the original art-history upload endpoint
          const apiPath = backendConfig.baseUrl.includes('/api') ? '/upload-art-history-activity' : '/api/upload-art-history-activity';
          const response = await fetch(`${backendConfig.baseUrl}${apiPath}`, {
            method: 'POST',
            body: formData,
            signal: abortController.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            const errorText = await response.text();
            let error;
            try {
              error = JSON.parse(errorText);
            } catch {
              error = { error: errorText || 'Failed to upload artwork' };
            }
            console.error('❌ Art History upload failed:', error);
            throw new Error(error.error || 'Failed to upload artwork');
          }

          const result = await response.json();
          console.log('✅ Art History upload successful:', result);
          
          // Show sophisticated success modal - conditional message based on visibility
          const isShared = visibility === 'public' || visibility === true;
          setWasArtworkShared(isShared);
          setSuccessModalArtworkType(artworkType); // Store artwork type before reset
          setUploadSuccessMessage(
            isShared 
              ? 'Your Art History activity artwork has been uploaded and shared to the Co-Learning Space and Artist Wall!'
              : 'Your Art History activity artwork has been uploaded to your Artist Wall!'
          );
          setShowUploadSuccess(true);
          
          resetForm();
          fetchArtworks();
          setShowUpload(false);
          setIsUploading(false); // Reset uploading state
          return;
        } catch (error) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            console.error('❌ Art History upload timeout after 2 minutes');
            alert('Upload timed out. Please try again with a smaller file or check your connection.');
          } else {
            console.error('❌ Art History upload error:', error);
            alert(`Error uploading artwork: ${error.message || 'Unknown error'}`);
          }
          throw error;
        }
      }

      // Handle Ways of Seeing upload - standardized validation (already checked above)
      if (artworkType === 'ways-of-seeing') {
        // If editing, use the update path instead of creating new documents
        if (editId) {
          // Use backend API for updates to avoid permission issues
          try {
            // IMPORTANT: Fetch old artwork data BEFORE updating (for sync)
            let oldArtwork = null;
            let oldVisibility = null;
            try {
              const artworkCollection = editSource === 'sharedArtworks' ? 'sharedArtworks' : 'artworks';
              const docRef = doc(db, artworkCollection, editId);
              const docSnap = await getDoc(docRef);
              
              if (docSnap.exists()) {
                const docData = docSnap.data();
                oldArtwork = { id: editId, ...docData };
                
                // For ways of seeing artworks, check the actual visibility in tutorialArtworks collection
                if (artworkType === 'ways-of-seeing' && (docData.styleId || docData.originalArtworkUrl)) {
                  try {
                    const styleId = docData.styleId || docData.metadata?.styleId;
                    const originalArtworkId = docData.originalArtworkId || docData.metadata?.originalArtworkId;
                    const originalArtworkUrl = docData.originalArtworkUrl || docData.metadata?.originalImageUrl;
                    
                    let tutorialQuery = null;
                    if (styleId && originalArtworkId) {
                      tutorialQuery = query(
                        collection(db, 'tutorialArtworks'),
                        where('styleId', '==', styleId),
                        where('originalArtworkId', '==', originalArtworkId),
                        where('userId', '==', user.uid)
                      );
                    } else if (originalArtworkUrl) {
                      tutorialQuery = query(
                        collection(db, 'tutorialArtworks'),
                        where('originalArtworkUrl', '==', originalArtworkUrl),
                        where('userId', '==', user.uid)
                      );
                    }
                    
                    if (tutorialQuery) {
                      const tutorialSnapshot = await getDocs(tutorialQuery);
                      if (!tutorialSnapshot.empty) {
                        const tutorialData = tutorialSnapshot.docs[0].data();
                        oldVisibility = tutorialData.visibility || 'private';
                        console.log('📋 [SYNC] Found in tutorialArtworks, using visibility from there:', oldVisibility);
                      } else {
                        // Not found in co-learning space, so it's private
                        oldVisibility = 'private';
                        console.log('📋 [SYNC] Not found in tutorialArtworks, defaulting to private');
                      }
                    } else {
                      // Fallback to sharedArtworks visibility
                      oldVisibility = editSource === 'sharedArtworks' 
                        ? (docData.shareWithArtmosphere ? 'public' : 'private')
                        : (docData.visibility || 'private');
                    }
                  } catch (tutorialError) {
                    console.warn('⚠️ Could not check tutorialArtworks, using fallback:', tutorialError);
                    // Fallback to sharedArtworks visibility
                    oldVisibility = editSource === 'sharedArtworks' 
                      ? (docData.shareWithArtmosphere ? 'public' : 'private')
                      : (docData.visibility || 'private');
                  }
                } else {
                  // For non-ways-of-seeing artworks, use the standard logic
                  oldVisibility = editSource === 'sharedArtworks' 
                    ? (docData.shareWithArtmosphere ? 'public' : 'private')
                    : (docData.visibility || 'private');
                }
                
                console.log('📋 [SYNC] Fetched old artwork data:', {
                  id: editId,
                  oldVisibility,
                  hasImageUrl: !!(docData.imageUrl || docData.artworkUrl),
                  isWaysOfSeeing: !!(docData.styleId || docData.originalArtworkUrl),
                  styleId: docData.styleId,
                  originalArtworkId: docData.originalArtworkId
                });
                // Store for success message detection
                oldArtworkForMessage = oldArtwork;
              }
            } catch (fetchError) {
              console.warn('⚠️ Could not fetch old artwork data for sync:', fetchError);
            }

            // Ensure visibility is correctly set (handle boolean/string conversion)
            const normalizedVisibility = visibility === true || visibility === 'public' ? 'public' : 'private';
            
            const updatePayload = {
              uid: user.uid,
              artworkId: editId,
              source: editSource || 'artworks',
              title,
              description,
              technique,
              inspiration,
              tag,
              visibility: normalizedVisibility,
              imageUrl: imageUrl || existingImageUrl // Use existing image if no new file uploaded
            };
            
            console.log('🔄 [SYNC] Updating Ways of Seeing artwork with visibility:', {
              rawVisibility: visibility,
              normalizedVisibility: normalizedVisibility,
              oldVisibility: oldVisibility,
              visibilityType: typeof visibility,
              visibilityChanged: normalizedVisibility !== oldVisibility
            });
            
            const response = await fetch(`${backendConfig.baseUrl}/api/update-artwork`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updatePayload),
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: 'Failed to update artwork' }));
              throw new Error(errorData.error || 'Failed to update artwork');
            }
            
            const result = await response.json();
            console.log('✅ Ways of Seeing artwork updated successfully via API:', result);

            // Sync with co-learning space - attempt sync even if oldArtwork is null
            // If oldVisibility is null, the sync function will check the co-learning space
            const shouldSync = normalizedVisibility !== oldVisibility || oldVisibility === null;
            
            if (shouldSync) {
              console.log('🔄 [SYNC] Syncing to co-learning space...', {
                oldVisibility,
                newVisibility: normalizedVisibility,
                artworkId: editId,
                hasOldArtwork: !!oldArtwork
              });
              
              // Construct artwork object for sync - use oldArtwork if available, otherwise use edit state
              let artworkForSync = null;
              
              if (oldArtwork) {
                // Fetch metadata from tutorialArtworks if not available in oldArtwork
                let waysOfSeeingSyncMetadata = null;
                if (!oldArtwork.styleId || !oldArtwork.originalArtworkId) {
                  try {
                    const imageUrlToCheck = imageUrl || existingImageUrl || oldArtwork.imageUrl || oldArtwork.artworkUrl;
                    const tutorialQuery = query(
                      collection(db, 'tutorialArtworks'),
                      where('imageUrl', '==', imageUrlToCheck),
                      where('userId', '==', user.uid)
                    );
                    const tutorialSnapshot = await getDocs(tutorialQuery);
                    if (!tutorialSnapshot.empty) {
                      waysOfSeeingSyncMetadata = tutorialSnapshot.docs[0].data();
                      console.log('📋 [SYNC] Fetched Ways of Seeing metadata from tutorialArtworks for sync:', {
                        styleId: waysOfSeeingSyncMetadata.styleId,
                        styleName: waysOfSeeingSyncMetadata.styleName,
                        originalArtworkId: waysOfSeeingSyncMetadata.originalArtworkId
                      });
                    }
                  } catch (metadataError) {
                    console.warn('⚠️ [SYNC] Could not fetch Ways of Seeing metadata for sync:', metadataError);
                  }
                }
                
                artworkForSync = {
                  ...oldArtwork,
                  visibility: normalizedVisibility,
                  imageUrl: imageUrl || existingImageUrl || oldArtwork.imageUrl || oldArtwork.artworkUrl,
                  title: title || oldArtwork.title || oldArtwork.artworkTitle,
                  description: description || oldArtwork.description,
                  styleId: oldArtwork.styleId || waysOfSeeingSyncMetadata?.styleId || waysOfSeeingStyleId,
                  styleName: oldArtwork.styleName || waysOfSeeingSyncMetadata?.styleName || waysOfSeeingStyleName,
                  originalArtworkId: oldArtwork.originalArtworkId || waysOfSeeingSyncMetadata?.originalArtworkId || oldArtwork.metadata?.originalArtworkId,
                  originalArtworkUrl: oldArtwork.originalArtworkUrl || waysOfSeeingSyncMetadata?.originalArtworkUrl || oldArtwork.metadata?.originalImageUrl,
                  source: 'ways-of-seeing',
                  metadata: {
                    ...oldArtwork.metadata,
                    styleId: oldArtwork.styleId || waysOfSeeingSyncMetadata?.styleId || waysOfSeeingStyleId,
                    styleName: oldArtwork.styleName || waysOfSeeingSyncMetadata?.styleName || waysOfSeeingStyleName,
                    originalArtworkId: oldArtwork.originalArtworkId || waysOfSeeingSyncMetadata?.originalArtworkId || oldArtwork.metadata?.originalArtworkId,
                    originalImageUrl: oldArtwork.originalArtworkUrl || waysOfSeeingSyncMetadata?.originalArtworkUrl || oldArtwork.metadata?.originalImageUrl,
                    feature: 'ways-of-seeing-journey'
                  }
                };
              } else {
                // oldArtwork is null - construct from edit state
                // Try to fetch from tutorialArtworks to get the full metadata
                try {
                  const imageUrlToCheck = imageUrl || existingImageUrl;
                  if (imageUrlToCheck) {
                    const tutorialQuery = query(
                      collection(db, 'tutorialArtworks'),
                      where('imageUrl', '==', imageUrlToCheck),
                      where('userId', '==', user.uid)
                    );
                    const tutorialSnapshot = await getDocs(tutorialQuery);
                    if (!tutorialSnapshot.empty) {
                      const tutorialData = tutorialSnapshot.docs[0].data();
                      artworkForSync = {
                        id: editId,
                        imageUrl: imageUrlToCheck,
                        title: title || tutorialData.title || 'My Artwork',
                        description: description || tutorialData.description || '',
                        styleId: tutorialData.styleId || waysOfSeeingStyleId,
                        styleName: tutorialData.styleName || waysOfSeeingStyleName,
                        originalArtworkId: tutorialData.originalArtworkId || waysOfSeeingOriginalArtwork?.id,
                        originalArtworkUrl: tutorialData.originalArtworkUrl || waysOfSeeingOriginalArtwork?.imageUrl,
                        source: 'ways-of-seeing',
                        visibility: normalizedVisibility,
                        metadata: {
                          styleId: tutorialData.styleId || waysOfSeeingStyleId,
                          styleName: tutorialData.styleName || waysOfSeeingStyleName,
                          originalArtworkId: tutorialData.originalArtworkId || waysOfSeeingOriginalArtwork?.id,
                          originalImageUrl: tutorialData.originalArtworkUrl || waysOfSeeingOriginalArtwork?.imageUrl,
                          feature: 'ways-of-seeing-journey'
                        }
                      };
                      console.log('📋 [SYNC] Constructed artwork from tutorialArtworks (oldArtwork was null):', {
                        styleId: artworkForSync.styleId,
                        styleName: artworkForSync.styleName,
                        originalArtworkId: artworkForSync.originalArtworkId
                      });
                    }
                  }
                } catch (fetchError) {
                  console.warn('⚠️ [SYNC] Could not fetch from tutorialArtworks to construct artwork:', fetchError);
                }
                
                // If still null, try to construct from edit state
                if (!artworkForSync && (waysOfSeeingStyleId || waysOfSeeingOriginalArtwork)) {
                  artworkForSync = {
                    id: editId,
                    imageUrl: imageUrl || existingImageUrl,
                    title: title || 'My Artwork',
                    description: description || '',
                    styleId: waysOfSeeingStyleId,
                    styleName: waysOfSeeingStyleName,
                    originalArtworkId: waysOfSeeingOriginalArtwork?.id,
                    originalArtworkUrl: waysOfSeeingOriginalArtwork?.imageUrl,
                    source: 'ways-of-seeing',
                    visibility: normalizedVisibility,
                    metadata: {
                      styleId: waysOfSeeingStyleId,
                      styleName: waysOfSeeingStyleName,
                      originalArtworkId: waysOfSeeingOriginalArtwork?.id,
                      originalImageUrl: waysOfSeeingOriginalArtwork?.imageUrl,
                      feature: 'ways-of-seeing-journey'
                    }
                  };
                  console.log('📋 [SYNC] Constructed artwork from edit state (fallback):', {
                    styleId: artworkForSync.styleId,
                    styleName: artworkForSync.styleName,
                    originalArtworkId: artworkForSync.originalArtworkId
                  });
                }
              }
              
              if (artworkForSync) {
                try {
                  const { syncArtworkVisibility } = await import('../utils/artworkVisibilitySync');
                  await syncArtworkVisibility(
                    artworkForSync,
                    normalizedVisibility,
                    oldVisibility, // May be null - sync function will handle it
                    editSource || 'artworks'
                  );
                  console.log('✅ [SYNC] Successfully synced artwork visibility to co-learning space');
                } catch (syncError) {
                  console.error('❌ [SYNC] Error syncing ways of seeing artwork visibility:', syncError);
                  // Don't throw - sync failure shouldn't block the update
                }
              } else {
                console.warn('⚠️ [SYNC] Could not construct artwork object for sync - missing required fields');
              }
            } else {
              console.log('ℹ️ [SYNC] No sync needed:', {
                hasOldArtwork: !!oldArtwork,
                visibilityChanged: normalizedVisibility !== oldVisibility,
                oldVisibility,
                newVisibility: normalizedVisibility
              });
            }

            // Show success message
            const isShared = normalizedVisibility === 'public';
            setWasArtworkShared(isShared);
            setSuccessModalArtworkType(artworkType);
            setUploadSuccessMessage(
              isShared 
                ? 'Your Ways of Seeing artwork has been updated and shared to the Co-Learning Space!'
                : 'Your Ways of Seeing artwork has been updated!'
            );
            setShowUploadSuccess(true);
            
            resetForm();
            fetchArtworks();
            setShowUpload(false);
            setIsUploading(false);
            return;
          } catch (updateError) {
            console.error('Error updating ways of seeing artwork via API:', updateError);
            throw updateError;
          }
        }

        // NEW UPLOAD ONLY - Handle Ways of Seeing upload
        // Use the selected Ways of Seeing activity's information
        // If selectedWaysOfSeeingActivity is not set, try to find it from availableStylesForArtwork
        let activity = selectedWaysOfSeeingActivity;
        if (!activity && availableStylesForArtwork.length > 0) {
          const matchingStyle = availableStylesForArtwork.find(s => s.styleId === waysOfSeeingStyleId);
          if (matchingStyle) {
            activity = matchingStyle.activity;
          }
        }

        // Fallback: use waysOfSeeingStyleName if activity is not available
        const styleName = activity?.metadata?.styleName || activity?.styleInfo?.name || waysOfSeeingStyleName || 'Unknown Style';
        const styleId = activity?.styleInfo?.id || activity?.metadata?.styleId || waysOfSeeingStyleId || 'default';
        const originalImageUrl = activity?.originalImageUrl || selectedOriginalArtwork?.imageUrl || '';
        const originalArtworkId = activity?.id || selectedOriginalArtwork?.id || 'unknown';

        console.log('📤 Uploading Ways of Seeing artwork:', {
          styleName,
          styleId,
          originalImageUrl: originalImageUrl.substring(0, 100),
          title: title || `Ways of Seeing: ${styleName}`,
          technique,
          inspiration
        });

        const formData = new FormData();
        formData.append('artwork', file);
        formData.append('originalArtworkId', originalArtworkId);
        formData.append('originalArtworkUrl', originalImageUrl);
        formData.append('styleId', styleId);
        formData.append('styleName', styleName);
        formData.append('userId', user.uid);
        formData.append('title', title || `Ways of Seeing: ${styleName}`);
        formData.append('description', description || `Artwork created using ${styleName} style transfer`);
        formData.append('visibility', visibility === 'public' ? 'public' : 'private'); // Pass visibility to backend
        // Add technique and inspiration if provided
        if (technique) {
          formData.append('technique', technique);
        }
        if (inspiration) {
          formData.append('inspiration', inspiration);
        }

        // Add timeout to prevent hanging
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 120000); // 120 second timeout (2 minutes)

        try {
          const response = await fetch(`${backendConfig.baseUrl}/api/tutorial-tag/upload`, {
            method: 'POST',
            body: formData,
            signal: abortController.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            const errorText = await response.text();
            let error;
            try {
              error = JSON.parse(errorText);
            } catch {
              error = { error: errorText || 'Failed to upload artwork' };
            }
            console.error('❌ Upload failed:', error);
            throw new Error(error.error || 'Failed to upload artwork');
          }

          const result = await response.json();
          console.log('✅ Upload successful:', result);
          
          // Show sophisticated success modal - conditional message based on visibility
          const isShared = visibility === 'public' || visibility === true;
          setWasArtworkShared(isShared);
          setSuccessModalArtworkType(artworkType); // Store artwork type before reset
          setUploadSuccessMessage(
            isShared 
              ? 'Your Ways of Seeing artwork has been uploaded and shared to the Co-Learning Space and Artist Wall!'
              : 'Your Ways of Seeing artwork has been uploaded to your Artist Wall!'
          );
          setShowUploadSuccess(true);
          
          resetForm();
          fetchArtworks();
          setShowUpload(false);
          setIsUploading(false); // Reset uploading state
          return;
        } catch (error) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            console.error('❌ Upload timeout after 2 minutes');
            alert('Upload timed out. Please try again with a smaller file or check your connection.');
          } else {
            console.error('❌ Upload error:', error);
            alert(`Error uploading artwork: ${error.message || 'Unknown error'}`);
          }
          throw error;
        }
      }

      // Handle Book Co-Learning upload
      const isArtmosphereBook = BOOK_CO_LEARNING_UPLOAD_TAGS.includes(tag);
      
      if (artworkType === 'book-co-learning' && isArtmosphereBook) {
        // If editing, use the update path instead of creating new documents
        if (editId) {
          // IMPORTANT: Fetch old artwork data BEFORE updating (for sync)
          let oldArtwork = null;
          let oldVisibility = null;
          try {
            const artworkCollection = editSource === 'sharedArtworks' ? 'sharedArtworks' : 'artworks';
            const docRef = doc(db, artworkCollection, editId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const docData = docSnap.data();
              oldArtwork = { id: editId, ...docData };
              oldVisibility = editSource === 'sharedArtworks' 
                ? (docData.shareWithArtmosphere ? 'public' : 'private')
                : (docData.visibility || 'private');
              console.log('📋 [SYNC] Fetched old artwork data:', {
                id: editId,
                oldVisibility,
                hasImageUrl: !!(docData.imageUrl || docData.artworkUrl),
                isBookArtwork: !!(docData.isBookArtwork || docData.bookArtworkId)
              });
            }
          } catch (fetchError) {
            console.warn('⚠️ Could not fetch old artwork data for sync:', fetchError);
          }

          // Use backend API for updates to avoid permission issues
          try {
            const updatePayload = {
              uid: user.uid,
              artworkId: editId,
              source: editSource || 'artworks',
              title,
              description,
              technique,
              inspiration,
              tag,
              visibility,
              imageUrl: imageUrl || existingImageUrl // Use existing image if no new file uploaded
            };
            
            console.log('🔄 [SYNC] Updating artwork with visibility:', visibility, 'old visibility:', oldVisibility);
            
            const response = await fetch(`${backendConfig.baseUrl}/api/update-artwork`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updatePayload),
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: 'Failed to update artwork' }));
              throw new Error(errorData.error || 'Failed to update artwork');
            }
            
            const result = await response.json();
            console.log('✅ Book artwork updated successfully via API:', result);

            // Sync with co-learning space if visibility changed
            if (oldArtwork && visibility !== oldVisibility) {
              console.log('🔄 [SYNC] Visibility changed, syncing to co-learning space...', {
                oldVisibility,
                newVisibility: visibility,
                artworkId: editId
              });
              
              const updatedArtwork = {
                ...oldArtwork,
                visibility: visibility,
                imageUrl: imageUrl || existingImageUrl || oldArtwork.imageUrl || oldArtwork.artworkUrl,
                title: title || oldArtwork.title || oldArtwork.artworkTitle,
                description: description || oldArtwork.description,
                isBookArtwork: true,
                bookArtworkId: oldArtwork.bookArtworkId,
                tag: tag || oldArtwork.tag,
                source: 'book-co-learning',
                activityCode: oldArtwork.activityCode || (tag === "Around the world with 100 artists" ? selectedCountry : (tag === "Simple acts of art" ? selectedActivityNumber : activityCode))
              };
              
              try {
                const { syncArtworkVisibility } = await import('../utils/artworkVisibilitySync');
                await syncArtworkVisibility(
                  updatedArtwork,
                  visibility,
                  oldVisibility,
                  editSource || 'artworks'
                );
                console.log('✅ [SYNC] Successfully synced artwork visibility to co-learning space');
              } catch (syncError) {
                console.error('❌ [SYNC] Error syncing book artwork visibility:', syncError);
                // Don't throw - sync failure shouldn't block the update
              }
            } else {
              console.log('ℹ️ [SYNC] No sync needed:', {
                hasOldArtwork: !!oldArtwork,
                visibilityChanged: visibility !== oldVisibility,
                oldVisibility,
                newVisibility: visibility
              });
            }

            // Show success message
            const isShared = visibility === 'public' || visibility === true;
            setWasArtworkShared(isShared);
            setSuccessModalArtworkType(artworkType);
            setUploadSuccessMessage(
              isShared 
                ? 'Your Book Co-Learning artwork has been updated and shared to the Co-Learning Space!'
                : 'Your Book Co-Learning artwork has been updated!'
            );
            setShowUploadSuccess(true);
            
            resetForm();
            fetchArtworks();
            setShowUpload(false);
            setIsUploading(false);
            return;
          } catch (updateError) {
            console.error('Error updating book artwork via API:', updateError);
            throw updateError;
          }
        }

        // Handle as book artwork - store in bookArtworks collection (NEW UPLOAD ONLY)
        // For "Around the world with 100 artists", use selectedCountry as activityCode
        // For "Simple acts of art", use selectedActivityNumber as activityCode
        const isAroundTheWorld = tag === "Around the world with 100 artists";
        const isSimpleActs = tag === "Simple acts of art";
        const finalActivityCode = isAroundTheWorld ? selectedCountry : (isSimpleActs ? selectedActivityNumber : activityCode);
        const activityLabel = isAroundTheWorld ? selectedCountry : (isSimpleActs ? selectedActivityNumber : `Activity ${activityCode}`);
        
        const contributorName = user.displayName || user.email || 'Anonymous';
        const bookPayload = {
          title: title || `Artwork from ${tag} - ${activityLabel}`,
          technique: technique || '',
          inspiration: inspiration || '',
          description: description || `Artwork created for ${tag} ${activityLabel}`,
          book: tag,
          activityCode: finalActivityCode,
          uid: user.uid,
          userName: contributorName,
          studentName: contributorName,
          imageUrl,
          thumbnailUrl,
          visibility,
          likesCount: 0,
          applauseCount: 0,
          createdAt: serverTimestamp(),
        };

        const bookArtworkRef = await addDoc(collection(db, "bookArtworks"), bookPayload);

        // Always save to Artist Wall (private wall) - Book artworks are part of Artmosphere learning
        // They are not for general exhibits, only for Co-Learning Space and Private Wall
        try {
          const sharedArtworkDoc = {
            uid: user.uid,
            studentName: user.displayName || user.email || 'Anonymous',
            artworkTitle: `Book Activity: ${title || `${tag} - ${activityLabel}`}`,
            artworkUrl: imageUrl,
            thumbnailUrl,
            artworkId: bookArtworkRef.id,
            status: 'active',
            shareWithArtmosphere: visibility === 'public' || visibility === true, // Only share to Artmosphere if public
            shareWithOrganization: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            type: 'book-activity',
            bookActivityMeta: {
              book: tag,
              activityCode: finalActivityCode,
              technique: technique || '',
              inspiration: inspiration || ''
            }
          };
          await addDoc(collection(db, "sharedArtworks"), sharedArtworkDoc);
          console.log('✅ Book artwork saved to Artist Wall (private wall)');
        } catch (error) {
          console.error('⚠️ Error saving to Artist Wall (non-fatal):', error);
          // Don't fail the request if Artist Wall save fails
        }

        // Create book activity participant record
        const participantId = `${tag}_${finalActivityCode}_${user.uid}`;
        console.log("Creating participant record with ID:", participantId);
        console.log("User UID:", user.uid);
        
        try {
          await addDoc(collection(db, "bookActivityParticipants"), {
            book: tag,
            activityCode: finalActivityCode,
            uid: user.uid,
            completed: true,
            createdAt: serverTimestamp()
          });
          console.log("Participant record created successfully");
        } catch (error) {
          console.error("Error creating participant record:", error);
          throw error;
        }

        // Also store in regular artworks collection for user's wall
        const regularPayload = {
          uid: user.uid,
          title: title || `Artwork from ${tag} - ${activityLabel}`,
          technique: technique || '',
          inspiration: inspiration || '',
          description: description || `Artwork created for ${tag} ${activityLabel}`,
          tag,
          visibility,
          imageUrl,
          thumbnailUrl,
          likes: [],
          comments: [],
          isBookArtwork: true,
          bookArtworkId: bookArtworkRef.id,
          createdAt: serverTimestamp(),
          environment: ENV,
          projectId: firebaseConfig?.projectId || null,
        };

        console.log("Creating regular artwork with payload:", {
          ...regularPayload,
          ENV,
          firebaseProjectId: firebaseConfig?.projectId,
          storageBucket: firebaseConfig?.storageBucket,
        });
        try {
          await addDoc(collection(db, "artworks"), regularPayload);
          console.log("Regular artwork created successfully");
        } catch (error) {
          console.error("Error creating regular artwork:", error);
          throw error;
        }

        // Show success modal for Book Co-Learning - conditional message based on visibility
        const isShared = visibility === 'public' || visibility === true;
        setWasArtworkShared(isShared);
        setSuccessModalArtworkType(artworkType); // Store artwork type before reset
        setUploadSuccessMessage(
          isShared 
            ? 'Your Book Co-Learning artwork has been uploaded and shared to the Co-Learning Space and Artist Wall!'
            : 'Your Book Co-Learning artwork has been uploaded to your Artist Wall!'
        );
        setShowUploadSuccess(true);
        
        // Clear cache to force refresh (user's own artworks + book co-learning participations)
        const artworksCacheKey = createCacheKey('artworks', { uid: user.uid }, user.uid);
        clearCache(artworksCacheKey);
        clearCache(createCacheKey('bookActivityParticipants', { uid: user.uid }, user.uid));
        if (isShared) invalidateSalonCache(user.uid);
        console.log('✅ [ARTIST WALL] Cache cleared after upload');
        
        resetForm();
        fetchArtworks();
        setShowUpload(false);
        setShowUploadNudge(false); // Hide nudge after first upload
        setIsUploading(false); // Reset uploading state
        return;

      } else {
        // Handle as regular artwork (My New Creation / General artwork)
        // Mark source so General Exhibit can include these even if tag matches Artmosphere tags
        const normalizedVisibility = visibility === 'public' ? 'public' : 'private';
        const payload = {
          uid: user.uid,
          title,
          technique,
          inspiration,
          description,
          tag,
          visibility: normalizedVisibility,
          imageUrl,
          thumbnailUrl,
          source: 'general',
          category: 'general',
          likes: [],
          comments: [],
          createdAt: serverTimestamp(),
        };

        if (editId) {
          // Use backend API for updates to avoid permission issues
          try {
            // Get old artwork data to check old visibility
            let oldArtwork = null;
            let oldVisibility = null;
            try {
              const artworkCollection = editSource === 'sharedArtworks' ? 'sharedArtworks' : 'artworks';
              const docRef = doc(db, artworkCollection, editId);
              const docSnap = await getDoc(docRef);
              
              if (docSnap.exists()) {
                const docData = docSnap.data();
                oldArtwork = { id: editId, ...docData };
                oldArtworkForMessage = oldArtwork; // Store for use in success message
                oldVisibility = editSource === 'sharedArtworks' 
                  ? (docData.shareWithArtmosphere ? 'public' : 'private')
                  : (docData.visibility || 'private');
                
                // If from sharedArtworks, also try to get metadata from artworks collection
                if (editSource === 'sharedArtworks' && docData.artworkId) {
                  try {
                    const artworksDocRef = doc(db, 'artworks', docData.artworkId);
                    const artworksDocSnap = await getDoc(artworksDocRef);
                    if (artworksDocSnap.exists()) {
                      const artworksData = artworksDocSnap.data();
                      // Merge metadata from artworks collection
                      oldArtwork = {
                        ...oldArtwork,
                        ...artworksData,
                        // Preserve sharedArtworks specific fields
                        artworkUrl: oldArtwork.artworkUrl || oldArtwork.imageUrl,
                        artworkTitle: oldArtwork.artworkTitle || oldArtwork.title
                      };
                      console.log('✅ [EDIT] Merged metadata from artworks collection');
                    }
                  } catch (mergeError) {
                    console.warn('Could not merge metadata from artworks:', mergeError);
                  }
                }
                
                console.log('📋 [EDIT] Old artwork data:', {
                  id: editId,
                  source: editSource,
                  oldVisibility,
                  isBookArtwork: oldArtwork.isBookArtwork,
                  bookArtworkId: oldArtwork.bookArtworkId,
                  tag: oldArtwork.tag,
                  type: oldArtwork.type,
                  bookActivityMeta: oldArtwork.bookActivityMeta
                });
              }
            } catch (fetchError) {
              console.warn('Could not fetch old artwork data:', fetchError);
            }

            const updatePayload = {
              uid: user.uid,
              artworkId: editId,
              source: 'general',
              title,
              description,
              technique,
              inspiration,
              tag,
              visibility: normalizedVisibility,
              category: 'general',
              imageUrl
            };
            
            const response = await fetch(`${backendConfig.baseUrl}/api/update-artwork`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updatePayload),
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: 'Failed to update artwork' }));
              throw new Error(errorData.error || 'Failed to update artwork');
            }
            
            const result = await response.json();
            console.log('✅ Artwork updated successfully via API:', result);

            // Sync with co-learning space only for activity artworks (not General Exhibit)
            if (oldArtwork && visibility !== oldVisibility && !isGeneralExhibitArtwork(oldArtwork)) {
              console.log('🔄 [SYNC] Visibility changed, preparing to sync:', {
                artworkId: editId,
                oldVisibility,
                newVisibility: visibility,
                oldArtworkKeys: Object.keys(oldArtwork),
                isBookArtwork: oldArtwork.isBookArtwork,
                bookArtworkId: oldArtwork.bookArtworkId,
                source: oldArtwork.source,
                tag: oldArtwork.tag
              });
              
              const updatedArtwork = {
                ...oldArtwork,
                visibility: normalizedVisibility,
                imageUrl: imageUrl || oldArtwork.imageUrl || oldArtwork.artworkUrl,
                title: title || oldArtwork.title || oldArtwork.artworkTitle,
                description: description || oldArtwork.description,
                activityId: oldArtwork.activityId,
                styleId: oldArtwork.styleId,
                styleName: oldArtwork.styleName,
                artworkId: oldArtwork.artworkId,
                originalArtworkUrl: oldArtwork.originalArtworkUrl || oldArtwork.originalImageUrl,
                originalArtworkId: oldArtwork.originalArtworkId,
                isBookArtwork: oldArtwork.isBookArtwork,
                bookArtworkId: oldArtwork.bookArtworkId,
                source: oldArtwork.source || (oldArtwork.isBookArtwork ? 'book-co-learning' : 'general'),
                category: oldArtwork.category || (oldArtwork.isBookArtwork ? '' : 'general'),
                tag: tag || oldArtwork.tag,
                metadata: oldArtwork.metadata || {},
                type: oldArtwork.type,
                activityType: oldArtwork.activityType
              };
              
              console.log('🔄 [SYNC] Calling syncArtworkVisibility with:', {
                artworkId: editId,
                oldVisibility,
                newVisibility: normalizedVisibility,
                hasIsBookArtwork: !!updatedArtwork.isBookArtwork,
                hasBookArtworkId: !!updatedArtwork.bookArtworkId,
                hasStyleId: !!updatedArtwork.styleId,
                hasArtworkId: !!updatedArtwork.artworkId,
                source: updatedArtwork.source,
                collectionName: editSource || 'artworks'
              });
              
              try {
                await syncArtworkVisibility(
                  updatedArtwork,
                  normalizedVisibility,
                  oldVisibility,
                  editSource || 'artworks'
                );
                console.log('✅ [SYNC] Sync completed successfully');
              } catch (syncError) {
                console.error('❌ [SYNC] Error syncing artwork visibility:', syncError);
              }
            } else {
              console.log('ℹ️ [SYNC] Skipping sync:', {
                hasOldArtwork: !!oldArtwork,
                visibilityChanged: visibility !== oldVisibility,
                isGeneral: isGeneralExhibitArtwork(oldArtwork),
                oldVisibility,
                newVisibility: normalizedVisibility
              });
            }
          } catch (updateError) {
            console.error('Error updating artwork via API:', updateError);
            throw updateError;
          }
        } else {
          await addDoc(collection(db, "artworks"), payload);
        }
      }

      const isShared = visibility === 'public' || visibility === true;
      setWasArtworkShared(isShared);
      const modalType = resolveSuccessModalArtworkType(artworkType, oldArtworkForMessage);
      setSuccessModalArtworkType(modalType);

      if (modalType === 'ways-of-seeing') {
        setUploadSuccessMessage(
          isShared
            ? 'Your Ways of Seeing artwork has been updated and shared to the Co-Learning Space!'
            : 'Your Ways of Seeing artwork has been updated!'
        );
      } else if (modalType === 'art-history') {
        setUploadSuccessMessage(
          isShared
            ? 'Your Art History artwork has been updated and shared to the Co-Learning Space!'
            : 'Your Art History artwork has been updated!'
        );
      } else if (modalType === 'book-co-learning') {
        setUploadSuccessMessage(
          isShared
            ? 'Your Book Co-Learning artwork has been updated and shared to the Co-Learning Space!'
            : 'Your Book Co-Learning artwork has been updated!'
        );
      } else {
        setUploadSuccessMessage(
          isShared
            ? editId
              ? 'Your artwork has been updated and shared to the General Exhibit and your Artist Wall!'
              : 'Your artwork has been uploaded and shared to the General Exhibit and your Artist Wall!'
            : editId
              ? 'Your artwork has been updated on your Artist Wall!'
              : 'Your artwork has been uploaded to your Artist Wall!'
        );
      }
      setShowUploadSuccess(true); // Keep user on Artist Wall to see the popup
      
      if (isShared) invalidateSalonCache(user.uid);
      resetForm();
      fetchArtworks();
      setShowUpload(false);
      setShowUploadNudge(false); // Hide nudge after first upload
      setIsUploading(false); // Reset uploading state
      
      // If a redirect was requested (e.g., from Ways of Seeing), send the new artwork forward
      try {
        const redirectTo = (location && location.state && location.state.redirectTo) || null;
        if (redirectTo) {
          navigate(redirectTo, {
            state: { artwork: { imageUrl, title, technique, inspiration }, fromPage: 'artist-wall-upload' },
          });
        }
      } catch (_) {}
    } catch (error) {
      console.error("Upload failed:", error);
      // Standardized error handling - provide more specific error messages
      if (error.message) {
        alert(`Upload failed: ${error.message}`);
      } else {
        alert("Upload failed. Check permissions or storage path.");
      }
    } finally {
      // Always reset uploading state
      setIsUploading(false);
      setUploadStatusText('Preparing your masterpiece...');
    }
  };

  const handleDelete = async (art) => {
    const confirmDelete = await showPremiumConfirm({
      title: 'Delete artwork?',
      bullets: [
        'This artwork from your gallery',
        'All related activities from your Activity Dashboard',
        'All related submissions from Co-Learning Spaces',
      ],
      confirmLabel: 'Delete permanently',
      cancelLabel: 'Cancel',
      variant: 'danger',
      zIndexClass: 'z-[200200]',
    });
    if (!confirmDelete) return;
    
    const user = auth.currentUser;
    if (!user) {
      alert('Please log in to delete artwork');
      return;
    }
    
    try {
      // Determine which collection to delete from based on source
      const source = art.source || 'artworks'; // Default to artworks for backward compatibility
      
      if (source === 'sharedArtworks') {
        // Delete from sharedArtworks collection via backend API (to handle permissions)
        try {
          const response = await fetch(`${backendConfig.baseUrl}/api/delete-shared-artwork`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uid: user.uid,
              artworkId: art.id
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to delete artwork' }));
            throw new Error(errorData.error || 'Failed to delete artwork');
          }
          
          const result = await response.json();
          console.log('✅ Deleted from sharedArtworks collection via API:', result);
        } catch (apiError) {
          // Fallback to direct Firestore delete if API fails
          console.warn('⚠️ API delete failed, trying direct Firestore delete:', apiError);
          try {
            await deleteDoc(doc(db, "sharedArtworks", art.id));
            console.log('✅ Deleted from sharedArtworks collection (direct)');
          } catch (firestoreError) {
            throw new Error(`Failed to delete artwork: ${firestoreError.message}`);
          }
        }
        
        // Also update original source if applicable (set visibility to private instead of deleting)
        if (art.type === 'book-activity' && art.bookActivityMeta?.book && art.bookActivityMeta?.activityCode) {
          // Try to find and update bookArtworks if artworkId matches
          try {
            const bookArtworksQuery = query(
              collection(db, "bookArtworks"),
              where("uid", "==", user.uid),
              where("imageUrl", "==", art.imageUrl || art.artworkUrl)
            );
            const bookArtworksSnapshot = await getDocs(bookArtworksQuery);
            if (!bookArtworksSnapshot.empty) {
              // Update visibility to private instead of deleting (preserve in co-learning space)
              const bookArtworkDoc = bookArtworksSnapshot.docs[0];
              await updateDoc(bookArtworkDoc.ref, { visibility: 'private' });
              console.log('✅ Updated bookArtwork visibility to private');
            }
          } catch (error) {
            console.warn('⚠️ Could not update bookArtwork:', error);
          }
        } else if (art.type === 'art-history-appreciation') {
          // Try to find and update artHistorySharedArtworks if artworkId matches
          try {
            const artHistoryQuery = query(
              collection(db, "artHistorySharedArtworks"),
              where("uid", "==", user.uid),
              where("imageUrl", "==", art.imageUrl || art.artworkUrl)
            );
            const artHistorySnapshot = await getDocs(artHistoryQuery);
            if (!artHistorySnapshot.empty) {
              // Update visibility to private instead of deleting (preserve in co-learning space)
              const artHistoryDoc = artHistorySnapshot.docs[0];
              await updateDoc(artHistoryDoc.ref, { visibility: 'private' });
              console.log('✅ Updated artHistorySharedArtwork visibility to private');
            }
          } catch (error) {
            console.warn('⚠️ Could not update artHistorySharedArtwork:', error);
          }
        }
      } else {
        // Delete from artworks collection (regular artwork)
    await deleteDoc(doc(db, "artworks", art.id));
        console.log('✅ Deleted from artworks collection');
      }
      
      // Delete all related activities from Activity Dashboard and Co-Learning Spaces
      const imageUrlToDelete = art.imageUrl || art.artworkUrl;
      const artworkUrl = art.imageUrl || art.artworkUrl || '';
      
      console.log('🗑️ Deleting related activities for artwork:', art.id);
      
      try {
        // Delete from userActivities (Activity Dashboard)
        const activitiesQuery = query(
          collection(db, 'userActivities'),
          where('userId', '==', user.uid),
          where('originalImageUrl', '==', artworkUrl)
        );
        const activitiesSnapshot = await getDocs(activitiesQuery);
        const activitiesToDelete = [];
        activitiesSnapshot.forEach((doc) => {
          activitiesToDelete.push(deleteDoc(doc.ref));
        });
        await Promise.all(activitiesToDelete);
        if (activitiesToDelete.length > 0) {
          console.log(`✅ Deleted ${activitiesToDelete.length} activities from Activity Dashboard`);
        }
      } catch (error) {
        console.warn('⚠️ Error deleting activities from Activity Dashboard:', error);
      }
      
      try {
        // Delete from tutorialArtworks (Ways of Seeing Co-Learning Space)
        const tutorialQuery = query(
          collection(db, 'tutorialArtworks'),
          where('userId', '==', user.uid),
          where('imageUrl', '==', artworkUrl)
        );
        const tutorialSnapshot = await getDocs(tutorialQuery);
        const tutorialsToDelete = [];
        tutorialSnapshot.forEach((doc) => {
          tutorialsToDelete.push(deleteDoc(doc.ref));
        });
        await Promise.all(tutorialsToDelete);
        if (tutorialsToDelete.length > 0) {
          console.log(`✅ Deleted ${tutorialsToDelete.length} Ways of Seeing artworks from Co-Learning Space`);
        }
      } catch (error) {
        console.warn('⚠️ Error deleting Ways of Seeing artworks:', error);
      }
      
      try {
        // Delete from artHistorySharedArtworks (Artmosphere Co-Learning Space)
        const artHistoryQuery = query(
          collection(db, 'artHistorySharedArtworks'),
          where('uid', '==', user.uid),
          where('imageUrl', '==', artworkUrl)
        );
        const artHistorySnapshot = await getDocs(artHistoryQuery);
        const artHistoryToDelete = [];
        artHistorySnapshot.forEach((doc) => {
          artHistoryToDelete.push(deleteDoc(doc.ref));
        });
        await Promise.all(artHistoryToDelete);
        if (artHistoryToDelete.length > 0) {
          console.log(`✅ Deleted ${artHistoryToDelete.length} Art History artworks from Co-Learning Space`);
        }
      } catch (error) {
        console.warn('⚠️ Error deleting Art History artworks:', error);
      }
      
      try {
        // Delete from bookArtworks (Book Co-Learning Space)
        const bookQuery = query(
          collection(db, 'bookArtworks'),
          where('uid', '==', user.uid),
          where('imageUrl', '==', artworkUrl)
        );
        const bookSnapshot = await getDocs(bookQuery);
        const booksToDelete = [];
        bookSnapshot.forEach((doc) => {
          booksToDelete.push(deleteDoc(doc.ref));
        });
        await Promise.all(booksToDelete);
        if (booksToDelete.length > 0) {
          console.log(`✅ Deleted ${booksToDelete.length} Book artworks from Co-Learning Space`);
        }
      } catch (error) {
        console.warn('⚠️ Error deleting Book artworks:', error);
      }
      
      // Delete image from Firebase Storage if applicable
      if (imageUrlToDelete && imageUrlToDelete.startsWith('gs://')) {
      // Only try to delete from Firebase Storage if it's a valid storage URL
        const imageRef = ref(storage, imageUrlToDelete);
      deleteObject(imageRef).catch(() => {});
    }
      
      // Close expanded view before unmounting this artwork’s modal node
      closeArtistWallExpandedView(art.id, true);

      // Optimistically remove artwork from UI immediately
      setArtworks(prev => prev.filter(a => a.id !== art.id));
      console.log('✅ Artwork removed from UI:', art.id);
      
      // Refresh artworks list in background to ensure consistency
      fetchArtworks().catch(err => {
        console.warn('⚠️ Error refreshing artworks after delete:', err);
        // If refresh fails, the optimistic update should still be visible
      });
    } catch (error) {
      console.error('❌ Error deleting artwork:', error);
      alert('Failed to delete artwork. Please try again.');
      // Re-fetch to restore correct state if deletion failed
    fetchArtworks();
    }
  };

  // resetForm moved above - see line ~227

  const handleEdit = async (art) => {
    // Handle both artworks and sharedArtworks structures
    setTitle(art.title || art.artworkTitle || '');
    setTechnique(art.technique || "");
    setInspiration(art.inspiration || "");
    setDescription(art.description || '');
    setTag(art.tag || bookTags[0]);
    setVisibility(art.visibility || (art.shareWithArtmosphere ? 'public' : 'private'));
    setEditId(art.id);
    setEditSource(art.source || 'artworks');
    setExistingImageUrl(art.imageUrl || art.artworkUrl);

    let detectedType = 'general';

    console.log('📝 [EDIT] Editing artwork:', {
      id: art.id,
      source: art.source,
      tag: art.tag,
      isBookArtwork: art.isBookArtwork,
    });

    let isWaysOfSeeing = false;
    let waysOfSeeingMetadata = null;
    
    // Check if artwork exists in tutorialArtworks collection (Ways of Seeing)
    if (art.imageUrl || art.artworkUrl) {
      try {
        const imageUrlToCheck = art.imageUrl || art.artworkUrl;
        const tutorialQuery = query(
          collection(db, 'tutorialArtworks'),
          where('imageUrl', '==', imageUrlToCheck),
          where('userId', '==', user.uid)
        );
        const tutorialSnapshot = await getDocs(tutorialQuery);
        if (!tutorialSnapshot.empty) {
          waysOfSeeingMetadata = tutorialSnapshot.docs[0].data();
          if (waysOfSeeingMetadata?.styleId && waysOfSeeingMetadata?.originalArtworkId) {
            isWaysOfSeeing = true;
          }
          console.log('✅ [EDIT] Found in tutorialArtworks - detected as Ways of Seeing:', {
            styleId: waysOfSeeingMetadata.styleId,
            styleName: waysOfSeeingMetadata.styleName,
            originalArtworkId: waysOfSeeingMetadata.originalArtworkId
          });
        }
      } catch (tutorialError) {
        console.warn('⚠️ [EDIT] Could not check tutorialArtworks:', tutorialError);
      }
    }
    
    if (isArtmosphereBookArtwork(art)) {
      detectedType = 'book-co-learning';
      setArtworkType('book-co-learning');
      setIsBookActivity(true);
      
      // Try to get activityCode from multiple sources
      let foundActivityCode = art.activityCode || art.activityId || art.bookActivityMeta?.activityCode;
      
      // If we have bookArtworkId but no activityCode, fetch from bookArtworks collection
      if (!foundActivityCode && art.bookArtworkId) {
        try {
          const bookArtworkDoc = await getDoc(doc(db, 'bookArtworks', art.bookArtworkId));
          if (bookArtworkDoc.exists()) {
            const bookArtworkData = bookArtworkDoc.data();
            foundActivityCode = bookArtworkData.activityCode;
            console.log('✅ [EDIT] Fetched activityCode from bookArtworks:', foundActivityCode);
          }
        } catch (error) {
          console.warn('⚠️ [EDIT] Could not fetch activityCode from bookArtworks:', error);
        }
      }
      
      // For "Around the world with 100 artists", use country dropdown instead of activity code
      // For "Simple acts of art", use activity number dropdown
      const isAroundTheWorld = (art.tag || bookTags[0]) === "Around the world with 100 artists";
      const isSimpleActs = (art.tag || bookTags[0]) === "Simple acts of art";
      if (foundActivityCode) {
        if (isAroundTheWorld) {
          // Check if the activityCode is a country name
          if (countries.includes(foundActivityCode)) {
            setSelectedCountry(foundActivityCode);
            setActivityCode(""); // Clear activity code for this book
            setSelectedActivityNumber(""); // Clear activity number
            console.log('✅ [EDIT] Set selectedCountry:', foundActivityCode);
          } else {
            // Fallback: if activityCode exists but not in countries list, set it as country anyway
            setSelectedCountry(foundActivityCode);
            setActivityCode("");
            setSelectedActivityNumber("");
            console.log('✅ [EDIT] Set selectedCountry (fallback):', foundActivityCode);
          }
        } else if (isSimpleActs) {
          // Check if the activityCode matches "Activity X" format
          if (foundActivityCode.startsWith("Activity ")) {
            setSelectedActivityNumber(foundActivityCode);
            setActivityCode(""); // Clear activity code for this book
            setSelectedCountry(""); // Clear country
            console.log('✅ [EDIT] Set selectedActivityNumber:', foundActivityCode);
          } else {
            // Fallback: set as activity number anyway
            setSelectedActivityNumber(foundActivityCode);
            setActivityCode("");
            setSelectedCountry("");
            console.log('✅ [EDIT] Set selectedActivityNumber (fallback):', foundActivityCode);
          }
        } else {
          setActivityCode(foundActivityCode);
          setSelectedCountry(""); // Clear country for other books
          setSelectedActivityNumber(""); // Clear activity number
          console.log('✅ [EDIT] Set activityCode:', foundActivityCode);
        }
      } else {
        console.warn('⚠️ [EDIT] No activityCode found for book artwork');
        setActivityCode("");
        setSelectedCountry("");
        setSelectedActivityNumber("");
      }
      
      console.log('✅ [EDIT] Detected as book co-learning artwork');
    } else if (
      isWaysOfSeeing ||
      art.source === 'ways-of-seeing' ||
      art.metadata?.feature === 'ways-of-seeing-journey' ||
      (art.styleId && art.originalArtworkId) ||
      (waysOfSeeingMetadata?.styleId && waysOfSeeingMetadata?.originalArtworkId)
    ) {
      detectedType = 'ways-of-seeing';
      setArtworkType('ways-of-seeing');
      setIsBookActivity(false);
      
      // Use metadata from tutorialArtworks if available, otherwise use art metadata
      const styleId = waysOfSeeingMetadata?.styleId || art.styleId || art.metadata?.styleId;
      const styleName = waysOfSeeingMetadata?.styleName || art.styleName || art.metadata?.styleName;
      const originalArtworkUrl = waysOfSeeingMetadata?.originalArtworkUrl || art.originalArtworkUrl || art.metadata?.originalImageUrl;
      const originalArtworkId = waysOfSeeingMetadata?.originalArtworkId || art.originalArtworkId || art.metadata?.originalArtworkId;
      
      if (styleId) setWaysOfSeeingStyleId(styleId);
      if (styleName) setWaysOfSeeingStyleName(styleName);
      if (originalArtworkUrl) {
        setWaysOfSeeingOriginalArtwork({ 
          imageUrl: originalArtworkUrl,
          id: originalArtworkId
        });
      }
      console.log('✅ [EDIT] Detected as ways of seeing artwork', {
        fromTutorialArtworks: !!waysOfSeeingMetadata,
        styleId,
        styleName,
        originalArtworkId
      });
    } else if (
      art.activityType === 'art-history-appreciation' ||
      art.source === 'art-history' ||
      art.type === 'art-history-appreciation'
    ) {
      detectedType = 'art-history';
      setArtworkType('art-history');
      setIsBookActivity(false);
      setSelectedArtHistoryActivity({
        id: art.activityId || art.id,
        title: art.metadata?.artworkTitle || art.activityTitle || art.title || 'Art History activity',
        metadata: art.metadata || {},
        originalImageUrl: art.metadata?.artworkImage || art.originalImageUrl,
      });
      console.log('✅ [EDIT] Detected as art history artwork');
    } else if (art.source === 'daily-activity' || (art.activityId && /^\d+$/.test(String(art.activityId).trim()))) {
      detectedType = art.tag === 'Simple acts of art' ? 'daily-acts' : 'daily-activity';
      setArtworkType('book-co-learning');
      setIsBookActivity(true);
      if (art.tag === 'Simple acts of art') setUploadWizardLock('daily-acts');
    } else {
      detectedType = 'general';
      setArtworkType('');
      setIsBookActivity(false);
      console.log('ℹ️ [EDIT] Detected as general artwork');
    }

    setEditLockedArtworkType(detectedType);
    if (detectedType === 'book-co-learning') {
      setUploadFlowStep('bookActivityUpload');
      const matchedBook = createPageBooks.find((b) => b.uploadTag === art.tag);
      if (matchedBook) setSelectedBookId(matchedBook.id);
    } else {
      setUploadFlowStep('generalUpload');
    }

    setShowUpload(true);
    if (detectedType !== 'daily-acts') setUploadWizardLock(null);
  };

  const goToAIStudio = (art) => {
    if (!art.imageUrl || !art.title) {
      alert('Artwork image and title are required.');
      return;
    }
    // Open Magic Studio modal (Awesome selection!) on My Art World with this artwork auto-selected
    navigate('/my-art-world', { state: { openMagicPicker: true, preselectedArtwork: art } });
  };

  const handleWaysOfSeeing = (originalArtwork, title) => {
    setWaysOfSeeingModalOriginalArtwork(originalArtwork);
    setSelectedOriginalArtworkTitle(title);
    setShowWaysOfSeeing(true);
  };

  const closeWaysOfSeeing = () => {
    setShowWaysOfSeeing(false);
    setWaysOfSeeingModalOriginalArtwork(null);
    setSelectedOriginalArtworkTitle('');
  };


  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const studioGridPlaceholders =
    Array.isArray(artworks) && artworks.length > 0 && artworks.length < 4 ? 4 - artworks.length : 0;

  const hasGallerySnapshot = Array.isArray(artworks) && artworks.length > 0;
  const hasProfileCache = profile && Object.keys(profile).length > 0;

  // Only block the full page when there is nothing to show yet (no cache, still fetching).
  if (
    !forceOpenUpload &&
    isLoading &&
    !hasGallerySnapshot &&
    !hasProfileCache
  ) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-transparent flex items-center justify-center">
        <RoomBackground />
        <KidBackground variant="blobs" intensity={0.12} className="!z-[1]" />
        <div className="relative z-10 text-amber-800 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <>
    {/* Upload-only overlay from Artist Corner — wizard only, not the full gallery room */}
    {!forceOpenUpload && (
    <>
    {/* Same 3D glass “room” + yellow wall as Learn Art History (RoomSceneWrapper includes RoomBackground + FloorShadow) */}
    <RoomSceneWrapper>
      <KidBackground variant="blobs" intensity={0.12} className="!z-[1]" />
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes studioPlusPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.06); }
          }
          .artist-wall-scroll { scrollbar-width: thin; scrollbar-color: #d4b896 #faf6ef; }
          .artist-wall-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
          .artist-wall-scroll::-webkit-scrollbar-track { background: rgba(250, 246, 239, 0.9); border-radius: 9999px; }
          .artist-wall-scroll::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, #e8d4b0, #c9a574);
            border-radius: 9999px;
          }
          .studio-nav-pill {
            display: inline-flex; align-items: center; gap: 0.3rem;
            padding: 0.32rem 0.6rem; border-radius: 9999px;
            border: 1px solid rgba(232, 212, 176, 0.9);
            background: rgba(255, 253, 248, 0.78);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            color: #5A4632; font-size: 0.68rem; font-weight: 600;
            transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease;
          }
          .studio-nav-pill:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(120, 72, 20, 0.12);
            background: rgba(255, 255, 255, 0.92);
            border-color: rgba(212, 181, 120, 0.95);
          }
          .studio-co-learning-cta {
            display: inline-flex; align-items: center; gap: 0.4rem;
            padding: 0.55rem 1.15rem; border-radius: 9999px;
            border: 1px solid rgba(212, 181, 120, 0.95);
            background: linear-gradient(180deg, rgba(255, 253, 248, 0.95), rgba(255, 246, 232, 0.88));
            color: #5A4632; font-size: 0.78rem; font-weight: 700;
            box-shadow: 0 10px 26px rgba(120, 72, 20, 0.1);
            transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
          }
          .studio-co-learning-cta:hover {
            transform: translateY(-2px);
            box-shadow: 0 14px 32px rgba(120, 72, 20, 0.14);
            background: rgba(255, 255, 255, 0.98);
          }
          .studio-nav-explore-cta {
            display: inline-flex; align-items: center; gap: 0.3rem;
            padding: 0.32rem 0.55rem; border-radius: 9999px;
            border: 1px solid rgba(212, 181, 120, 0.95);
            background: linear-gradient(180deg, rgba(255, 253, 248, 0.95), rgba(255, 246, 232, 0.88));
            color: #5A4632; font-size: 0.65rem; font-weight: 700;
            box-shadow: 0 6px 16px rgba(120, 72, 20, 0.08);
            white-space: nowrap;
            transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
          }
          .studio-nav-explore-cta:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 22px rgba(120, 72, 20, 0.12);
            background: rgba(255, 255, 255, 0.98);
          }
          @media (max-width: 767px) {
            .studio-nav-explore-cta {
              justify-content: center;
              gap: 0;
              min-width: 2.25rem;
              min-height: 2.25rem;
              padding: 0.35rem !important;
            }
            .studio-nav-explore-cta .studio-nav-explore-cta-label {
              display: none !important;
            }
            .studio-nav-explore-cta svg {
              width: 1.125rem;
              height: 1.125rem;
            }
          }
          .artist-expanded-overlay {
            opacity: 0;
            transition: opacity 0.32s ease;
            background:
              radial-gradient(circle at 12% 8%, rgba(255, 220, 160, 0.42), transparent 42%),
              radial-gradient(circle at 88% 92%, rgba(255, 186, 120, 0.32), transparent 48%),
              linear-gradient(180deg, rgba(255, 252, 246, 0.88) 0%, rgba(255, 244, 228, 0.82) 45%, rgba(255, 236, 212, 0.86) 100%);
            -webkit-backdrop-filter: blur(26px) saturate(1.15);
            backdrop-filter: blur(26px) saturate(1.15);
          }
          .artist-expanded-overlay--open {
            opacity: 1;
          }
          .artist-expanded-shell {
            transform: scale(0.9) translateY(18px);
            opacity: 0;
            transition: transform 0.42s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease;
            will-change: transform, opacity;
          }
          .artist-expanded-shell--open {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        `
      }} />

      <div className="relative z-[100]" style={{ overflow: 'visible' }}>
        {/* Outer top bar: one-line studio title (left) · nav pills (right) */}
        <div className="mx-auto flex max-w-[90rem] flex-nowrap items-center justify-between gap-2 border-b border-[#efe2d0]/50 px-3 py-2 md:gap-4 md:px-6 md:py-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex shrink-0 items-center gap-1.5 transition hover:opacity-95"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-orange-300 text-xs font-bold text-[#2F251B] shadow-md md:h-8 md:w-8 md:text-sm">
                A
              </span>
              <span className="hidden shrink-0 truncate text-xs font-semibold tracking-wide text-[#2F251B] sm:inline md:text-sm">
                Artmosphere
              </span>
            </button>
          </div>
          <div className="studio-outer-nav flex min-w-0 max-w-[72%] shrink-0 items-center justify-end gap-1 sm:max-w-none sm:gap-1.5">
            {user?.uid && (
              <MuseumExploreCta uid={user.uid} className="studio-nav-explore-cta shrink-0" compact />
            )}
            <div className="artist-wall-nav-menu relative z-[110] shrink-0">
              <button
                ref={artistWallNavButtonRef}
                type="button"
                onClick={toggleArtistWallNavMenu}
                onMouseDown={(e) => e.stopPropagation()}
                className="studio-nav-pill !gap-0 px-2 py-2"
                aria-expanded={navMenuOpen}
                aria-haspopup="menu"
                aria-label="Open navigation menu"
              >
                <Menu className="h-4 w-4 flex-shrink-0 text-[#8A7357]" strokeWidth={2} aria-hidden />
              </button>
            </div>
            <div className="shrink-0">
              <ReactionNotifications refreshKey={notificationRefresh} />
            </div>
            {navMenuOpen && navMenuAnchor && typeof document !== 'undefined' && createPortal(
              <div
                data-artist-wall-nav-dropdown
                role="menu"
                className="fixed z-[250] min-w-[11.5rem] overflow-hidden rounded-2xl border border-[#F6D57B]/90 bg-[#FFFDF9] py-1.5 shadow-[0_12px_32px_rgba(120,72,20,0.14)]"
                style={{ top: navMenuAnchor.top, right: navMenuAnchor.right }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setNavMenuOpen(false); setNavMenuAnchor(null); navigate('/home'); }}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                >
                  <Home className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                  Home
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setNavMenuOpen(false); setNavMenuAnchor(null); navigate('/my-art-world'); }}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                >
                  <LayoutDashboard className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                  Artist Corner
                </button>
                {user && (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setNavMenuOpen(false); setNavMenuAnchor(null); navigate('/artist-wall'); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                    >
                      <Frame className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                      My Gallery
                    </button>
                    <div className="my-1 border-t border-[#F5D99B]/50" aria-hidden />
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setNavMenuOpen(false); setNavMenuAnchor(null); setShowAccountSettings(true); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                    >
                      <User className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                      My Account
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setNavMenuOpen(false); setNavMenuAnchor(null); handleSignOut(); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                    >
                      <LogOut className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                      Sign out
                    </button>
                  </>
                )}
              </div>,
              document.body
            )}
          </div>
        </div>

        {/* Profile row — mobile: avatar + stories (Instagram-style) · desktop: avatar · meta · insights */}
        <div className="relative z-30 mx-auto max-w-[90rem] px-3 pb-2 pt-1 md:px-6 md:pb-2 md:pt-1.5">
          <div className="pointer-events-none absolute left-6 top-0 hidden h-8 w-px bg-gradient-to-b from-[#e8c896]/0 via-[#e8c896]/70 to-[#e8c896]/0 lg:block" aria-hidden />
          <div className="grid grid-cols-[auto_1fr] items-center gap-x-2.5 gap-y-2 sm:gap-x-3 md:grid-cols-[auto_minmax(0,11rem)_1fr] md:gap-x-4 lg:grid-cols-[auto_minmax(0,14rem)_1fr]">
              <div className="row-start-1 col-start-1 self-center group shrink-0 transition-transform duration-300 ease-out hover:-translate-y-0.5">
                <div className="rounded-full bg-gradient-to-br from-[#fcecc9] via-[#f4e0bc] to-[#e8c896] p-[2px] shadow-[0_8px_20px_rgba(160,100,40,0.16)] ring-1 ring-white/90">
                  <div className="rounded-full bg-[#fffdf8] p-0.5">
                    {profile.isUnder13 && profile.avatarId ? (
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#fff7e8] to-[#fdebd4] text-2xl shadow-inner md:h-[3.75rem] md:w-[3.75rem] md:text-3xl">
                        {getAvatarEmoji(profile.avatarId)}
                      </div>
                    ) : profile.profilePicture ? (
                      <>
                        <SmartImage
                          src={profile.profilePicture}
                          alt="Artist"
                          className="h-14 w-14 rounded-full object-cover md:h-[3.75rem] md:w-[3.75rem]"
                          containerClassName="h-14 w-14 overflow-hidden rounded-full md:h-[3.75rem] md:w-[3.75rem]"
                          onError={(e) => {
                            console.error('❌ ArtistWall: Profile picture failed to load:', profile.profilePicture);
                            console.error('❌ ArtistWall: Error details:', e);
                            const img = e.currentTarget;
                            const fallback = img.nextElementSibling;
                            if (img) img.style.display = 'none';
                            if (fallback) fallback.style.display = 'flex';
                          }}
                          onLoad={() => {
                            console.log('✅ ArtistWall: Profile picture loaded successfully:', profile.profilePicture);
                          }}
                        />
                        <div
                          className="hidden h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#fff7e8] to-[#fdebd4] md:h-[3.75rem] md:w-[3.75rem]"
                          style={{ display: 'none' }}
                        >
                          <svg className="h-8 w-8 text-[#c9a574] md:h-9 md:w-9" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </>
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#fff7e8] to-[#fdebd4] md:h-[3.75rem] md:w-[3.75rem]">
                        <svg className="h-8 w-8 text-[#c9a574] md:h-9 md:w-9" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            {user?.uid && (
              <div className="row-start-1 col-start-2 min-w-0 self-center md:col-start-3">
                <ArtistWallMuseumInsights uid={user.uid} placement="profile" />
              </div>
            )}
            <div className="row-start-2 col-span-2 min-w-0 text-left md:row-start-1 md:col-start-2 md:col-span-1 md:self-center">
              <h1 className="inline-flex max-w-full items-center gap-1 font-semibold leading-tight text-[#2F251B] text-[clamp(1rem,2vw,1.2rem)]">
                <Frame className="h-3 w-3 shrink-0 text-[#c9a574] opacity-75" aria-hidden />
                <span className="truncate">{displayName || profile.name || 'Young Artist'}</span>
              </h1>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="inline-flex items-center gap-1 rounded-full border border-[#e8d4bc] bg-white/70 px-2 py-0.5 text-[10px] font-medium text-[#5A4632] shadow-sm transition hover:bg-white"
                >
                  <FaEdit className="text-[9px] text-[#8A7357]" />
                  <span>Edit</span>
                </button>
                <p className="text-[10px] font-medium tracking-wide text-[#8A7357]">
                  <span className="font-semibold text-[#2F251B]">{artworks?.length ?? 0}</span> artworks
                </p>
              </div>
              <p className="mt-0.5 max-w-full truncate text-[10px] italic leading-snug text-[#5A4632] md:max-w-[11rem] lg:max-w-[14rem]">
                {profile.bio || 'Love Art. Live Art.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable gallery — museum studio panel */}
      <div
        className="artist-wall-scroll h-[calc(100vh-188px)] overflow-y-auto px-2 pb-3 pt-0 sm:h-[calc(100vh-178px)] md:h-[calc(100vh-102px)] lg:h-[calc(100vh-108px)] md:px-4"
        style={{ minHeight: 'min(70vh, 500px)' }}
      >
        <div
          className="mx-auto mb-2 mt-0 h-full w-full max-w-[90rem] rounded-[28px] border border-white/75 p-3 shadow-[0_28px_70px_rgba(120,72,20,0.12),inset_0_1px_0_rgba(255,255,255,0.65)] backdrop-blur-xl md:mb-4 md:mt-1 md:rounded-[36px] md:p-4"
          style={{
            backgroundImage: `
              radial-gradient(circle at 0% 0%, rgba(255, 208, 120, 0.18), transparent 35%),
              radial-gradient(circle at 100% 100%, rgba(255, 183, 77, 0.12), transparent 40%),
              linear-gradient(180deg, rgba(255,253,248,0.94) 0%, rgba(255,246,232,0.9) 100%)
            `,
          }}
        >

        {/* Empty state: one airy glass panel (no second opaque white layer on top of room glass) */}
        {artworks && artworks.length === 0 && (
          <div className="relative mb-4 min-h-0 w-full rounded-[24px] border border-[#f0e4d4]/90 bg-white/45 p-3 shadow-[0_16px_40px_rgba(200,150,90,0.08)] backdrop-blur-md sm:p-5 md:mb-6 md:min-h-[min(520px,calc(100vh-240px))] md:rounded-[30px] md:p-8">
              <PerspectiveHero
                title="Your gallery is waiting for its first creation"
                subtitle="Upload your first piece, then explore Magic Studio, Learn Art History, and more."
              >
                <div className="flex flex-col items-center gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowUpload(true);
                      setShowUploadNudge(false);
                    }}
                    className={`${artPrimaryBtnLg} inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-xs font-semibold sm:px-6 sm:py-3 sm:text-sm`}
                  >
                    <Plus className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                    Upload your first artwork
                  </button>
                  <div className="grid w-full grid-cols-3 gap-2 sm:max-w-3xl sm:gap-3">
                    {[0, 1, 2].map((slot) => (
                      <div
                        key={slot}
                        className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9]/80 to-[#fff6e8]/50 px-2 py-4 text-center sm:rounded-2xl sm:px-3 sm:py-6"
                      >
                        <Frame className="mb-1 h-6 w-6 text-[#e8d4bc] sm:mb-2 sm:h-8 sm:w-8" aria-hidden />
                        <p className="text-[10px] font-medium leading-snug text-[#8A7357] sm:text-xs">
                          Your art here
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </PerspectiveHero>
          </div>
        )}
          
          {artworks && artworks.length > 0 && (
            <div className="mb-4 flex flex-col gap-3 border-b border-gradient-to-r from-transparent via-[#e8d4bc]/60 to-transparent pb-3">
              <div className="min-w-0 text-center md:text-left">
                <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#8A7357]">Curated wall</p>
                <div className="mt-0.5 flex items-center justify-center gap-2 md:justify-start">
                  <h2 className="font-semibold text-[#2F251B] text-[clamp(1.1rem,1.9vw,1.4rem)]">
                    Your Gallery Collection
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowUpload(true);
                      setShowUploadNudge(false);
                    }}
                    className="group relative inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-[#f59e0b] via-[#fbbf24] to-[#ea580c] text-white shadow-[0_8px_22px_rgba(234,88,12,0.28)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(234,88,12,0.36)] active:translate-y-0 md:h-10 md:w-10"
                    title="Upload a new artwork"
                    aria-label="Create new artwork"
                  >
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <Plus
                      className="relative h-5 w-5 animate-[studioPlusPulse_2.4s_ease-in-out_infinite] md:h-[1.35rem] md:w-[1.35rem]"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  </button>
                </div>
                <p className="mt-0.5 max-w-2xl text-xs leading-relaxed text-[#5A4632] md:mx-0 mx-auto">
                  A growing collection of your creative explorations across ARTmosphere.
                </p>
              </div>
              {user?.uid && <ArtistWallMostLoved uid={user.uid} />}
            </div>
          )}

          <div className="grid w-full grid-cols-1 items-start gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {artworks && artworks.length > 0 && (
            <>
        {artworks.map((art, index) => {
                const studioMeta = getArtworkStudioMeta(art);
                const cardTitle = String(art.title || art.artworkTitle || 'Untitled').trim();
                const activityIconTooltip = [studioMeta.activityTooltip, cardTitle]
                  .filter((s) => String(s || '').trim())
                  .join(' — ');
                const reactionCount = art.kudosCount ?? reactionCountsByArtworkId[art.id] ?? 0;
                return (
                <React.Fragment key={art.id}>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.38, ease: 'easeOut', delay: Math.min(index, 14) * 0.035 }}
                  className="group relative overflow-hidden rounded-[24px] bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] p-[2px] shadow-[0_14px_40px_rgba(120,72,20,0.1)] ring-1 ring-[#e8d4bc]/70 transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_22px_52px_rgba(120,72,20,0.14)]"
                >
                  <div className="flex h-full flex-col overflow-hidden rounded-[22px] bg-[#fffdf9]/95">
                    <div className="relative w-full min-h-[11rem] flex-1 overflow-hidden bg-[#f4efe6] ring-1 ring-inset ring-[#efe2d0]/90 sm:min-h-[12.5rem]">
                      <ArtistWallActivityTypeBadge
                        kind={studioMeta.activityKind}
                        tooltip={activityIconTooltip}
                      />
                <SmartImage
                  src={resolveImageUrlUtil(art.thumbnailUrl || art.imageUrl || art.artworkUrl)}
                  alt={cardTitle}
                  loading={index < 24 ? "eager" : "lazy"}
                  fetchPriority={index < 12 ? "high" : (index < 24 ? "auto" : "low")}
                  decoding="async"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCuratedWallReactionPopover(null);
                    console.log('Single click detected for:', art.title);
                    
                    // Show the expanded view directly on single click
                    openArtistWallExpandedView(art.id);
                    console.log('Modal opened for: ' + art.title);
                  }}
                  onError={(e) => {
                    try {
                      if (e.currentTarget.dataset.fallbackTried === '1') return;
                      const current = e.currentTarget.src || '';
                      const raw = (art?.imageUrl || art?.artworkUrl || '').trim();

                      // Try Firebase Storage direct URL first when we still have a storage reference.
                      if (raw && (raw.startsWith('gs://') || (!/^https?:\/\//i.test(raw) && !raw.startsWith('/uploads') && !raw.startsWith('uploads')))) {
                        e.currentTarget.dataset.fallbackTried = '1';
                        const objectPath = raw.startsWith('/') ? raw.slice(1) : raw;
                        getDownloadURL(ref(storage, raw.startsWith('gs://') ? raw : objectPath))
                          .then((downloadUrl) => {
                            e.currentTarget.src = downloadUrl;
                          })
                          .catch(() => {
                            if (!current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                              const api = require('../config/backendConfig').API_ENDPOINTS;
                              e.currentTarget.src = `${api.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                            }
                          });
                        return;
                      }

                      if (!current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                        e.currentTarget.dataset.fallbackTried = '1';
                        const api = require('../config/backendConfig').API_ENDPOINTS;
                        e.currentTarget.src = `${api.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                      }
                      console.warn('[ArtistWall] Image failed to load', {
                        title: art?.title,
                        rawImageUrl: art?.imageUrl,
                        artworkUrl: art?.artworkUrl,
                        currentSrc: current
                      });
                    } catch (_) {}
                  }}
                  className="h-full min-h-[11rem] w-full cursor-pointer object-cover transition-transform duration-300 ease-out group-hover:scale-[1.05] sm:min-h-[12.5rem]"
                  containerClassName="aspect-[3/4] w-full min-h-[11rem] sm:min-h-[12.5rem]"
                />
                      {reactionCount > 0 && (
                        <button
                          type="button"
                          data-curated-reaction-trigger="1"
                          className="absolute right-2 top-2 z-[12] inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-semibold text-[#2F251B] shadow-md ring-1 ring-[#efe2d0]/80 transition hover:bg-white hover:ring-[#e8d4bc]"
                          title={`${reactionCount} reaction${reactionCount === 1 ? '' : 's'} — see who reacted`}
                          aria-label={`${reactionCount} reaction${reactionCount === 1 ? '' : 's'}. Open list of people who reacted.`}
                          onClick={(e) => openCuratedWallReactionList(e, art.id)}
                        >
                          <Heart className="h-3 w-3 fill-rose-400 text-rose-500" aria-hidden />
                          {reactionCount}
                        </button>
                      )}
                    </div>

                    <div className="flex min-h-[3.25rem] shrink-0 flex-col justify-center border-t border-[#f0e6d8]/90 px-2.5 pb-2.5 pt-2">
                      <h3 className="line-clamp-2 min-h-[2.5rem] font-semibold leading-snug text-[#8A7357] text-[clamp(0.88rem,1.12vw,0.98rem)]">
                        {cardTitle}
                      </h3>
                    </div>
                  </div>
                </motion.div>

                {/* Expanded modal portaled to document.body — full-viewport blur + pop-out above Artist Wall */}
                {createPortal(
                  <div
                    id={`expanded-view-${art.id}`}
                    className="artist-expanded-overlay hidden fixed inset-0 z-[200050] flex items-center justify-center bg-[rgba(255,244,228,0.68)] px-3 py-3 backdrop-blur-3xl backdrop-saturate-150 sm:px-4"
                    style={{ display: 'none', ...coLearningWallBackdropProps.style }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) closeArtistWallExpandedView(art.id);
                    }}
                  >
              <div
                className="artist-expanded-shell relative z-[1] flex h-[min(88vh,720px)] w-[min(94vw,980px)] max-w-full flex-col overflow-hidden rounded-[32px] border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] shadow-[0_32px_90px_rgba(120,72,20,0.22)]"
                onClick={(e) => e.stopPropagation()}
              >
                                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  {/* Header with Close Button */}
                  <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#e8d4bc]/60 px-4 py-3 sm:px-6">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">
                        Your gallery
                      </p>
                      <h2 className="truncate text-lg font-semibold text-[#2F251B] sm:text-xl">
                        {art.title || art.artworkTitle || 'Artwork'}
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => closeArtistWallExpandedView(art.id)}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e8d4bc] bg-white/90 text-[#5e5a54] shadow-sm transition hover:bg-white hover:text-[#2f2a24]"
                      aria-label="Close"
                    >
                      <FaTimes className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4 sm:p-6 lg:flex-row lg:gap-6">
                    <div className="lg:w-[58%] lg:shrink-0">
                      <div className="overflow-hidden rounded-2xl border border-[#e8d4bc]/90 shadow-inner">
                        <div className="relative">
                          <ArtworkFillDisplay
                            src={art.thumbnailUrl || art.imageUrl || art.artworkUrl}
                            alt={art.title || art.artworkTitle || 'Artwork'}
                            containerClassName="h-[min(52vh,480px)] w-full min-h-[240px] sm:min-h-[280px]"
                            onExpandClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openArtworkFullscreenView(art);
                            }}
                            onError={(e) => {
                              try {
                                const current = e.currentTarget.src || '';
                                if (!current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                                  const api = require('../config/backendConfig').API_ENDPOINTS;
                                  e.currentTarget.src = `${api.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                                }
                              } catch (_) {}
                            }}
                          />
                          <button
                            className="absolute top-3 right-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-[#e8d4bc] bg-white/95 text-[#8A7357] shadow-md transition hover:bg-white hover:text-[#2F251B]"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openArtworkFullscreenView(art);
                            }}
                            title="Expand to fullscreen"
                            type="button"
                          >
                            <FaExpand className="h-3.5 w-3.5" aria-hidden />
                          </button>
                          <ArtworkReactions
                            artworkId={getReactionArtworkId(art) || art.id}
                            artistId={art.uid || user?.uid}
                            enableComments={false}
                            listLayout="dropdown"
                            variant="premium"
                            onKudosChange={(count) => handleKudosChange(art.id, count)}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex min-w-0 flex-1 flex-col">
                            {(art.description || art.artworkDescription) && (
                              <p className="mb-4 text-sm leading-relaxed text-[#5e5a54] sm:text-base">
                                {art.description || art.artworkDescription}
                              </p>
                            )}
                            {(art.technique || art.artworkTechnique) && (
                              <div className="mb-3 text-sm">
                                <span className="font-semibold text-[#8A7357]">Art style </span>
                                <span className="text-[#2F251B]">{art.technique || art.artworkTechnique}</span>
                              </div>
                            )}
                            {(art.inspiration || art.artworkInspiration) && (
                              <div className="mb-3 text-sm">
                                <span className="font-semibold text-[#8A7357]">Inspiration </span>
                                <span className="text-[#2F251B]">{art.inspiration || art.artworkInspiration}</span>
                          </div>
                        )}
                        {art.originalArtworkMeta && (
                          <div className="mb-4 rounded-xl border border-[#f0e6d8]/90 bg-white/60 p-3">
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A7357]">
                              Original artwork
                            </p>
                            {art.originalArtworkMeta.styleName && (
                              <p className="text-sm text-[#2F251B]">Style: {art.originalArtworkMeta.styleName}</p>
                            )}
                          </div>
                        )}
                        
                            <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => handleWaysOfSeeing(art.imageUrl, art.title)}
                      className="rounded-full border border-[#f5d99b] bg-gradient-to-r from-[#fde68a] to-[#fbbf24] px-5 py-2.5 text-sm font-semibold text-[#2F251B] shadow-md transition hover:from-[#fcd34d] hover:to-[#f59e0b]"
                    >
                      Ways of Seeing
                    </button>
                    <button
                      type="button"
                      onClick={() => goToAIStudio(art)}
                      className="rounded-full border border-[#f5d99b] bg-gradient-to-r from-[#fde68a] to-[#fbbf24] px-5 py-2.5 text-sm font-semibold text-[#2F251B] shadow-md transition hover:from-[#fcd34d] hover:to-[#f59e0b]"
                    >
                      Magic Studio
                    </button>
                  </div>
                  
                            {art.uid === user?.uid && (
                              <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-[#f0e6d8]/90 pt-4">
                    <button
                                  onClick={() => {
                                    handleEdit(art);
                                    closeArtistWallExpandedView(art.id);
                                  }}
                                  className="flex items-center gap-2 rounded-full border border-[#f5d99b] bg-gradient-to-r from-[#fde68a] to-[#fbbf24] px-4 py-2 text-sm font-semibold text-[#2F251B] shadow-sm transition hover:from-[#fcd34d] hover:to-[#f59e0b]"
                      title="Edit"
                    >
                                  <FaEdit className="w-4 h-4" />
                      <span className="text-sm font-medium">Edit</span>
                    </button>
                    <button
                                  onClick={() => {
                                    handleDelete(art);
                                  }}
                                  className="flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50"
                      title="Delete"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      <span className="text-sm font-medium">Delete</span>
                    </button>
                              </div>
                            )}
                          </div>
                  </div>
                </div>
              </div>
            </div>,
                  document.body
                )}
                </React.Fragment>
                );
        })}
            {studioGridPlaceholders > 0 &&
              Array.from({ length: studioGridPlaceholders }).map((_, ph) => (
                <div
                  key={`studio-ph-${ph}`}
                  className="flex min-h-[260px] flex-col justify-between rounded-[24px] border border-dashed border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9]/55 to-[#fff6ec]/35 p-6 text-center shadow-inner"
                >
                  <Frame className="mx-auto h-12 w-12 text-[#e8d4bc]" aria-hidden />
                  <div>
                    <p className="text-sm font-semibold text-[#8A7357]">Reserved for your art</p>
                    <p className="mt-1 text-xs leading-relaxed text-[#8A7357]/90">
                      Your next masterpiece belongs here
                    </p>
                  </div>
                </div>
              ))}
            {curatedWallReactionPopover &&
              createPortal(
                <div
                  ref={curatedReactionPopoverRef}
                  role="dialog"
                  aria-modal="true"
                  aria-label="People who reacted"
                  className="pointer-events-auto overflow-hidden rounded-2xl border border-[#e8d4bc] bg-[#fffdf9] shadow-[0_16px_48px_rgba(120,72,20,0.22)]"
                  style={getCuratedReactionPopoverStyle(curatedWallReactionPopover.anchorRect)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between border-b border-[#f0e6d8] bg-white/90 px-3 py-2">
                    <p className="text-xs font-semibold text-[#2F251B]">
                      {curatedWallReactionPopover.loading
                        ? 'Loading…'
                        : `${curatedWallReactionPopover.items.length} reaction${
                            curatedWallReactionPopover.items.length === 1 ? '' : 's'
                          }`}
                    </p>
                    <button
                      type="button"
                      className="flex h-7 w-7 items-center justify-center rounded-full text-[#8A7357] hover:bg-[#fff6ec]"
                      aria-label="Close"
                      onClick={() => setCuratedWallReactionPopover(null)}
                    >
                      <X className="h-4 w-4" strokeWidth={2} aria-hidden />
                    </button>
                  </div>
                  <div className="max-h-[min(280px,45vh)] overflow-y-auto bg-white p-2.5 pr-1">
                    {curatedWallReactionPopover.loading ? (
                      <p className="py-6 text-center text-xs text-[#8A7357]">Loading reactions…</p>
                    ) : curatedWallReactionPopover.items.length === 0 ? (
                      <p className="py-6 text-center text-xs text-[#8A7357]">No reactions yet.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {curatedWallReactionPopover.items.map((item, itemIdx) => {
                          const meta = KUDOS_REACTION_LABELS[item.reactionType] || {
                            emoji: '🌟',
                            label: item.reactionType || 'Reaction',
                          };
                          let displayName = item.userName || 'Artmosphere Artist';
                          if (displayName && displayName.includes('@')) {
                            displayName = displayName.split('@')[0];
                          }
                          const rowKey = item.id || `${item.userId || 'u'}-${itemIdx}`;
                          return (
                            <li key={rowKey}>
                              <button
                                type="button"
                                className="flex w-full items-center gap-2.5 rounded-xl border border-[#f0e6d8] bg-white px-2 py-2 text-left transition hover:border-[#f5d99b] hover:bg-[#fff9ef]"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  ev.stopPropagation();
                                  if (item.userId) navigate(`/artist-public-gallery/${item.userId}`);
                                }}
                              >
                                <span
                                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fff6ec] text-lg"
                                  aria-hidden
                                >
                                  {meta.emoji}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-sm font-semibold text-[#2F251B]">{displayName}</div>
                                  <div className="text-xs text-[#8A7357]">{meta.label}</div>
                                  {item.timestamp && (
                                    <p className="mt-0.5 text-[10px] text-[#8A7357]/80">
                                      {formatCuratedKudosTime(item.timestamp)}
                                    </p>
                                  )}
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>,
                document.body
              )}
            </>
          )}
        </div>
      </div>
      </div>

    </RoomSceneWrapper>
    </>
    )}

      {showUpload &&
        typeof document !== 'undefined' &&
        createPortal(
        <div
          role="presentation"
          className={`${coLearningWallBackdropProps.className} ${forceOpenUpload ? 'z-[200110]' : '!z-[500]'}`}
          style={coLearningWallBackdropProps.style}
          onClick={() => {
            setShowUpload(false);
            resetForm();
            onCloseUploadModal?.();
          }}
        >
          <div
            className="relative flex max-h-[min(92dvh,900px)] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] shadow-[0_28px_80px_rgba(120,72,20,0.2)] ring-1 ring-[#efe2d0]/80"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="h-1 w-full shrink-0 bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F97316]"
              aria-hidden
            />
            <header className="relative shrink-0 border-b border-[#e8d4bc]/60 bg-[#FFFDF9]/95 px-4 py-3.5 backdrop-blur-md sm:px-6 sm:py-4">
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.35),transparent_70%)]"
                aria-hidden
              />
              <div className="relative flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
                  {!editId && uploadFlowStep !== 'sourceChoice' && (
                    <button
                      type="button"
                      onClick={handleUploadModalBack}
                      className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-[#E8C97A] bg-[#FFF9EF] px-2.5 py-1.5 text-xs font-semibold text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:bg-white"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                      Back
                    </button>
                  )}
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A7357]">
                      Artist Wall
                    </p>
                    <h3 className="font-serif text-lg font-semibold leading-snug text-[#2F251B] sm:text-xl">
                      {editId
                        ? 'Edit Artwork'
                        : uploadFlowStep === 'sourceChoice'
                          ? 'Is this artwork for an ARTmosphere activity?'
                          : uploadFlowStep === 'activityCategoryChoice'
                            ? 'What kind of activity?'
                            : uploadFlowStep === 'studioActivityChoice'
                              ? 'Artmosphere Studio activities'
                              : uploadFlowStep === 'bookSelect'
                                ? 'Choose your ARTmosphere book'
                                : 'Upload New Artwork'}
                    </h3>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowUpload(false);
                    resetForm();
                    onCloseUploadModal?.();
                  }}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:bg-white hover:shadow-md"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" strokeWidth={2.25} />
                </button>
              </div>
            </header>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">

            {!editId && uploadFlowStep === "sourceChoice" && (
              <div className={uploadWizardPanelClass}>
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setUploadFlowStep("activityCategoryChoice");
                      setBookSelectError("");
                    }}
                    className={`${artPrimaryBtnLg} flex-1 min-h-[3rem] text-base`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsFromBook(false);
                      setUploadWizardLock("general-only");
                      setUploadFlowStep("generalUpload");
                      setVisibility("private");
                      setArtworkType("");
                      setIsBookActivity(false);
                      setActivityCode("");
                      setSelectedCountry("");
                      setSelectedActivityNumber("");
                    }}
                    className={`${artSecondaryBtnLg} flex-1 min-h-[3rem] text-base`}
                  >
                    No, it&apos;s my own artwork
                  </button>
                </div>
              </div>
            )}

            {!editId && uploadFlowStep === "activityCategoryChoice" && (
              <div className={uploadWizardStepCardClass}>
                <p className={`${uploadWizardBodyClass} mb-6 text-center`}>
                  Tap the picture that matches where you did your activity.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setIsFromBook(true);
                      setUploadFlowStep("bookSelect");
                      setSelectedBookId("");
                      setBookSelectError("");
                    }}
                    className={uploadWizardChoiceCardClass}
                  >
                    <div className="aspect-[4/3] bg-amber-100 relative overflow-hidden">
                      <img
                        src={createPageBooks[0]?.image}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                      />
                      <div className="absolute inset-x-0 bottom-0 border-t border-[#F6D57B]/80 bg-gradient-to-t from-[#FFFDF9] via-[#FFF9EF]/95 to-transparent px-3 pb-3 pt-10">
                        <p className="text-center text-base font-semibold leading-tight text-[#2F251B]">Activity from ARTmosphere books</p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsFromBook(false);
                      setUploadFlowStep("studioActivityChoice");
                    }}
                    className={uploadWizardChoiceCardClass}
                  >
                    <div className="aspect-[4/3] bg-gradient-to-br from-amber-100 via-amber-200 to-amber-300/90 relative overflow-hidden">
                      <img
                        src={createPageBooks.find((b) => b.title === "Impressionism")?.image}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                      />
                      <div className="absolute inset-x-0 bottom-0 border-t border-[#F6D57B]/80 bg-gradient-to-t from-[#FFFDF9] via-[#FFF9EF]/95 to-transparent px-3 pb-3 pt-12">
                        <p className="text-center text-base font-semibold leading-tight text-[#2F251B]">Artmosphere Studio activities</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {!editId && uploadFlowStep === "studioActivityChoice" && (
              <div className={uploadWizardStepCardClass}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setUploadWizardLock("ways-of-seeing");
                      setArtworkType("ways-of-seeing");
                      setIsBookActivity(false);
                      setActivityCode("");
                      setSelectedCountry("");
                      setSelectedActivityNumber("");
                      setUploadFlowStep("generalUpload");
                    }}
                    className={`${uploadWizardChoiceCardClass} bg-gradient-to-b from-[#FFFDF9] to-[#FFF5E8]`}
                  >
                    <div className="relative flex aspect-[4/3] flex-col items-center justify-center gap-2 p-4">
                      <Sparkles className="h-14 w-14 text-[#B45309] drop-shadow-sm transition-transform group-hover:rotate-6 sm:h-16 sm:w-16" strokeWidth={2} aria-hidden />
                      <p className="text-center text-base font-semibold tracking-tight text-[#2F251B] sm:text-lg">Ways of Seeing</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadWizardLock("daily-acts");
                      setArtworkType("book-co-learning");
                      setIsBookActivity(true);
                      setTag("Simple acts of art");
                      setActivityCode("");
                      setSelectedCountry("");
                      setSelectedActivityNumber("");
                      setUploadFlowStep("generalUpload");
                    }}
                    className={`${uploadWizardChoiceCardClass} bg-gradient-to-b from-[#FFFDF9] to-[#FFF5E8]`}
                  >
                    <div className="relative flex aspect-[4/3] flex-col items-center justify-center gap-2 p-4">
                      <Calendar className="h-14 w-14 text-[#B45309] drop-shadow-sm transition-transform group-hover:-rotate-3 sm:h-16 sm:w-16" strokeWidth={2} aria-hidden />
                      <p className="text-center text-base font-semibold tracking-tight text-[#2F251B] sm:text-lg">Daily Acts</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadWizardLock("art-history");
                      setArtworkType("art-history");
                      setIsBookActivity(false);
                      setActivityCode("");
                      setSelectedCountry("");
                      setSelectedActivityNumber("");
                      setUploadFlowStep("generalUpload");
                    }}
                    className={`${uploadWizardChoiceCardClass} bg-gradient-to-b from-[#FFFDF9] to-[#FFF5E8]`}
                  >
                    <div className="relative flex aspect-[4/3] flex-col items-center justify-center gap-2 p-4">
                      <BookMarked className="h-14 w-14 text-[#B45309] drop-shadow-sm transition-transform group-hover:rotate-3 sm:h-16 sm:w-16" strokeWidth={2} aria-hidden />
                      <p className="text-center text-sm font-semibold tracking-tight text-[#2F251B] sm:text-lg">Art History</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {!editId && uploadFlowStep === "bookSelect" && (
              <div className={`${uploadWizardStepCardClass} space-y-4`}>
                <label className={uploadWizardLabelClass}>Which ARTmosphere book is your activity from?</label>
                <select
                  value={selectedBookId}
                  onChange={(e) => {
                    setSelectedBookId(e.target.value);
                    setBookSelectError("");
                  }}
                  className={onboardingInputClass}
                >
                  <option value="">Select a book</option>
                  {createPageBooks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.title}
                    </option>
                  ))}
                </select>
                {bookSelectError && <p className={onboardingErrorClass}>{bookSelectError}</p>}
                <button
                  type="button"
                  onClick={confirmBookSelectionForUpload}
                  className={`${artPrimaryBtnLg} w-full`}
                >
                  Continue
                </button>
              </div>
            )}

            {(editId ||
              uploadFlowStep === "generalUpload" ||
              uploadFlowStep === "bookActivityUpload") && (
            <div className="flex flex-col md:flex-row gap-6">
              {editId && existingImageUrl && (
                <div className="md:w-1/3">
                  <SmartImage src={existingImageUrl} alt="Current Artwork" className="w-full h-auto object-cover rounded-xl border-2 border-amber-200 shadow-md" containerClassName="w-full h-auto" />
                </div>
              )}
              <div className={`${editId && existingImageUrl ? 'md:w-2/3' : 'w-full'} space-y-5`}>
                {editId && editLockedArtworkType && (
                <div className="rounded-2xl border border-amber-200/70 bg-amber-50/60 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700/90">Artwork type</p>
                  <p className="mt-1 text-sm font-medium text-amber-900">
                    {getEditArtworkTypeLabel(editLockedArtworkType)}
                  </p>
                  <p className="mt-1 text-xs text-amber-700/80">
                    Fields below match how this piece was created. Type cannot be changed while editing.
                  </p>
                </div>
                )}

                {/* Book Co-Learning Fields */}
                {artworkType === 'book-co-learning' && (
                  <div className="mt-4 space-y-3 pt-4 border-t border-amber-200/60">
                      <div>
                        <label className="block text-sm font-semibold text-amber-700 mb-2">
                          {editId ? 'Artmosphere book' : 'Select Artmosphere Book *'}
                        </label>
                        {editId ? (
                          <div className="w-full p-3 border-2 border-amber-200 rounded-xl bg-amber-50/90 text-amber-900 font-medium shadow-sm">
                            {createPageBooks.find((b) => b.uploadTag === tag)?.title || tag}
                          </div>
                        ) : !editId && uploadFlowStep === "bookActivityUpload" ? (
                          <div className="w-full p-3 border-2 border-amber-200 rounded-xl bg-amber-50/90 text-amber-900 font-medium shadow-sm">
                            {createPageBooks.find((b) => b.id === selectedBookId)?.title || tag}
                          </div>
                        ) : !editId && uploadWizardLock === "daily-acts" ? (
                          <div className="w-full p-3 border-2 border-amber-200 rounded-xl bg-amber-50/90 text-amber-900 font-medium shadow-sm">
                            Daily Acts — Artmosphere Studio
                          </div>
                        ) : (
                        <select 
                          value={tag} 
                          onChange={(e) => {
                            setTag(e.target.value);
                            // Reset activity code/country/activity number when book changes
                            setActivityCode("");
                            setSelectedCountry("");
                            setSelectedActivityNumber("");
                          }} 
                          className="w-full p-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 bg-white shadow-sm"
                        >
                          {Array.from(new Set([...BOOK_CO_LEARNING_UPLOAD_TAGS, "Misc"])).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        )}
                      </div>
                      
                      {editId ? (
                        <div>
                          <label className="block text-sm font-semibold text-amber-700 mb-2">Linked book activity</label>
                          <div className="w-full p-3 border-2 border-amber-200 rounded-xl bg-amber-50/90 text-amber-900 font-medium shadow-sm">
                            {tag === "Around the world with 100 artists"
                              ? (selectedCountry || "—")
                              : tag === "Simple acts of art"
                                ? (selectedActivityNumber || "—")
                                : (activityCode || "—")}
                          </div>
                          <p className="text-xs text-amber-600 mt-2">Activity link is fixed for this upload and cannot be changed while editing.</p>
                        </div>
                      ) : tag === "Around the world with 100 artists" ? (
                        <div>
                          <label className="block text-sm font-semibold text-amber-700 mb-2">
                            Activity for which country? *
                          </label>
                          <select 
                            value={selectedCountry} 
                            onChange={(e) => setSelectedCountry(e.target.value)} 
                            className="w-full p-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 bg-white shadow-sm" 
                            required={artworkType === 'book-co-learning'}
                          >
                            <option value="">Select a country</option>
                            {countries.map((country) => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </select>
                          <p className="text-xs text-amber-600 mt-2 flex items-center">
                            <span className="mr-1">💡</span>
                            Select the country for this activity. All users who upload for the same country will be grouped together.
                          </p>
                        </div>
                      ) : tag === "Simple acts of art" ? (
                        <div>
                          <label className="block text-sm font-semibold text-amber-700 mb-2">
                            Activity Number *
                          </label>
                          <select 
                            value={selectedActivityNumber} 
                            onChange={(e) => setSelectedActivityNumber(e.target.value)} 
                            className="w-full p-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 bg-white shadow-sm" 
                            required={artworkType === 'book-co-learning'}
                          >
                            <option value="">Select an activity</option>
                            {activityNumbers.map((num) => (
                              <option key={num} value={`Activity ${num}`}>Activity {num}</option>
                            ))}
                          </select>
                          <p className="text-xs text-amber-600 mt-2 flex items-center">
                            <span className="mr-1">💡</span>
                            Select the activity number. All users who upload for the same activity will be grouped together.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-semibold text-amber-700 mb-2">
                            Activity Code *
                          </label>
                          <input 
                            type="text" 
                            value={activityCode} 
                            onChange={(e) => setActivityCode(e.target.value)} 
                            className="w-full p-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 bg-white shadow-sm" 
                            placeholder="Enter the activity code from the book"
                            required={artworkType === 'book-co-learning'}
                          />
                          <p className="text-xs text-amber-600 mt-2 flex items-center">
                            <span className="mr-1">💡</span>
                            Find this code in your Artmosphere book for the specific activity
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                {/* Art History Activity Selection */}
                {artworkType === 'art-history' && (
                  <div className="mt-4 space-y-3 pt-4 border-t border-amber-200/60">
                      <div>
                        <label className="block text-sm font-semibold text-amber-700 mb-2">
                          {editId ? 'Linked Art History activity' : 'Select Art History Activity *'}
                        </label>
                        {editId ? (
                          <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                            <p className="text-sm text-amber-800 font-medium">
                              {selectedArtHistoryActivity?.metadata?.artworkTitle ||
                                selectedArtHistoryActivity?.title ||
                                'Art History activity'}
                            </p>
                            <p className="text-xs text-amber-600 mt-1">Activity link cannot be changed while editing.</p>
                          </div>
                        ) : artHistoryActivities.length === 0 ? (
                          <div className="p-4 bg-amber-100 rounded-xl border border-amber-300">
                            <p className="text-amber-700 text-sm">No Art History activities found. Please complete an Art History Appreciation first.</p>
                </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="overflow-x-auto pb-2">
                              <div className="flex gap-4 min-w-max">
                                {artHistoryActivities.map((activity) => {
                                  const artworkTitle = activity.metadata?.artworkTitle || activity.title || 'Art History Activity';
                                  const artworkImage = activity.metadata?.artworkImage || activity.originalImageUrl || activity.metadata?.originalImageUrl || null;
                                  
                                  // Resolve any backend-relative / gs:// URLs to a real HTTP(S) URL.
                                  let finalImageUrl = artworkImage;
                                  if (finalImageUrl && typeof finalImageUrl === 'string') {
                                    finalImageUrl = resolveImageUrlUtil(finalImageUrl);
                                  }
                                  
                                  const isSelected = selectedArtHistoryActivity?.id === activity.id;
                                  
                                  return (
                                    <div
                                      key={activity.id}
                                      onClick={() => {
                                        setSelectedArtHistoryActivity(activity);
                                      }}
                                      className={`flex-shrink-0 w-48 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                        isSelected
                                          ? 'border-amber-500 bg-amber-50 shadow-md'
                                          : 'border-amber-200 bg-white hover:border-amber-300 hover:shadow-sm'
                                      }`}
                                    >
                                      <div className="relative w-full h-32 mb-2 rounded-lg overflow-hidden bg-gray-100">
                                        {finalImageUrl ? (
                                          <SmartImage
                                            src={finalImageUrl}
                                            alt={artworkTitle}
                                            loading="eager"
                                            fetchPriority="high"
                                            decoding="async"
                                            className="w-full h-full object-cover"
                                            containerClassName="w-full h-full"
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100">
                                            <div className="text-center p-2">
                                              <svg className="w-8 h-8 mx-auto text-amber-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                              </svg>
                                              <p className="text-xs text-amber-600">No image</p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-center">
                                        <p className="text-sm font-semibold text-amber-800 truncate" title={artworkTitle}>{artworkTitle}</p>
                                        {isSelected && (
                                          <p className="text-xs text-amber-600 mt-1">✓ Selected</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            {selectedArtHistoryActivity && (
                              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                                <p className="text-sm text-amber-700">
                                  <span className="font-semibold">Selected:</span> {selectedArtHistoryActivity.metadata?.artworkTitle || selectedArtHistoryActivity.title || 'Art History Activity'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Ways of Seeing Activity Selection */}
                {artworkType === 'ways-of-seeing' && (
                  <div className="mt-4 space-y-3 pt-4 border-t border-amber-200/60">
                      <div>
                        <label className="block text-sm font-semibold text-amber-700 mb-2">
                          {editId ? 'Ways of Seeing style transfer' : 'Select which artwork you have re-imagined? *'}
                        </label>
                        {editId ? (
                          <div className="space-y-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                            {waysOfSeeingStyleName && (
                              <p className="text-sm text-amber-800">
                                <span className="font-semibold">Style:</span> {waysOfSeeingStyleName}
                              </p>
                            )}
                            {waysOfSeeingOriginalArtwork?.imageUrl && (
                              <p className="text-xs text-amber-600">Original artwork linked to this re-imagination.</p>
                            )}
                            <p className="text-xs text-amber-600">Style and source artwork cannot be changed while editing.</p>
                          </div>
                        ) : waysOfSeeingActivities.length === 0 ? (
                          <div className="p-4 bg-amber-100 rounded-xl border border-amber-300">
                            <p className="text-amber-700 text-sm">No Ways of Seeing style transfers found. Please complete a style transfer first.</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* Get unique original artworks */}
                            {(() => {
                              const uniqueOriginals = waysOfSeeingActivities.reduce((acc, activity) => {
                                const originalUrlRaw = activity.originalImageUrl || activity.metadata?.originalImageUrl;
                                const artworkTitle = activity.title || activity.metadata?.originalTitle || 'Original Artwork';
                                const originalUrl = originalUrlRaw ? resolveImageUrlUtil(originalUrlRaw) : null;
                                
                                if (originalUrl && !acc.find(a => a.imageUrl === originalUrl)) {
                                  acc.push({
                                    imageUrl: originalUrl,
                                    title: artworkTitle,
                                    id: activity.id // Use first activity ID as identifier
                                  });
                                }
                                return acc;
                              }, []);
                              
                              return (
                                <div className="overflow-x-auto pb-2">
                                  <div className="flex gap-4 min-w-max">
                                    {uniqueOriginals.map((original) => {
                                      const isSelected = selectedOriginalArtwork?.imageUrl === original.imageUrl;
                                      
                                      return (
                                        <div
                                          key={original.id}
                                          onClick={() => {
                                            setSelectedOriginalArtwork(original);
                                            // Reset style selection when artwork changes
                                            setSelectedWaysOfSeeingActivity(null);
                                            setWaysOfSeeingStyleId(null);
                                            setWaysOfSeeingStyleName(null);
                                          }}
                                          className={`flex-shrink-0 w-48 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                            isSelected
                                              ? 'border-amber-500 bg-amber-50 shadow-md'
                                              : 'border-amber-200 bg-white hover:border-amber-300 hover:shadow-sm'
                                          }`}
                                        >
                                          <div className="relative w-full h-32 mb-2 rounded-lg overflow-hidden bg-gray-100">
                                            {original.imageUrl ? (
                                              <SmartImage
                                                src={original.imageUrl}
                                                alt={original.title}
                                                loading="eager"
                                                fetchPriority="high"
                                                decoding="async"
                                                className="w-full h-full object-cover"
                                                containerClassName="w-full h-full"
                                              />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100">
                                                <div className="text-center p-2">
                                                  <svg className="w-8 h-8 mx-auto text-amber-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                  </svg>
                                                  <p className="text-xs text-amber-600">No image</p>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          <div className="text-center">
                                            <p className="text-sm font-semibold text-amber-800 truncate" title={original.title}>{original.title}</p>
                                            {isSelected && (
                                              <p className="text-xs text-amber-600 mt-1">✓ Selected</p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()}
                            
                            {/* Style Selection Dropdown - Only show after original artwork is selected */}
                            {selectedOriginalArtwork && (
                              <div className="mt-4">
                                <label className="block text-sm font-semibold text-amber-700 mb-2">
                                  You have re-imagined your original artwork as *
                                </label>
                                {availableStylesForArtwork.length === 0 ? (
                                  <div className="p-4 bg-amber-100 rounded-xl border border-amber-300">
                                    <p className="text-amber-700 text-sm">No styles found for this artwork. Please complete a style transfer first.</p>
                                  </div>
                                ) : (
                                  <select 
                                    value={waysOfSeeingStyleId || ''} 
                                    onChange={(e) => {
                                      const selectedStyle = availableStylesForArtwork.find(s => s.styleId === e.target.value);
                                      if (selectedStyle) {
                                        setWaysOfSeeingStyleId(selectedStyle.styleId);
                                        setWaysOfSeeingStyleName(selectedStyle.styleName);
                                        setSelectedWaysOfSeeingActivity(selectedStyle.activity);
                                        setWaysOfSeeingOriginalArtwork({
                                          id: selectedStyle.activity.id,
                                          imageUrl: selectedOriginalArtwork.imageUrl,
                                          title: selectedOriginalArtwork.title
                                        });
                                      }
                                    }} 
                                    className="w-full p-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 bg-white shadow-sm"
                                    required
                                  >
                                    <option value="">-- Select a style --</option>
                                    {availableStylesForArtwork.map((style) => (
                                      <option key={style.styleId} value={style.styleId}>
                                        {style.styleName}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {waysOfSeeingStyleName && (
                                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 mt-2">
                                    <p className="text-sm text-amber-700">
                                      <span className="font-semibold">Selected:</span> {waysOfSeeingStyleName}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Artwork Details — all types when editing; general / ways-of-seeing / book / art-history on create */}
                {(editId ||
                  artworkType === '' ||
                  artworkType === 'ways-of-seeing' ||
                  artworkType === 'book-co-learning' ||
                  artworkType === 'art-history') && (
                  <div className={uploadWizardStepCardClass}>
                    <div className="mb-4 flex items-center gap-3">
                      <div className={uploadWizardStepBadgeClass}>
                        <span>1</span>
                      </div>
                      <h3 className={uploadWizardStepTitleClass}>Artwork Details</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className={uploadWizardLabelClass}>Title *</label>
                        <input 
                          type="text" 
                          value={title} 
                          onChange={(e) => setTitle(e.target.value)} 
                          className={onboardingInputClass}
                          placeholder="Enter artwork title"
                          required
                        />
                      </div>
                      
                      {(artworkType === '' || artworkType === 'ways-of-seeing') && (
                      <>
                      <div>
                        <label className={uploadWizardLabelClass}>
                          {artworkType === 'ways-of-seeing' ? 'Art Style' : 'Technique'}
                        </label>
                        <input 
                          type="text" 
                          value={technique} 
                          onChange={(e) => setTechnique(e.target.value)} 
                          className={onboardingInputClass}
                          placeholder={artworkType === 'ways-of-seeing' ? 'e.g., Surrealism, Impressionism' : 'e.g., Watercolor, Digital Art, Collage'}
                        />
                      </div>
                      
                      <div>
                        <label className={uploadWizardLabelClass}>Inspiration</label>
                        <input 
                          type="text" 
                          value={inspiration} 
                          onChange={(e) => setInspiration(e.target.value)} 
                          className={onboardingInputClass}
                          placeholder={artworkType === 'ways-of-seeing' ? 'Artmosphere Ways of Seeing Tutorial' : 'What inspired this artwork?'}
                        />
                      </div>
                      </>
                      )}
                      
                      <div>
                        <label className={uploadWizardLabelClass}>Description *</label>
                        <textarea 
                          value={description} 
                          onChange={(e) => setDescription(e.target.value)} 
                          rows={3}
                          className={`${onboardingInputClass} resize-none`}
                          placeholder="Describe your artwork and creative process"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* File Upload — new uploads only */}
                {!editId && (
                <div className={uploadWizardStepCardClass}>
                  <div className="mb-4 flex items-center gap-3">
                    <div className={uploadWizardStepBadgeClass}>
                      <span>2</span>
                    </div>
                    <h3 className={uploadWizardStepTitleClass}>Upload Image</h3>
                  </div>
                  
                  <div className={`${uploadWizardPanelClass} text-center transition-colors hover:border-[#F59E0B]/60`}>
                    {file ? (
                      <div className="space-y-3">
                        <div className="text-green-500 mb-3">
                          <svg className="w-10 h-10 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-green-700 font-medium mb-2">File Selected: {file.name}</p>
                        <p className="text-sm text-green-600 mb-4">Size: {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        <button
                          onClick={() => setFile(null)}
                          className="inline-block bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-xl cursor-pointer transition-colors shadow-sm mr-2"
                        >
                          Remove File
                        </button>
                        <label 
                          htmlFor="artwork-upload" 
                          className="inline-block art-btn-primary font-semibold py-2 px-6 rounded-xl cursor-pointer transition-colors shadow-sm"
                        >
                          Choose Different File
                        </label>
                      </div>
                    ) : (
                      <div>
                        <div className="mb-3 text-[#B45309]">
                          <svg className="mx-auto h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <p className={`${uploadWizardBodyClass} mb-2 font-medium text-[#2F251B]`}>
                          Click a picture of your artwork and upload
                        </p>
                        <p className="mb-4 text-xs text-[#8A7357]">Maximum file size: 5MB</p>
                        <label 
                          htmlFor="artwork-upload" 
                          className="inline-block art-btn-primary font-semibold py-2 px-6 rounded-xl cursor-pointer transition-colors shadow-sm"
                        >
                          Take or upload photo
                        </label>
                      </div>
                    )}
                    <input 
                      type="file" 
                      onChange={(e) => setFile(e.target.files[0])} 
                      className="hidden" 
                      id="artwork-upload"
                      accept="image/*"
                      capture="environment"
                    />
                  </div>
                </div>
                )}


                {/* Visibility Settings - Only for general and ways-of-seeing (art-history and book-co-learning have their own sections below) */}
                {(artworkType === '' || artworkType === 'ways-of-seeing') && (
                <div className={uploadWizardStepCardClass}>
                  <div className="mb-4 flex items-center gap-3">
                    <div className={uploadWizardStepBadgeClass}>
                        <span>3</span>
                    </div>
                    <h3 className={uploadWizardStepTitleClass}>Visibility Settings</h3>
                  </div>
                  
                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#F5D99B]/70 bg-[#FFFDF9] px-3 py-2.5 transition hover:border-[#F59E0B]/50">
                    <input 
                      type="checkbox" 
                      checked={visibility === "public"} 
                      onChange={(e) => setVisibility(e.target.checked ? "public" : "private")} 
                      className="h-5 w-5 rounded border-2 border-[#F5D99B] bg-white text-[#F59E0B] focus:ring-[#F5D99B]/40 checked:border-[#F59E0B] checked:bg-[#F59E0B]" 
                    />
                    <span className="font-medium text-[#2F251B]">Share to General Exhibit</span>
                  </label>
                  <p className={`${uploadWizardBodyClass} mt-2 pl-1 text-xs`}>
                      {artworkType === 'ways-of-seeing' 
                      ? "Ways of Seeing artworks will appear in the Ways of Seeing Co-Learning Space and Artist Wall."
                      : "Leave unchecked to keep the artwork private on your Artist Wall only. Check this box to share it on the General Exhibit."
                    }
                  </p>
                </div>
                )}

                {/* Visibility Settings for Art History */}
                {artworkType === 'art-history' && (
                  <div className={uploadWizardStepCardClass}>
                    <div className="mb-4 flex items-center gap-3">
                      <div className={uploadWizardStepBadgeClass}>
                        <span>3</span>
                      </div>
                      <h3 className={uploadWizardStepTitleClass}>Visibility Settings</h3>
                    </div>
                    
                    <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#F5D99B]/70 bg-[#FFFDF9] px-3 py-2.5 transition hover:border-[#F59E0B]/50">
                      <input 
                        type="checkbox" 
                        checked={visibility === "public"} 
                        onChange={(e) => setVisibility(e.target.checked ? "public" : "private")} 
                        className="h-5 w-5 rounded border-2 border-[#F5D99B] bg-white text-[#F59E0B] focus:ring-[#F5D99B]/40 checked:border-[#F59E0B] checked:bg-[#F59E0B]" 
                      />
                      <span className="font-medium text-[#2F251B]">Make this artwork public</span>
                    </label>
                    <p className={`${uploadWizardBodyClass} mt-2 pl-1 text-xs`}>
                      Public artworks will appear in the Art History Co-Learning Space and Artist Wall. Private artworks will only appear in your Artist Wall.
                    </p>
                  </div>
                )}

                {/* Visibility Settings for Book Co-Learning */}
                {artworkType === 'book-co-learning' && (
                  <div className={uploadWizardStepCardClass}>
                    <div className="mb-4 flex items-center gap-3">
                      <div className={uploadWizardStepBadgeClass}>
                        <span>3</span>
                      </div>
                      <h3 className={uploadWizardStepTitleClass}>Visibility Settings</h3>
                    </div>
                    
                    <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#F5D99B]/70 bg-[#FFFDF9] px-3 py-2.5 transition hover:border-[#F59E0B]/50">
                      <input 
                        type="checkbox" 
                        checked={visibility === "public"} 
                        onChange={(e) => setVisibility(e.target.checked ? "public" : "private")} 
                        className="h-5 w-5 rounded border-2 border-[#F5D99B] bg-white text-[#F59E0B] focus:ring-[#F5D99B]/40 checked:border-[#F59E0B] checked:bg-[#F59E0B]" 
                      />
                      <span className="font-medium text-[#2F251B]">Make this artwork public</span>
                    </label>
                    <p className={`${uploadWizardBodyClass} mt-2 pl-1 text-xs`}>
                      Public artworks will appear in the Book Co-Learning Space and Artist Wall. Private artworks will only appear in your Artist Wall.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 border-t border-[#e8d4bc]/60 pt-4">
                  <button 
                    type="button"
                    onClick={() => { setShowUpload(false); resetForm(); onCloseUploadModal?.(); }} 
                    className={`${artSecondaryBtnLg} flex-1`}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button"
                    onClick={handleUpload}
                    disabled={isUploading}
                    className={`${artPrimaryBtnLg} flex-1 ${
                      isUploading ? 'cursor-not-allowed opacity-70' : ''
                    }`}
                  >
                    {isUploading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </span>
                    ) : (
                      editId ? "Update Artwork" : "Upload Artwork"
                    )}
                  </button>
                </div>
              </div>
            </div>
            )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Manage Subscription Modal */}
      {showManageSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border-2 border-amber-200 overflow-hidden my-4">
            {/* Header */}
            <div className="p-4 border-b-2 border-amber-200 flex items-center justify-between bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100">
              <h3 className="text-xl font-bold text-amber-900">Manage Subscription</h3>
              <button 
                onClick={() => {
                  setShowManageSubscription(false);
                  setProratedDetails(null);
                  setPlanConfirm(null);
                  setPlanChangeStep(1);
                  setMsSuccess("");
                  setMsError("");
                }} 
                className="text-amber-700 hover:text-amber-900 text-xl font-bold transition-colors w-7 h-7 flex items-center justify-center rounded-full hover:bg-amber-200"
              >
                ✕
              </button>
            </div>
            
            <div className="p-5 max-h-[calc(100vh-120px)] overflow-y-auto">
              {msError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
                  {msError}
                </div>
              )}
              {msSuccess && (
                <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
                  {msSuccess}
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Left: Plan Details */}
                <div className="space-y-4">
                  {/* Current Plan Badge */}
                  <div>
                    <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Current plan</div>
                    <div className="inline-flex flex-col gap-1.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold tracking-wider">
                          {(() => {
                            const m = msData?.membership || {};
                            const planId = m.planId;
                            const selectedPlan = m.selectedPlan;
                            const tier = m.tier;
                            const price = m.price;
                            
                            // Get plan label - prioritize planId/selectedPlan over tier
                            if (planId && planMeta[planId]) {
                              return planMeta[planId].label.toUpperCase();
                            }
                            if (selectedPlan && planMeta[selectedPlan]) {
                              return planMeta[selectedPlan].label.toUpperCase();
                            }
                            // For monthly tier, infer from price if no planId
                            if (tier === 'monthly') {
                              if (price === 199) return 'BASE';
                              if (price === 399) return 'PRO';
                              if (price === 749) return 'PREMIUM';
                              // If no price, try lastChargeAmount
                              const lastCharge = m.lastChargeAmount;
                              if (lastCharge === 199) return 'BASE';
                              if (lastCharge === 399) return 'PRO';
                              if (lastCharge === 749) return 'PREMIUM';
                              // Default to BASE if monthly tier has no planId or price
                              if (!planId && !selectedPlan) return 'BASE';
                            }
                            // For tier, only show if it's a valid billing frequency plan
                            if (tier && planMeta[tier]) {
                              return planMeta[tier].label.toUpperCase();
                            }
                            // Fallback: infer from price
                            if (price === 199) return 'BASE';
                            if (price === 399) return 'PRO';
                            if (price === 749) return 'PREMIUM';
                            return '—';
                          })()}
                    </span>
                        <span className="text-sm opacity-90">Plan</span>
                  </div>
                      {/* Plan Amount */}
                      <div className="text-xl font-extrabold">
                        {(() => {
                          const m = msData?.membership || {};
                          const planId = m.planId;
                          const selectedPlan = m.selectedPlan;
                          const tier = m.tier;
                          const price = m.price;
                          const lastChargeAmount = m.lastChargeAmount;
                          
                          // Get plan amount - same priority as plan name
                          if (planId && planMeta[planId]) {
                            const plan = planMeta[planId];
                            return `${plan.currency}${plan.price}`;
                          }
                          if (selectedPlan && planMeta[selectedPlan]) {
                            const plan = planMeta[selectedPlan];
                            return `${plan.currency}${plan.price}`;
                          }
                          // For monthly tier, infer from price
                          if (tier === 'monthly') {
                            if (price === 199 || lastChargeAmount === 199) return '₹199';
                            if (price === 399 || lastChargeAmount === 399) return '₹399';
                            if (price === 749 || lastChargeAmount === 749) return '₹749';
                            // Default to BASE if monthly tier has no planId or price
                            if (!planId && !selectedPlan && !price && !lastChargeAmount) {
                              return '₹199';
                            }
                          }
                          // For billing frequency plans
                          if (tier === 'annual' && planMeta[tier]) {
                            const plan = planMeta[tier];
                            return `${plan.currency}${plan.price}`;
                          }
                          if (tier === 'semiannual' && planMeta[tier]) {
                            const plan = planMeta[tier];
                            return `${plan.currency}${plan.price}`;
                          }
                          // Fallback: use stored price (excluding $9)
                          if (price && price !== 9) {
                            if (price === 199) return '₹199';
                            if (price === 399) return '₹399';
                            if (price === 749) return '₹749';
                            if (price === 49) return '$49';
                            if (price === 89) return '$89';
                          }
                          if (lastChargeAmount && lastChargeAmount !== 9) {
                            if (lastChargeAmount === 199) return '₹199';
                            if (lastChargeAmount === 399) return '₹399';
                            if (lastChargeAmount === 749) return '₹749';
                            if (lastChargeAmount === 49) return '$49';
                            if (lastChargeAmount === 89) return '$89';
                          }
                          // Final fallback: if monthly tier with no data, default to BASE
                          if (tier === 'monthly') {
                            return '₹199';
                          }
                          return '—';
                    })()}
                        <span className="text-sm font-semibold ml-1 opacity-90">
                          {(() => {
                            const m = msData?.membership || {};
                            const planId = m.planId;
                            const selectedPlan = m.selectedPlan;
                            const tier = m.tier;
                            
                            if (planId && planMeta[planId]) {
                              return planMeta[planId].suffix;
                            }
                            if (selectedPlan && planMeta[selectedPlan]) {
                              return planMeta[selectedPlan].suffix;
                            }
                            if (tier === 'annual' && planMeta[tier]) {
                              return planMeta[tier].suffix;
                            }
                            if (tier === 'semiannual' && planMeta[tier]) {
                              return planMeta[tier].suffix;
                            }
                            if (tier === 'monthly' || planId || selectedPlan) {
                              return '/mo';
                            }
                            return '';
                          })()}
                        </span>
                  </div>
                    </div>
                  </div>

                  {/* Member Since */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
                    <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Member since</div>
                    <div className="text-base font-bold text-amber-900">
                      {(() => {
                        const d = msData?.membership?.memberSince || msData?.membership?.activatedAt || msData?.membership?.trialStartsAt;
                        const dt = d ? (d._seconds ? new Date(d._seconds * 1000) : (d.toDate ? d.toDate() : new Date(d))) : null;
                        return dt ? dt.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '—';
                      })()}
                    </div>
                  </div>

                  {/* Subscription Details */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-1.5 border-b border-amber-100">
                      <span className="text-xs font-medium text-amber-700">Status</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        msData?.membership?.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : msData?.membership?.status === 'trialing'
                          ? 'bg-blue-100 text-blue-700'
                          : msData?.membership?.status === 'canceled'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {msData?.membership?.status || '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5">
                      <span className="text-xs font-medium text-amber-700">Next payment</span>
                      <span className="text-xs font-semibold text-amber-900">
                        {(() => {
                      const m = msData?.membership || {};
                      const raw = m.status === 'trialing' ? m.trialEndsAt : (m.currentPeriodEnd || msData?.expiresAt);
                          const dt = raw ? (raw._seconds ? new Date(raw._seconds * 1000) : (raw.toDate ? raw.toDate() : new Date(raw))) : null;
                          return dt ? dt.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '—';
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* Update Credit Card */}
                    <button
                      disabled={msLoading}
                      onClick={() => alert('Update card via Stripe portal coming soon')}
                    className="w-full px-3 py-2 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold bg-white hover:bg-amber-50 transition-colors disabled:opacity-50"
                    >
                      Update credit card
                    </button>
                </div>

                {/* Right: Pricing & Actions */}
                <div className="flex flex-col justify-between">
                  {/* Price Display */}
                  <div className="mb-6">
                    <div className="text-right mb-2">
                      <div className="text-5xl font-extrabold text-amber-900 leading-none">
                    {(() => {
                          const m = msData?.membership || {};
                          const planId = m.planId;
                          const selectedPlan = m.selectedPlan;
                          const tier = m.tier;
                          const price = m.price;
                          const lastChargeAmount = m.lastChargeAmount;
                          
                          console.log('💰 Price calculation:', {
                            planId,
                            selectedPlan,
                            tier,
                            price,
                            lastChargeAmount,
                            membership: m
                          });
                          
                          // Priority: planId/selectedPlan > tier (only if valid) > price mapping (excluding $9)
                          
                          // First, try to identify the plan from planId or selectedPlan
                      if (planId && planMeta[planId]) {
                        const plan = planMeta[planId];
                            console.log('✅ Using planId:', planId, plan);
                        return `${plan.currency}${plan.price}`;
                      }
                      
                      if (selectedPlan && planMeta[selectedPlan]) {
                        const plan = planMeta[selectedPlan];
                            console.log('✅ Using selectedPlan:', selectedPlan, plan);
                        return `${plan.currency}${plan.price}`;
                      }
                      
                          // Then try tier - but ONLY for valid billing frequency plans (not monthly without planId)
                          if (tier === 'annual' && planMeta[tier]) {
                            const plan = planMeta[tier];
                            console.log('✅ Using tier: annual', plan);
                            return `${plan.currency}${plan.price}`;
                          }
                          
                          if (tier === 'semiannual' && planMeta[tier]) {
                            const plan = planMeta[tier];
                            console.log('✅ Using tier: semiannual', plan);
                            return `${plan.currency}${plan.price}`;
                          }
                          
                          // For monthly tier, MUST have planId (base/pro/premium)
                          // If no planId, default to 'base' plan
                          if (tier === 'monthly') {
                            if (planId === 'base') {
                              console.log('✅ Monthly with base planId');
                              return '₹199';
                            }
                            if (planId === 'pro') {
                              console.log('✅ Monthly with pro planId');
                              return '₹399';
                            }
                            if (planId === 'premium') {
                              console.log('✅ Monthly with premium planId');
                              return '₹749';
                            }
                            // Monthly without planId - default to base plan
                            if (!planId && !selectedPlan) {
                              console.log('⚠️ Monthly tier without planId - defaulting to BASE (₹199)');
                              return '₹199';
                            }
                            // Try to infer from price
                            if (price === 199) {
                              console.log('✅ Monthly inferred from price: 199');
                              return '₹199';
                            }
                            if (price === 399) {
                              console.log('✅ Monthly inferred from price: 399');
                              return '₹399';
                            }
                            if (price === 749) {
                              console.log('✅ Monthly inferred from price: 749');
                              return '₹749';
                            }
                            // Final fallback: default to base
                            console.log('⚠️ Monthly tier without valid planId or price - defaulting to BASE');
                            return '₹199';
                          }
                          
                          // Fallback to stored price (but exclude $9)
                          if (price !== null && price !== undefined && price !== 9) {
                            if (price === 199) {
                              console.log('✅ Using price: 199');
                              return '₹199';
                            }
                            if (price === 399) {
                              console.log('✅ Using price: 399');
                              return '₹399';
                            }
                            if (price === 749) {
                              console.log('✅ Using price: 749');
                              return '₹749';
                            }
                            if (price === 49) {
                              console.log('✅ Using price: 49');
                              return '$49';
                            }
                            if (price === 89) {
                              console.log('✅ Using price: 89');
                              return '$89';
                            }
                          }
                          
                          // Last resort: use lastChargeAmount but only if it matches a known plan (excluding $9)
                          if (lastChargeAmount !== null && lastChargeAmount !== undefined && lastChargeAmount !== 9) {
                            if (lastChargeAmount === 49) {
                              console.log('✅ Using lastChargeAmount: 49');
                              return '$49';
                            }
                            if (lastChargeAmount === 89) {
                              console.log('✅ Using lastChargeAmount: 89');
                              return '$89';
                            }
                            if (lastChargeAmount === 199) {
                              console.log('✅ Using lastChargeAmount: 199');
                              return '₹199';
                            }
                            if (lastChargeAmount === 399) {
                              console.log('✅ Using lastChargeAmount: 399');
                              return '₹399';
                            }
                            if (lastChargeAmount === 749) {
                              console.log('✅ Using lastChargeAmount: 749');
                              return '₹749';
                            }
                          }
                          
                          console.log('❌ No valid price found, returning —');
                      return '—';
                    })()}
                      </div>
                      <div className="text-lg font-semibold text-amber-700 mt-1">
                        {(() => {
                          const m = msData?.membership || {};
                          const planId = m.planId;
                          const selectedPlan = m.selectedPlan;
                          const tier = m.tier;
                          
                          // Priority: planId/selectedPlan > tier > price mapping
                          
                          // First, try to identify the plan from planId or selectedPlan
                      if (planId && planMeta[planId]) {
                        return planMeta[planId].suffix;
                      }
                      
                      if (selectedPlan && planMeta[selectedPlan]) {
                        return planMeta[selectedPlan].suffix;
                      }
                      
                          // Then try tier
                          if (tier && planMeta[tier]) {
                            return planMeta[tier].suffix;
                          }
                          
                          // Fallback to stored price mapping
                          const price = m.price;
                      if (price !== null && price !== undefined) {
                        if ([199, 399, 749].includes(price)) return '/mo';
                            // Don't map $9 - it's not a valid plan
                        if (price === 49) return '/6mo';
                        if (price === 89) return '/yr';
                          }
                          
                          // Last resort: use lastChargeAmount mapping
                          const lastChargeAmount = m.lastChargeAmount;
                          if (lastChargeAmount !== null && lastChargeAmount !== undefined) {
                            // Don't map $9 - it's not a valid plan
                            if (lastChargeAmount === 49) return '/6mo';
                            if (lastChargeAmount === 89) return '/yr';
                            if ([199, 399, 749].includes(lastChargeAmount)) return '/mo';
                          }
                          
                          // Final fallback
                          if (tier === 'annual') return '/yr';
                          if (tier === 'semiannual') return '/6mo';
                          if (tier === 'monthly') {
                            // Monthly should have planId for student plans
                            if (planId && ['base', 'pro', 'premium'].includes(planId)) return '/mo';
                            return '';
                          }
                      return '';
                        })()}
                  </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      disabled={msLoading}
                      onClick={async () => {
                        if (!user) return;
                        setMsLoading(true); setMsError("");
                        try {
                          const action = msData?.membership?.canceled ? 'resume' : 'cancel';
                          const res = await authFetch(`${backendConfig.baseUrl}/api/membership/portal`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ uid: user.uid, action })
                          });
                          const ct = res.headers.get('content-type') || '';
                          const j = ct.includes('application/json') ? await res.json() : null;
                          if (!res.ok) throw new Error((j && j.error) || 'Failed to update subscription');
                          
                          // Update with response from portal endpoint first (optimistic update)
                          if (j && j.membership) {
                            setMsData(prev => prev ? { ...prev, membership: j.membership } : { ok: true, allowed: false, membership: j.membership });
                          }
                          
                          // Then refresh from status endpoint to get the effective status
                          const s = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}&_t=${Date.now()}`);
                          const sct = s.headers.get('content-type') || '';
                          const sj = sct.includes('application/json') ? await s.json() : null;
                          if (!s.ok) throw new Error((sj && sj.error) || 'Failed to fetch status');
                          
                          // Use the status endpoint response which has the correct effectiveStatus
                          setMsData(sj);
                          console.log('✅ Subscription status updated:', {
                            action,
                            membershipStatus: sj?.membership?.status,
                            canceled: sj?.membership?.canceled,
                            allowed: sj?.allowed
                          });
                        } catch (e) {
                          setMsError(e.message);
                        } finally {
                          setMsLoading(false);
                        }
                      }}
                      className={`w-full px-4 py-2 rounded-lg border font-semibold text-sm transition-all ${
                        msData?.membership?.canceled
                          ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                          : 'border-amber-300 bg-white text-amber-800 hover:bg-amber-50'
                      } ${msLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {msLoading ? 'Working…' : (msData?.membership?.canceled ? 'Resume subscription' : 'Cancel subscription')}
                    </button>
                    
                    <div className="relative">
                      <button
                        disabled={msLoading}
                        onClick={() => setShowPlanPicker(v => !v)}
                        className={`w-full px-4 py-2 rounded-lg text-white font-semibold text-sm shadow-md transition-all ${
                          msLoading 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'art-btn-primary hover:shadow-lg'
                        }`}
                      >
                        Downgrade / Upgrade
                      </button>
                      {/* Plan Picker */}
                      {showPlanPicker && (
                        <div className="absolute right-0 mt-2 w-56 bg-white border border-amber-200 rounded-lg shadow-xl p-2 z-[9999]">
                          {['base', 'pro', 'premium'].map(p => {
                            const plan = planMeta[p];
                            if (!plan) return null;
                            const isCurrent = msData?.membership?.tier === p || 
                                            msData?.membership?.planId === p || 
                                            msData?.membership?.selectedPlan === p;
                            return (
                              <button
                                key={p}
                                disabled={msLoading}
                                onClick={() => { 
                                  // Calculate prorated details before showing confirmation
                                  const currentMembership = msData?.membership || {};
                                  const currentPlanId = currentMembership.planId || 'base';
                                  const currentPrice = currentPlanId === 'base' ? 199 : currentPlanId === 'pro' ? 399 : 749;
                                  const newPrice = plan.price;
                                  
                                  // Calculate days remaining
                                  let daysRemaining = 30; // Default to 30 days
                                  const expiresAt = currentMembership.expiresAt || currentMembership.currentPeriodEnd || currentMembership.trialEndsAt;
                                  console.log('📅 Days remaining calculation:', {
                                    expiresAt,
                                    currentPeriodEnd: currentMembership.currentPeriodEnd,
                                    trialEndsAt: currentMembership.trialEndsAt
                                  });
                                  
                                  if (expiresAt) {
                                    let expiryDate;
                                    if (expiresAt._seconds) {
                                      expiryDate = new Date(expiresAt._seconds * 1000);
                                    } else if (expiresAt.toDate && typeof expiresAt.toDate === 'function') {
                                      expiryDate = expiresAt.toDate();
                                    } else if (typeof expiresAt === 'string') {
                                      expiryDate = new Date(expiresAt);
                                    } else if (expiresAt instanceof Date) {
                                      expiryDate = expiresAt;
                                    } else {
                                      expiryDate = new Date(expiresAt);
                                    }
                                    
                                    const now = new Date();
                                    const diffTime = expiryDate.getTime() - now.getTime();
                                    daysRemaining = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
                                    
                                    console.log('📅 Date calculation:', {
                                      expiryDate: expiryDate.toISOString(),
                                      now: now.toISOString(),
                                      diffTime,
                                      daysRemaining
                                    });
                                    
                                    // If daysRemaining is 0 or negative, it means the billing cycle has ended
                                    // In this case, we should use the full month for proration calculation
                                    if (daysRemaining <= 0) {
                                      console.log('⚠️ Billing cycle has ended or is ending today. Using full month for calculation.');
                                      daysRemaining = 30; // Use full month for calculation
                                    }
                                  } else {
                                    console.log('⚠️ No expiration date found, using default 30 days');
                                  }
                                  
                                  // Calculate prorated amount (for active or trialing memberships)
                                  let proratedAmount = 0;
                                  const isActiveOrTrialing = currentMembership.status === 'active' || currentMembership.status === 'trialing';
                                  const plansAreDifferent = currentPlanId !== p;
                                  
                                  console.log('🔍 Plan change calculation check:', {
                                    currentPlanId,
                                    newPlan: p,
                                    currentPrice,
                                    newPrice,
                                    status: currentMembership.status,
                                    isActiveOrTrialing,
                                    plansAreDifferent,
                                    expiresAt: currentMembership.expiresAt,
                                    currentPeriodEnd: currentMembership.currentPeriodEnd,
                                    daysRemaining
                                  });
                                  
                                  // Always calculate if plans are different and user has active/trialing membership
                                  if (isActiveOrTrialing && plansAreDifferent) {
                                    const priceDifference = newPrice - currentPrice;
                                    // Calculate prorated amount (positive for upgrade, negative for downgrade)
                                    // Even if daysRemaining is 0, we should still show the calculation
                                    proratedAmount = Math.round((priceDifference * daysRemaining / 30) * 100) / 100;
                                    console.log('💰 Prorated calculation result:', {
                                      currentPlan: currentPlanId,
                                      newPlan: p,
                                      currentPrice,
                                      newPrice,
                                      priceDifference,
                                      daysRemaining,
                                      proratedAmount,
                                      status: currentMembership.status,
                                      willShow: proratedAmount !== 0
                                    });
                                  } else {
                                    console.log('⚠️ Prorated calculation skipped:', {
                                      isActiveOrTrialing,
                                      plansAreDifferent,
                                      reason: !isActiveOrTrialing ? 'Not active or trialing' : (!plansAreDifferent ? 'Same plan selected' : 'Unknown')
                                    });
                                  }
                                  
                                  const proratedDetailsData = {
                                    proratedAmount,
                                    daysRemaining,
                                    currentPlan: currentPlanId,
                                    newPlan: p,
                                    currentPrice,
                                    newPrice: plan.price
                                  };
                                  
                                  console.log('📋 Setting proratedDetails:', proratedDetailsData);
                                  setProratedDetails(proratedDetailsData);
                                  
                                  setPlanConfirm({ 
                                    id: p, 
                                    label: plan.label, 
                                    price: plan.price, 
                                    currency: plan.currency,
                                    suffix: plan.suffix 
                                  }); 
                                  setPlanChangeStep(1); // Reset to step 1
                                  setShowPlanPicker(false);
                                }}
                                className={`w-full text-left px-3 py-2 rounded-md mb-1 last:mb-0 transition-all text-sm ${
                                  msLoading ? 'opacity-60' : 'hover:bg-amber-50'
                                } ${
                                  isCurrent 
                                    ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-900 font-semibold border border-amber-300' 
                                    : 'text-amber-800 border border-transparent'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{plan.label}</span>
                                  <span className="font-bold">{plan.currency}{plan.price}{plan.suffix}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip/Disclaimer */}
              <div className="mt-4 pt-4 border-t border-amber-200">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <div className="text-amber-600 text-base">💡</div>
                    <div className="text-xs text-amber-800 leading-relaxed">
                      <span className="font-semibold">Tip:</span> Canceling your plan keeps access until the end of your current billing period.
                    </div>
                  </div>
                </div>
              </div>

              {planConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      {/* Error Display */}
                      {msError && (
                        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
                          {msError}
                        </div>
                      )}
                      
                      {/* Header */}
                      <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-amber-900 mb-2">Change Your Plan</h2>
                        <p className="text-sm text-amber-700">Step-by-step process to switch to {planConfirm.label} plan</p>
                      </div>
                      
                      {/* Progress Steps */}
                      <div className="flex items-center justify-center mb-6">
                        {[
                          { title: "Review Plan", desc: "Current and new plan details" },
                          { title: "Billing Summary", desc: "Prorated charges and next cycle" },
                          { title: "Confirmation", desc: "Final review and processing" }
                        ].map((step, index) => (
                          <div key={index} className="flex items-center">
                            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                              planChangeStep > index + 1 
                                ? 'bg-green-500 border-green-500 text-white' 
                                : planChangeStep === index + 1
                                ? 'bg-amber-500 border-amber-500 text-white'
                                : 'border-gray-300 text-gray-400'
                            }`}>
                              {planChangeStep > index + 1 ? '✓' : index + 1}
                            </div>
                            <div className="ml-2">
                              <div className={`text-xs font-medium ${
                                planChangeStep >= index + 1 ? 'text-amber-900' : 'text-gray-400'
                              }`}>
                                {step.title}
                              </div>
                            </div>
                            {index < 2 && (
                              <div className={`w-12 h-0.5 mx-3 ${
                                planChangeStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
                              }`} />
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Step Content */}
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 mb-6 border border-amber-200">
                        {/* Step 1: Review Plan */}
                        {planChangeStep === 1 && (
                          <div>
                            <h3 className="text-lg font-semibold text-amber-900 mb-4">Current Plan Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-white rounded-lg p-4 border border-amber-200">
                                <h4 className="font-semibold text-amber-800 mb-3">Current Plan</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-amber-600">Plan Type:</span>
                                    <span className="font-medium text-amber-900">{(proratedDetails?.currentPlan || 'base').toUpperCase()}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-amber-600">Monthly Cost:</span>
                                    <span className="font-medium text-amber-900">₹{proratedDetails?.currentPrice || 199}/mo</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="bg-white rounded-lg p-4 border border-amber-200">
                                <h4 className="font-semibold text-amber-800 mb-3">New Plan</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-amber-600">Plan Type:</span>
                                    <span className="font-medium text-amber-900">{planConfirm.label.toUpperCase()}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-amber-600">Monthly Cost:</span>
                                    <span className="font-medium text-amber-900">₹{planConfirm.price}/mo</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Step 2: Billing Summary */}
                        {planChangeStep === 2 && (
                          <div>
                            <h3 className="text-lg font-semibold text-amber-900 mb-4">Billing Summary</h3>
                            <div className="bg-white rounded-lg p-5 border border-amber-200">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                  <h4 className="font-semibold text-amber-800 mb-3">Immediate Charges</h4>
                                  <div className="space-y-2">
                                    {(() => {
                                      const hasProratedDetails = !!proratedDetails;
                                      const proratedAmount = proratedDetails?.proratedAmount;
                                      const isNotZero = proratedAmount !== 0;
                                      const status = msData?.membership?.status;
                                      const isActiveOrTrialing = status === 'active' || status === 'trialing';
                                      const shouldShow = hasProratedDetails && isNotZero && isActiveOrTrialing;
                                      
                                      console.log('🔍 Rendering Immediate Charges:', {
                                        hasProratedDetails,
                                        proratedAmount,
                                        isNotZero,
                                        status,
                                        isActiveOrTrialing,
                                        shouldShow,
                                        fullProratedDetails: proratedDetails,
                                        fullMembership: msData?.membership
                                      });
                                      
                                      if (shouldShow) {
                                        return (
                                          <>
                                            <div className="flex justify-between items-center">
                                              <span className="text-amber-600">
                                                {proratedDetails.proratedAmount > 0 ? 'Immediate Charge:' : 'Credit Applied:'}
                                              </span>
                                              <span className={`font-bold text-lg ${proratedDetails.proratedAmount > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                                {proratedDetails.proratedAmount > 0 ? '₹' : '-₹'}{Math.abs(proratedDetails.proratedAmount)}
                                              </span>
                                            </div>
                                            <div className="text-xs text-amber-500">
                                              Based on {proratedDetails.daysRemaining} days remaining in current cycle
                                            </div>
                                            <div className="text-xs text-amber-500 mt-1">
                                              {proratedDetails.proratedAmount > 0 
                                                ? `Upgrading from ${proratedDetails.currentPlan.toUpperCase()} (₹${proratedDetails.currentPrice}) to ${proratedDetails.newPlan.toUpperCase()} (₹${proratedDetails.newPrice})`
                                                : `Downgrading from ${proratedDetails.currentPlan.toUpperCase()} (₹${proratedDetails.currentPrice}) to ${proratedDetails.newPlan.toUpperCase()} (₹${proratedDetails.newPrice})`
                                              }
                                            </div>
                                          </>
                                        );
                                      }
                                      return <div className="text-sm text-amber-600">No immediate charge</div>;
                                    })()}
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="font-semibold text-amber-800 mb-3">Next Billing Cycle</h4>
                                  <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                      <span className="text-amber-600">Monthly Total:</span>
                                      <span className="font-bold text-lg text-green-600">₹{planConfirm.price}</span>
                                    </div>
                                    <div className="text-xs text-amber-500">
                                      For {planConfirm.label} plan
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {proratedDetails && proratedDetails.proratedAmount !== 0 && (msData?.membership?.status === 'active' || msData?.membership?.status === 'trialing') && (
                                <div className="mt-4 p-3 bg-amber-100 rounded-lg border border-amber-300">
                                  <p className="text-sm text-amber-800">
                                    <strong>Important:</strong> {proratedDetails.proratedAmount > 0 
                                      ? `You'll be charged ₹${proratedDetails.proratedAmount} immediately for the plan upgrade. `
                                      : `A credit of ₹${Math.abs(proratedDetails.proratedAmount)} will be applied to your account. `
                                    }
                                    Starting next billing cycle, you'll be charged ₹{planConfirm.price}/month for the {planConfirm.label} plan.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Step 3: Final Confirmation */}
                        {planChangeStep === 3 && (
                          <div>
                            <h3 className="text-lg font-semibold text-amber-900 mb-4">
                              {msSuccess ? 'Plan Change Successful!' : 'Final Confirmation'}
                            </h3>
                            <div className="bg-white rounded-lg p-5 border border-amber-200">
                              <div className="text-center">
                                {msSuccess ? (
                                  <>
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                    <h4 className="text-lg font-semibold text-green-700 mb-2">Plan Updated Successfully!</h4>
                                    <p className="text-sm text-amber-700 mb-4">
                                      {msSuccess}
                                    </p>
                                    <p className="text-xs text-amber-600 mb-4">
                                      Your plan has been changed to {planConfirm.label.toUpperCase()}. You can close this window.
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                    <h4 className="text-lg font-semibold text-amber-900 mb-2">Ready to Change Plan</h4>
                                    <p className="text-sm text-amber-700 mb-6">
                                      You're about to switch from {(proratedDetails?.currentPlan || 'base').toUpperCase()} to {planConfirm.label.toUpperCase()} plan.
                                    </p>
                                  </>
                                )}
                                
                                {!msSuccess && (
                                  <>
                                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 mb-6 border border-amber-200">
                                      {proratedDetails && proratedDetails.proratedAmount !== 0 && (msData?.membership?.status === 'active' || msData?.membership?.status === 'trialing') ? (
                                        <>
                                          <div className="flex justify-between items-center mb-2">
                                            <span className="text-amber-700">Immediate Charge:</span>
                                            <span className={`font-bold text-xl ${proratedDetails.proratedAmount > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                              {proratedDetails.proratedAmount > 0 ? '₹' : '-₹'}{Math.abs(proratedDetails.proratedAmount)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className="text-amber-700">Next Billing Cycle:</span>
                                            <span className="font-bold text-lg text-green-600">₹{planConfirm.price}/month</span>
                                          </div>
                                        </>
                                      ) : (
                                        <div className="flex justify-between items-center">
                                          <span className="text-amber-700">Next Billing Cycle:</span>
                                          <span className="font-bold text-lg text-green-600">₹{planConfirm.price}/month</span>
                                        </div>
                                      )}
                                    </div>
                                    
                                    <p className="text-xs text-amber-600">
                                      Click "Confirm & Process" to complete the plan change.
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Navigation Buttons */}
                      <div className="flex justify-between">
                        <button
                          onClick={() => {
                            setPlanConfirm(null);
                            setProratedDetails(null);
                            setPlanChangeStep(1);
                            setMsSuccess("");
                            setMsError("");
                          }}
                          className="px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors text-sm"
                        >
                          {msSuccess ? 'Close' : 'Cancel'}
                        </button>
                        
                        <div className="flex gap-3">
                          {!msSuccess && planChangeStep > 1 && (
                            <button
                              onClick={() => setPlanChangeStep(planChangeStep - 1)}
                              className="px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors text-sm"
                            >
                              Previous
                            </button>
                          )}
                          
                          {!msSuccess && planChangeStep < 3 && (
                            <button
                              onClick={() => setPlanChangeStep(planChangeStep + 1)}
                              className="art-btn-primary px-4 py-2 rounded-lg transition-colors text-sm font-semibold"
                            >
                              Next Step
                            </button>
                          )}
                          
                          {!msSuccess && planChangeStep === 3 && (
                      <button
                        disabled={msLoading}
                        onClick={async () => {
                          if (!user) return;
                                setMsLoading(true); 
                                setMsError(""); 
                                setMsSuccess("");
                                
                                let controller = null;
                                let fetchTimeout = null;
                                let overallTimeout = null;
                                
                                try {
                                  const requestBody = { uid: user.uid, action: 'change_plan', plan: planConfirm.id };
                                  console.log('🔄 Sending plan change request:', requestBody);
                                  
                                  // Create AbortController with longer timeout (60 seconds)
                                  controller = new AbortController();
                                  fetchTimeout = setTimeout(() => {
                                    console.warn('⚠️ Fetch timeout reached, aborting request');
                                    controller.abort();
                                  }, 60000); // 60 second fetch timeout
                                  
                                  // Overall timeout as backup (65 seconds)
                                  overallTimeout = setTimeout(() => {
                                    if (controller && !controller.signal.aborted) {
                                      controller.abort();
                                    }
                                    setMsError('Request timed out. Please try again.');
                                    setMsLoading(false);
                                  }, 65000);
                                  
                            const res = await authFetch(`${backendConfig.baseUrl}/api/membership/portal`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(requestBody),
                                    signal: controller.signal
                            });
                                  
                                  // Clear timeouts on successful response
                                  if (fetchTimeout) clearTimeout(fetchTimeout);
                                  if (overallTimeout) clearTimeout(overallTimeout);
                                  
                            const ct = res.headers.get('content-type') || '';
                            const j = ct.includes('application/json') ? await res.json() : null;
                                  console.log('🔄 Plan change response:', { status: res.status, ok: res.ok, data: j });
                                  
                                  if (!res.ok) {
                                    const errorMsg = (j && j.error) || `Failed to change plan (${res.status})`;
                                    console.error('❌ Plan change failed:', errorMsg);
                                    throw new Error(errorMsg);
                                  }
                                  
                                  // Update membership data from response
                            if (j && j.membership) {
                                    setMsData(prev => prev ? { ...prev, membership: j.membership } : { ok: true, allowed: true, membership: j.membership, expiresAt: j.membership.expiresAt });
                                    console.log('✅ Membership updated:', j.membership);
                                    
                                    // Force refresh membership status to ensure all dates are updated
                                    setTimeout(async () => {
                                      try {
                                        const refreshRes = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}&_t=${Date.now()}`);
                                        const refreshCt = refreshRes.headers.get('content-type') || '';
                                        const refreshJ = refreshCt.includes('application/json') ? await refreshRes.json() : null;
                                        if (refreshRes.ok && refreshJ && refreshJ.membership) {
                                          setMsData(refreshJ);
                                          console.log('✅ Membership status refreshed:', refreshJ.membership);
                                        }
                                      } catch (refreshError) {
                                        console.warn('⚠️ Failed to refresh membership status:', refreshError);
                                      }
                                    }, 500);
                                  }
                                  
                                  // Show success message with billing details if available
                                  let successMsg = 'Plan updated successfully.';
                                  if (j && j.billing) {
                                    successMsg = j.billing.confirmation || successMsg;
                                  }
                                  
                            setShowPlanPicker(false);
                                  // Don't close the confirmation modal - let user close it manually
                                  // Keep planConfirm and proratedDetails so modal stays open
                                  setMsSuccess(successMsg);
                                  
                                  // Show success state in the modal (user will close manually)
                                  
                          } catch (e) {
                                  // Clear timeouts on error
                                  if (fetchTimeout) clearTimeout(fetchTimeout);
                                  if (overallTimeout) clearTimeout(overallTimeout);
                                  
                                  console.error('❌ Plan change error:', e);
                                  
                                  // Handle different error types
                                  let errorMsg = 'Failed to change plan. Please try again.';
                                  if (e.name === 'AbortError') {
                                    errorMsg = 'Request timed out. The server may be slow. Please try again.';
                                  } else if (e.message) {
                                    errorMsg = e.message;
                                  }
                                  
                                  setMsError(errorMsg);
                                  // Don't close modal on error so user can see the error
                          } finally {
                            setMsLoading(false);
                          }
                        }}
                              className={`px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {msLoading ? 'Processing...' : proratedDetails && proratedDetails.proratedAmount !== 0 && (msData?.membership?.status === 'active' || msData?.membership?.status === 'trialing')
                                ? (proratedDetails.proratedAmount > 0 
                                    ? `Confirm & Pay ₹${proratedDetails.proratedAmount}`
                                    : `Confirm & Apply Credit ₹${Math.abs(proratedDetails.proratedAmount)}`)
                                : 'Confirm & Process'}
                      </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {msError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
                  {msError}
                </div>
              )}

              {!msData && msLoading && (
                <div className="mt-4 animate-pulse grid grid-cols-2 gap-6">
                  <div className="h-28 bg-amber-100/60 rounded-xl"></div>
                  <div className="h-28 bg-amber-100/60 rounded-xl"></div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Artworks are now displayed inline with Daily Activity button above - old grid section removed */}

      {selectedPost && (
        <PostModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onKudosChange={(count) => handleKudosChange(selectedPost.id, count)}
        />
      )}

      {/* Account Settings Modal with Consent Toggles */}
      <AccountSettingsModal 
        isOpen={showAccountSettings} 
        onClose={() => setShowAccountSettings(false)}
        onProfileUpdate={() => {
          // Clear profile cache when profile is updated
          const user = auth.currentUser;
          if (user) {
            const profileCacheKey = createCacheKey('artistProfile', { uid: user.uid }, user.uid);
            invalidateArtistProfileCache(user.uid);
            console.log('✅ [ARTIST WALL] Profile cache cleared after update');
            fetchProfile(true);
          }
        }}
      />

      {/* Ways of Seeing Modal */}
      {showWaysOfSeeing && waysOfSeeingModalOriginalArtwork && (
      <WaysOfSeeingModal
        isOpen={showWaysOfSeeing}
        onClose={closeWaysOfSeeing}
        originalArtwork={waysOfSeeingModalOriginalArtwork}
        originalArtworkTitle={selectedOriginalArtworkTitle}
      />
      )}

      {/* Art History Modal */}
      {showArtHistoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white p-8 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>Art History Tools</h3>
                    <button 
                  onClick={() => setShowArtHistoryModal(false)}
                  className="text-white/90 hover:text-white text-3xl leading-none transition-colors duration-200"
                    >
                      ×
                    </button>
                  </div>
            </div>
            <div className="p-8">
              <p className="text-black mb-8 font-medium">Choose your art history exploration tool:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <button
                            onClick={() => {
                    setShowArtHistoryModal(false);
                    navigate('/art-appreciation');
                  }}
                  className="flex items-center gap-6 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl hover:from-amber-100 hover:to-yellow-100 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center text-white shadow-xl">
                    <BookOpen className="w-8 h-8" />
                        </div>
                  <div className="text-left">
                    <h4 className="font-bold text-black text-xl mb-2 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>Art History Appreciation</h4>
                    <p className="text-black font-medium">Explore art movements and learn about masterpieces</p>
                      </div>
                          </button>
                          <button 
                            onClick={() => {
                    setShowArtHistoryModal(false);
                    navigate('/art-history');
                  }}
                  className="flex items-center gap-6 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl hover:from-amber-100 hover:to-yellow-100 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center text-white shadow-xl">
                    <ImageIcon className="w-8 h-8" />
                            </div>
                  <div className="text-left">
                    <h4 className="font-bold text-black text-xl mb-2 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>The Art of Comparison</h4>
                    <p className="text-black font-medium">Compare artworks and learn about differences</p>
                  </div>
                                    </button>
                                  </div>
                                </div>
                                        </div>
                                      </div>
      )}

      {/* Uploading Overlay */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isUploading && (
            <div className="fixed inset-0 z-[100005] bg-black/55 backdrop-blur-md flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 8 }}
                transition={{ duration: 0.2 }}
                className="w-full max-w-md rounded-3xl border border-amber-200/70 bg-gradient-to-br from-white via-amber-50/70 to-orange-50/70 shadow-2xl overflow-hidden"
              >
                <div className="px-6 pt-6 pb-4 bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400">
                  <h3 className="text-white text-xl font-bold text-center">Uploading Artwork</h3>
                </div>
                <div className="px-6 py-7">
                  <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center border-2 border-amber-300/70">
                    <svg className="animate-spin h-8 w-8 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  </div>
                  <p className="text-center text-amber-900 font-semibold text-base mb-2">{uploadStatusText}</p>
                  <p className="text-center text-amber-700 text-sm">Please keep this window open while we finish.</p>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Sophisticated Success Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showUploadSuccess && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100003] p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-gradient-to-br from-white via-amber-50/30 to-orange-50/30 rounded-3xl shadow-2xl max-w-md w-full border-2 border-amber-200/50 overflow-hidden"
              >
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30 shadow-lg">
                      <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                        </div>
                  <h3 className="text-2xl font-bold text-white text-center tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Success!
                  </h3>
                  </div>
                  
                {/* Content */}
                <div className="p-8 bg-white rounded-b-2xl">
                  <p className="text-gray-900 text-center text-lg leading-relaxed mb-6 font-semibold">
                    {uploadSuccessMessage}
                  </p>
                  
                  {/* Action Button - Only show if artwork was actually shared */}
                  {wasArtworkShared && (
                    <button
                      onClick={() => {
                        const destination = getSharedArtworkDestination(successModalArtworkType);
                        setShowUploadSuccess(false);
                        setUploadSuccessMessage('');
                        setWasArtworkShared(false);
                        setSuccessModalArtworkType('');
                        navigate(destination);
                      }}
                      className="w-full art-btn-primary font-semibold py-4 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                      {getSharedArtworkCtaLabel(successModalArtworkType)}
                    </button>
                  )}
                  
                  {/* Close button */}
                  <button
                    onClick={() => {
                      const type = successModalArtworkType;
                      const destination = wasArtworkShared ? getSharedArtworkDestination(type) : null;
                      setShowUploadSuccess(false);
                      setUploadSuccessMessage('');
                      setWasArtworkShared(false);
                      setSuccessModalArtworkType('');
                      if (destination) navigate(destination);
                    }}
                    className={`w-full ${wasArtworkShared ? 'mt-3' : ''} bg-white border-2 border-amber-300 hover:border-amber-400 text-amber-800 hover:text-amber-900 font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md`}
                  >
                    Close
                  </button>
                </div>
              </motion.div>
                      </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
